import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'od_language';

export const languages = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const dictionaries = {
  en: {
    'app.feed': 'Feed',
    'app.discover': 'Discover',
    'app.photos': 'My photos',
    'app.fitWall': 'Fit Wall',
    'nav.feed': 'Feed',
    'nav.fitWall': 'Wall',
    'nav.photos': 'Photos',
    'nav.discover': 'Discover',
    'nav.help': 'Help',
    'app.saved': 'Saved',
    'app.profile': 'Profile',
    'app.legal': 'Legal',
    'app.menu': 'Menu',
    'app.logout': 'Log out',
    'app.language': 'Language',
    'beta.badge': 'Beta',
    'beta.copy': 'Still in beta. Matches are suggestions: useful for finding a similar vibe, not exact copies.',
    'copyright.source': 'Source: {source}',
    'copyright.sourceFallback': 'social post',
    'copyright.preview': 'We use the preview to find similar alternatives. Rights stay with the owners.',
    'copyright.takedown': 'Need removal?',
    'betaInvite.title': 'Invite a friend',
    'betaInvite.copy': 'Know someone who saves outfits, follows creators, or hunts for similar products? Send them Outfit Detective.',
    'betaInvite.whatsapp': 'WhatsApp',
    'betaInvite.email': 'Email',
    'betaInvite.copyLink': 'Copy link',
    'betaInvite.native': 'Share',
    'betaInvite.shareTitle': 'Try Outfit Detective Beta',
    'betaInvite.shareText': 'I am trying Outfit Detective Beta: a personal wall of social looks, similar products, and virtual try-on on your photos. It is still in beta, but the idea is strong.',
    'share.copy': 'Copy link',
    'share.copyDone': 'Copied',
    'share.copyToast': 'Link copied',
    'share.channel.whatsapp': 'WhatsApp',
    'share.channel.email': 'Email',
    'share.channel.native': 'Share',
    'share.channel.pinterest': 'Pinterest',
    'authPrompt.kicker': 'One tiny login',
    'authPrompt.title': 'Keep this fit yours',
    'authPrompt.copy': 'Drop your email and we send a one-time code. No password, no heavy signup.',
    'authPrompt.signIn': 'Sign in',
    'authPrompt.sendCode': 'Send code',
    'authPrompt.verify': 'Continue',
    'authPrompt.tryOnTitle': 'See how it looks on you',
    'authPrompt.tryOnCopy': 'Sign in so your photos stay private.',
    'authPrompt.matchTitle': 'Let’s find the look',
    'authPrompt.matchCopy': 'Jump in quickly so we can run this search for you.',
    'authPrompt.photosTitle': 'Your photos',
    'authPrompt.photosCopy': 'Keep the photos you want to try looks on.',
    'authPrompt.savedTitle': 'Keep the good looks',
    'authPrompt.savedCopy': 'Sign in so you do not lose them.',
    'authPrompt.followTitle': 'Make it feel like you',
    'authPrompt.followCopy': 'Sign in to follow the people who inspire your style.',
    'publicIntro.tagline': 'Public social looks · Similar alternatives · Private fit-on-me',
    'publicIntro.imageAlt': 'Outfit idea',
    'publicIntro.demoLabel': 'Real look to fit-on-me demo',
    'publicIntro.sourceAlt': 'Original public outfit inspiration',
    'publicIntro.resultAlt': 'Fit-on-me preview',
    'publicIntro.mediaSource': 'fit inspo',
    'publicIntro.mediaResult': 'on me',
    'publicIntro.mediaCelebrities': 'Public looks',
    'publicIntro.mediaSimilar': 'Similar clothes',
    'publicIntro.mediaTryOn': 'Try-on ideas',
    'publicIntro.kicker': 'Public preview',
    'publicIntro.title': 'Put the look on your photo',
    'publicIntro.copy': 'Pick a public look, and Outfit Detective shows how it could look on you with similar clothes.',
    'publicIntro.howCopy': 'Follow public Instagram or TikTok profiles, open a look, and test it on your photo.',
    'publicIntro.featuresLabel': 'Outfit Detective features',
    'publicIntro.pillCelebrities': 'Public posts',
    'publicIntro.pillProducts': 'Similar products',
    'publicIntro.pillTryOn': 'Virtual try-on ideas',
    'publicIntro.primaryCta': 'Try this fit',
    'publicIntro.secondaryCta': 'See looks shared by others',
    'how.metaTitle': 'How Outfit Detective Works',
    'how.metaDescription': 'Try on public looks from Instagram and TikTok, find similar alternatives to recreate the fit, and keep fit-on-me previews private.',
    'how.kicker': 'How it works',
    'how.title': 'From a public social look to a fit you can try on you.',
    'how.copy': 'Outfit Detective reads visual clues in public looks from Instagram and TikTok, separates the pieces, finds similar alternatives and helps you test the vibe on your own photos.',
    'how.primaryCta': 'Start with email',
    'how.secondaryCta': 'See the Fit Wall',
    'how.demoLabel': 'Generated demo of the Outfit Detective flow',
    'how.demoImageAlt': 'Generated Outfit Detective demo showing detected outfit pieces',
    'how.demoDetected': 'Detected pieces',
    'how.demoPieces': 'Top, jacket, pants, shoes and accessories',
    'how.demoMatch': 'Similar style',
    'how.demoMatchTitle': 'Closest visual alternatives',
    'how.demoMatchMeta': 'Color, shape and vibe matched',
    'how.promiseLabel': 'What Outfit Detective does',
    'how.promise.1.value': '01',
    'how.promise.1.label': 'Read the fit from a public Instagram or TikTok post',
    'how.promise.2.value': '02',
    'how.promise.2.label': 'Find similar alternatives without knowing the brand',
    'how.promise.3.value': '03',
    'how.promise.3.label': 'Try the look privately on your own photos',
    'how.stepsKicker': 'The flow',
    'how.stepsTitle': 'Three steps to recreate a public look safely.',
    'how.steps.1.title': 'Bring the look',
    'how.steps.1.copy': 'Outfit Detective starts from looks published by creators on Instagram and TikTok. The app focuses on the outfit and styling signals, not on collecting unnecessary data.',
    'how.steps.2.title': 'Let the AI split the fit',
    'how.steps.2.copy': 'We detect visible pieces, colors, silhouette and styling signals, then turn them into a clean outfit breakdown.',
    'how.steps.3.title': 'Act on it',
    'how.steps.3.copy': 'Browse similar products, save the look, share it, or create a fit-on-me preview to see whether the mood works for you.',
    'how.showcaseBadge': 'Fit-on-me preview',
    'how.showcaseTitle': 'Upload your photo and get a private preview of the creator look on you.',
    'how.featureKicker': 'What it does',
    'how.featureTitle': 'A style search engine with a little taste.',
    'how.feature.find.title': 'Find similar clothes',
    'how.feature.find.copy': 'Search by what the outfit looks like, even when the original brand is unknown.',
    'how.feature.explain.title': 'Understand the look',
    'how.feature.explain.copy': 'See the pieces and styling signals instead of a flat list of random products.',
    'how.feature.try.title': 'Fit on me',
    'how.feature.try.copy': 'Use your private photos to create a preview inspired by the selected outfit.',
    'how.feature.save.title': 'Save and compare',
    'how.feature.save.copy': 'Keep looks, product ideas and try-on previews together for later decisions.',
    'how.trustKicker': 'Good boundaries',
    'how.trustTitle': 'Useful, but not fake certainty.',
    'how.trustCopy': 'Outfit Detective is built to suggest visually similar styles, not to pretend every product is an exact match or endorsed by a creator.',
    'how.trust.1': 'Similar alternatives are labeled as suggestions, not guaranteed exact products.',
    'how.trust.2': 'Fit-on-me photos stay personal and are used for your preview flow.',
    'how.trust.3': 'The app explains style signals like color, silhouette and category where possible.',
    'how.finalKicker': 'Ready when curiosity hits',
    'how.finalTitle': 'Next time a look stops your scroll, make it useful.',
    'how.finalCopy': 'Turn a public creator look into an outfit breakdown, similar alternatives and a private fit-on-me idea while the inspiration is still fresh.',
    'how.finalCta': 'Start the beta',
    'how.finalSecondary': 'Browse public looks',
    'common.follow': 'Follow',
    'common.unfollow': 'Unfollow',
    'common.remove': 'Remove',
    'common.open': 'Open',
    'common.preview': 'Preview',
    'common.loading': 'Loading...',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.refresh': 'Refresh',
    'common.cancel': 'Cancel',
    'common.on': 'On',
    'common.off': 'Off',
    'common.profile': 'profile',
    'common.profiles': 'profiles',
    'login.kicker': 'Your style feed',
    'login.title': 'Sign in to Outfit Detective',
    'login.subtitle': 'Follow public profiles you love and collect looks in your personal feed.',
    'login.heroEyebrow': 'Outfit Detective',
    'login.heroTitle': 'Try on the looks you see on Instagram and TikTok.',
    'login.heroSubtitle': 'Follow public creator looks, keep fit-on-me previews private, and find similar alternatives to recreate the fit.',
    'login.heroBenefitsLabel': 'Benefits',
    'login.pillCelebs': 'Fresh drops',
    'login.pillDetect': 'Fit radar',
    'login.pillShop': 'Similar finds',
    'login.proofFast': 'New drops',
    'login.proofFastLabel': 'looks as they land',
    'login.proofSources': 'Instagram + TikTok',
    'login.proofSourcesLabel': 'together in one feed',
    'login.proofPersonal': 'Yours',
    'login.proofPersonalLabel': 'only your follows',
    'login.formTitle': 'Sign in to Outfit Detective',
    'login.formSubtitle': 'Enter your email and we will send a one-time login code.',
    'login.email': 'Your email',
    'login.emailPlaceholder': 'name@email.com',
    'login.sendCode': 'Send my one-time code',
    'login.sending': 'Sending...',
    'login.otp': 'Drop the code',
    'login.changeEmail': 'Use another email',
    'login.verify': 'Continue',
    'login.verifying': 'Verifying...',
    'login.codeSent': 'Check your inbox. The code is fresh for {minutes} minutes. If it is not there, peek at spam or promotions.',
    'login.pendingOtpRestored': 'The code we sent still works.',
    'login.pendingOtpExpired': 'That code expired. Request a new one to continue.',
    'login.useReceivedCode': 'I already have a code',
    'login.resendCode': 'Send me another code',
    'login.useDevOtp': 'Use dev code: {otp}',
    'login.success': 'You are signed in.',
    'welcome.kicker': 'You are in',
    'welcome.title': 'Build a feed that actually gets the outfit.',
    'welcome.copy': 'Follow public creator looks, try the fit privately on your photos, then compare similar alternatives across luxury, mid, and budget-friendly options.',
    'welcome.pointFeedTitle': 'Your people',
    'welcome.pointFeedCopy': 'Pick the profiles. We keep the style feed focused.',
    'welcome.pointDetectTitle': 'Fit radar',
    'welcome.pointDetectCopy': 'We turn public posts into useful outfit clues.',
    'welcome.pointBudgetTitle': 'Price lanes',
    'welcome.pointBudgetCopy': 'Similar vibe, from luxury grails to affordable finds.',
    'welcome.cta': 'Find profiles',
    'welcome.secondary': 'Show me the feed',
    'welcome.dismiss': 'Close welcome',
    'welcome.legalPrefix': 'By continuing you accept our',
    'welcome.legalAnd': 'and',
    'welcome.preferencesLabel': 'Style preferences',
    'welcome.pref.budget': 'Budget',
    'welcome.pref.street': 'Street',
    'welcome.pref.minimal': 'Minimal',
    'welcome.pref.luxury': 'Luxury',
    'welcome.pref.everyday': 'Everyday',
    'followRefresh.kicker': 'Welcome back',
    'followRefresh.title': 'Let us freshen up your follows.',
    'followRefresh.copy': 'While you were away we paused a few background checks, so some followed profiles may not be fully up to date yet.',
    'followRefresh.cta': 'Refresh my follows',
    'followRefresh.refreshing': 'Refreshing...',
    'followRefresh.secondary': 'Not now',
    'followRefresh.dismiss': 'Close refresh notice',
    'followRefresh.success': 'We are refreshing {count} profiles now.',
    'followRefresh.fresh': 'Your followed profiles already look fresh.',
    'followRefresh.error': 'I could not start the refresh right now.',
    'feed.following': 'Following',
    'feed.yourList': 'Your list',
    'feed.findProfiles': 'Find profiles',
    'feed.searchList': 'Search your list',
    'feed.noListSearchResults': 'No profiles match this search.',
    'followConfirm.title': 'Unfollow this profile?',
    'followConfirm.copy': '{name} will leave your list and stop showing up in your personal feed.',
    'followConfirm.confirming': 'Unfollowing...',
    'feed.noProfiles': 'No profiles yet.',
    'feed.heroKicker': 'Personal fashion feed',
    'feed.heroTitle': 'New looks from the profiles you follow.',
    'feed.heroEmpty': 'Follow profiles you care about to build your outfit feed.',
    'feed.heroCount': 'You are following {count} {label}.',
    'feed.apiError': 'API unreachable: {error}',
    'feed.loading': 'Loading feed...',
    'feed.emptyTitle': 'The feed is empty',
    'feed.emptyCopy': 'Add profiles from Discover to build a personal feed.',
    'feed.popularFallbackTitle': 'Popular looks while your feed gets ready',
    'feed.popularFallbackCopy': 'Start following profiles to make this feed yours. For now, here are looks people are watching.',
    'feed.popularFallbackCta': 'Find your style crushes',
    'feed.personalPendingCopy': 'Your followed profiles are being prepared. New looks from them will appear here automatically.',
    'feed.openDetail': 'Open look',
    'feed.suggested': 'Suggested',
    'feed.noSuggestions': 'No suggestions for now.',
    'feed.news': 'Updates',
    'feed.cachedPosts': 'cached posts',
    'feed.images': 'images',
    'feed.updated': 'Updated {date}.',
    'feed.workerReady': 'Worker ready to collect new looks.',
    'feed.checkingServices': 'Checking services...',
    'feed.aiAction': 'Recreate this look',
    'feed.save': 'Save look',
    'feed.unsave': 'Remove saved look',
    'feed.share': 'Share look',
    'feed.tryOn': 'Try it on',
    'feed.shareTitle': 'Look from {name}',
    'feed.shareText': 'I found this look: {title}',
    'feed.shareFallback': 'Outfit Detective look',
    'feed.shareFailed': 'Could not copy the share link.',
    'shareLook.kicker': 'Beta invite',
    'shareLook.cardTitle': 'Share this look',
    'shareLook.cardCopy': 'Send the look to a friend or invite them to try Outfit Detective Beta.',
    'shareLook.button': 'Share look',
    'shareLook.title': '{celebrity} look on Outfit Detective',
    'shareLook.text': 'I found this {celebrity} look on Outfit Detective Beta: social looks, similar products, and virtual try-on.',
    'feed.badgeFresh': 'new from your follows',
    'feed.badgePopular': 'popular right now',
    'feed.badgeMatched': '{count} pieces matched',
    'feed.starterKicker': 'Start here',
    'feed.starterTitle': 'Make the feed yours in three taps.',
    'feed.starterFollow': 'Follow profiles',
    'feed.starterMatch': 'Steal a fit',
    'feed.starterSave': 'Save or share',
    'feed.starterCta': 'Find profiles',
    'aiDialog.kicker': 'Outfit Detective AI',
    'aiDialog.title': 'Shop the look with clearer matches.',
    'aiDialog.copy': 'We read the post, spot the pieces, then line up matches by vibe and budget.',
    'aiDialog.tagsLabel': 'Match details',
    'aiDialog.tagThreeLanes': '3 price lanes',
    'aiDialog.tagSmartMatch': 'same vibe first',
    'aiDialog.laneExactTitle': 'Almost the real one',
    'aiDialog.laneExactCopy': 'Shown only when brand, model, category, and visual clues line up with high confidence.',
    'aiDialog.laneSimilarTitle': 'Same energy',
    'aiDialog.laneSimilarCopy': 'Pieces close to the post style, brand mood, and silhouette.',
    'aiDialog.laneBudgetTitle': 'Smart save',
    'aiDialog.laneBudgetCopy': 'The look, minus the wallet jump scare.',
    'aiDialog.laneLuxuryTitle': 'Luxury lane',
    'aiDialog.laneLuxuryCopy': 'Premium finds when the fit deserves main-character money.',
    'aiDialog.loadingTitle': 'Detecting the fit',
    'aiDialog.loadingCopy': 'You can close this. We keep cooking in the background. Open this dialog or the post again and the finds will be there when ready.',
    'aiDialog.loadingPageCopy': 'We are cooking in the background. Stay here or come back later: the finds will drop here as soon as they are ready.',
    'aiDialog.liveTitle': 'Still searching',
    'aiDialog.liveCopy': 'Some finds are already in. You can close this; we keep updating in the background.',
    'aiDialog.livePageCopy': 'Some finds are already in. We keep updating this page in the background.',
    'aiDialog.readyTitle': 'Matches ready',
    'aiDialog.readyCopy': 'Open anything you like. We will tune ranking after the first tests.',
    'aiDialog.errorTitle': 'Match missed',
    'aiDialog.errorCopy': 'This look did not match right now. Try again in a bit.',
    'aiDialog.retry': 'Try again',
    'aiDialog.resultCount': '{count} product matches',
    'aiDialog.emptyTitle': 'No clean match yet',
    'aiDialog.emptyCopy': 'The image was not clear enough for a useful shopping result.',
    'aiDialog.noLaneResults': 'No solid finds in this lane yet.',
    'aiDialog.lookMatch': 'look match',
    'aiDialog.vibeMatch': 'vibe match',
    'aiDialog.error.ai_matching_disabled': 'AI matching is not active yet.',
    'aiDialog.error.event_image_missing': 'This post has no image to analyze.',
    'aiDialog.error.no_items_detected': 'The outfit was not clear enough to detect useful pieces.',
    'aiDialog.error.auth_required': 'This cached match is not ready yet. Sign in to start a fresh search.',
    'aiDialog.openLook': 'Fit source',
    'aiDialog.close': 'Close AI dialog',
    'aiDialog.best': 'Best pick',
    'aiDialog.bestPick': 'Best pick · {price}',
    'aiDialog.quick.budget': 'Budget from {price}',
    'aiDialog.quick.similar': 'Similar from {price}',
    'aiDialog.quick.luxury': 'Luxury from {price}',
    'profile.refreshFeed': 'Update feed',
    'profile.refreshingFeed': 'Updating...',
    'profile.feedCollectingShort': 'Collecting...',
    'profile.feedCollectingTitle': 'Collecting the first looks',
    'profile.feedCollecting': 'I am collecting the latest posts for this profile. This can take a few seconds.',
    'profile.feedUpdatingTitle': 'Updating this profile',
    'profile.feedUpdating': 'I am fetching fresh posts and outfit data. New looks will appear here automatically.',
    'profile.feedUpdated': 'Feed updated.',
    'profile.feedRefreshFailed': 'I could not update this feed right now.',
    'profile.followingStatus': 'Seguito',
    'profile.notFollowingStatus': 'Not following',
    'discover.title': 'Search who you want to follow.',
    'discover.subtitle': 'Find Instagram or TikTok profiles and add them to your personal list.',
    'discover.searching': 'Searching',
    'discover.noVerified': 'No verified profile found for now.',
    'discover.loadMore': 'Scroll to see more results',
    'discover.loadingMore': 'Loading...',
    'discover.added': '{name} added to your list.',
    'discover.removed': 'Unfollowed.',
    'discover.removeError': 'I cannot update this follow right now. Try again soon.',
    'discover.pasteTitle': 'Paste a profile',
    'discover.pasteCopy': 'Works with Instagram links, TikTok links or @handles.',
    'discover.pastePlaceholder': 'https://instagram.com/... or @username',
    'discover.checkProfile': 'Check',
    'discover.checkingProfile': 'Checking...',
    'discover.profilePreviewFailed': 'I could not fetch this profile. Check the platform and handle.',
    'discover.closeBulk': 'Close list import',
    'discover.openBulk': 'Import more profiles',
    'discover.bulkAdded': '{count} profiles added to your list.',
    'discover.addProfiles': 'Add profiles',
    'discover.yourList': 'Your list',
    'discover.noFollowingTitle': 'No profiles yet',
    'discover.noFollowingCopy': 'Search a profile above and press Follow.',
    'discover.inFeed': 'In your feed',
    'discover.followersUnavailable': 'Followers unavailable',
    'discover.followers': '{count} followers',
    'discover.verifiedBadge': 'verified badge',
    'discover.publicProfile': 'public profile with image',
    'discover.profileDetailsLoading': 'Loading profile details...',
    'discover.closePreview': 'Close preview',
    'discover.audience': 'Audience',
    'discover.posts': 'Posts',
    'discover.videos': 'Videos',
    'discover.totalLikes': 'Total likes',
    'discover.onPlatformSince': 'On {platform} since',
    'discover.country': 'Country',
    'discover.latestActivity': 'Latest activity',
    'discover.openProfile': 'Open profile',
    'discover.followError': 'I cannot follow this profile right now. Try again soon.',
    'discover.blockedKicker': 'Rights notice',
    'discover.blockedTitle': 'This profile cannot be followed',
    'discover.blockedCopy': 'For rights reasons, {name} is not available for following or personal feed use on Outfit Detective.',
    'discover.blockedReference': 'Reference',
    'discover.blockedClose': 'Got it',
    'detail.loading': 'Loading look...',
    'detail.back': 'Back to feed',
    'detail.priceUnavailable': 'Price unavailable',
    'detail.aiTitle': 'Shopping matches for this look',
    'detail.aiEmptyTitle': 'No matches saved yet',
    'detail.aiEmptyCopy': 'Start the AI match to detect pieces and get shopping lanes.',
    'detail.refreshAi': 'Refresh Fit AI',
    'lookShare.open': 'Create story card',
    'lookShare.title': 'Story card',
    'lookShare.total': 'Look found for about {total}',
    'lookShare.noTotal': 'Look found with Outfit Detective',
    'lookShare.download': 'Download story card',
    'lookShare.hint': 'Use it as a Reel/Story cover or post it after your screen recording.',
    'similarRail.kicker': 'Visual shopping',
    'similarRail.title': 'Shop similar products',
    'similarRail.emptyCopy': 'Start Fit AI to unlock a visual rail of clean product matches for this look.',
    'similarRail.loading': 'Finding clean visual matches...',
    'similarRail.start': 'Find similar products',
    'similarRail.showAll': 'Show all matches',
    'similarRail.showMore': 'Show more',
    'similarRail.lane.exact': 'Exact',
    'similarRail.lane.similar': 'Similar',
    'similarRail.lane.budget': 'Budget',
    'similarRail.lane.luxury': 'Luxury',
    'productInfo.kicker': 'Product match',
    'productInfo.price': 'Price',
    'productInfo.match': 'Matched item',
    'productInfo.matchFallback': 'This look',
    'productInfo.copy': 'Stay here first: inspect the piece, then try the full fit on your photo.',
    'productInfo.tryOn': 'Try the fit',
    'productInfo.openShop': 'Open shop',
    'saved.kicker': 'Your closet radar',
    'saved.title': 'Saved looks',
    'saved.emptyTitle': 'No saved looks yet',
    'saved.emptyCopy': 'Save the looks you want to revisit, share, or try on later.',
    'saved.emptyCta': 'Back to feed',
    'saved.open': 'Open look',
    'saved.remove': 'Remove',
    'photos.kicker': 'Try-on photos',
    'photos.title': 'My photos',
    'photos.copy': 'Keep a small set of clean fit photos ready for Try the vibe on me.',
    'photos.uploadTitle': 'Add a photo',
    'photos.uploadCopy': 'Best results come from bright, full-body shots.',
    'photos.usageNoteBefore': 'These photos are used only to create your personal',
    'photos.usageNoteAfter': 'previews. Pick clear full-body shots for better clothing transfer; you can remove them anytime.',
    'photos.pickFile': 'Pick a photo',
    'photos.optimizedBeforeUpload': 'Ready for your try-on',
    'photos.titlePlaceholder': 'Photo name',
    'photos.upload': 'Upload photo',
    'photos.uploading': 'Uploading...',
    'photos.uploaded': 'Photo saved.',
    'photos.processingConsent': 'I consent to processing this photo with our AI systems and OpenAI to generate try-ons. The photo remains private.',
    'photos.emptyTitle': 'No photos yet',
    'photos.emptyCopy': 'Upload a full-body shot to unlock better try-on previews.',
    'photos.default': 'Default',
    'photos.makeDefault': 'Make default',
    'photos.framing.full_body': 'Full body',
    'photos.framing.upper_body': 'Upper body',
    'photos.framing.portrait': 'Portrait',
    'photos.framing.unknown': 'Not sure',
    'photos.error.image_too_large': 'The optimized image is still too large. Try another photo.',
    'photos.error.photo_processing_consent_required': 'Please confirm photo processing consent before uploading.',
    'photos.error.photo_safety_rejected': 'This photo cannot be used for try-on. Please upload a safe, fully clothed adult photo.',
    'photos.error.photo_safety_rejected_with_reason': 'This photo cannot be used for try-on because it appears to contain {reason}. Please upload a safe, fully clothed adult photo.',
    'photos.error.reason.adult': 'adult or nude content',
    'photos.error.reason.racy': 'overly revealing content',
    'photos.error.reason.violence': 'violent content',
    'photos.error.reason.selfHarm': 'self-harm content',
    'photos.error.reason.minors': 'sexual content involving minors',
    'photos.error.reason.generic': 'content that does not follow our photo safety rules',
    'photos.error.photo_safety_check_failed': 'We could not run the photo safety check right now. Try again in a bit.',
    'tryon.cartPresetLabel': 'Automatic cart choices',
    'tryon.cartPresetClosest': 'Most similar',
    'tryon.cartPresetLuxury': 'Luxury',
    'tryon.cartPresetBudget': 'Budget',
    'account.kicker': 'Profile',
    'account.title': 'Your Outfit Detective profile',
    'account.copy': 'Manage photos and personal tools.',
    'account.overview': 'Overview',
    'account.tryonTitle': 'Try-on ready',
    'account.tryonCopy': 'Your photos power personal previews from the looks you discover.',
    'account.privacyTitle': 'You control it',
    'account.privacyCopy': 'We do not need your personal data to make the app useful. These settings are optional, editable anytime, and used only to tune your experience.',
    'account.legalLink': 'Privacy and legal settings',
    'account.shopAreaTitle': 'Your fit radar area',
    'account.shopAreaCopy': 'Outfit Detective checks online platforms and physical stores. Pick a base area and we will prioritize finds that are actually close enough to try, grab, or ship fast.',
    'account.locationLabel': 'Base area',
    'account.locationPlaceholder': 'City or area, e.g. Milan, Porta Nuova',
    'account.locationResults': 'Location suggestions',
    'account.locationSearching': 'Finding places...',
    'account.locationNoResults': 'No clean match yet. Try city plus country.',
    'account.locationSelected': 'Verified area selected',
    'account.shoppingCountryFromLocation': 'Shopping country: {country}, calculated from your fit radar area.',
    'account.radiusLabel': 'Search radius: {km} km',
    'account.radiusAria': 'Search radius',
    'account.locationPrivacy': 'No exact address needed. A city or neighborhood is enough, and you can clear it anytime.',
    'account.styleGenderTitle': 'Wall style',
    'account.styleGenderCopy': 'Choose the kind of looks you want to see first in your wall and suggested profiles.',
    'account.styleGender.all': 'All looks',
    'account.styleGender.menswear': 'Men',
    'account.styleGender.womenswear': 'Women',
    'account.styleGenderConfirm': 'I confirm I want to see all looks in my wall.',
    'account.styleGenderSummaryTitle': 'Wall preference',
    'account.styleGenderSummary.all': 'Your wall and suggestions can include every style.',
    'account.styleGenderSummary.menswear': 'Your wall and suggestions prioritize men and menswear looks.',
    'account.styleGenderSummary.womenswear': 'Your wall and suggestions prioritize women and womenswear looks.',
    'account.identityTitle': 'How you show up',
    'account.identityCopy': 'For public things like Fit Wall and shared try-ons, choose a real name or keep it low-key with a nickname. Your email stays backstage.',
    'account.useNickname': 'Nickname',
    'account.useRealName': 'Real name',
    'account.realNameLabel': 'Real name',
    'account.realNamePlaceholder': 'Name shown only if you choose real name',
    'account.nicknameLabel': 'Nickname',
    'account.nicknamePlaceholder': 'Your public style name',
    'account.useSuggestion': 'Use {nickname}',
    'account.save': 'Save profile',
    'account.saving': 'Saving...',
    'account.saved': 'Profile saved.',
    'account.areaSummaryTitle': 'Shopping priority',
    'account.areaSummarySet': 'Prioritizing online matches plus stores within {km} km of {location}.',
    'account.areaSummaryEmpty': 'No area set yet. We will keep results broad and online-first.',
    'account.publicSummaryTitle': 'Public name',
    'account.publicSummaryCopy': 'Public try-ons will show as {name}.',
    'account.deleteTitle': 'Delete account',
    'account.deleteCopy': 'This suspends your account now. You have 15 days to reactivate by signing in again; after that we permanently delete your profile, photos, try-ons, follows, preferences, and sessions.',
    'account.deleteConfirmLabel': 'Type DELETE to confirm',
    'account.deleteConfirmMismatch': 'Type DELETE exactly to confirm account deletion.',
    'account.deleteButton': 'Delete my account',
    'account.deleting': 'Scheduling deletion...',
    'account.deleteError': 'Could not schedule account deletion right now.',
    'account.error.nickname_required': 'Choose a nickname to continue.',
    'account.error.display_name_required': 'Add a real name or switch to nickname.',
    'account.error.request_failed': 'Could not save right now. Try again.',
    'account.error.api_unreachable': 'API unreachable right now.',
    'account.error.profile_not_found': 'Profile not found.',
    'alerts.tab': 'Alerts',
    'alerts.title': 'Alerts',
    'alerts.copy': 'Important updates about profiles you follow and account activity.',
    'alerts.notice': 'Notice',
    'alerts.emptyTitle': 'No alerts yet',
    'alerts.emptyCopy': 'When something important changes, you will find it here.',
    'alerts.markRead': 'Mark read',
    'alerts.emailSent': 'Email sent: {date}',
    'alerts.error': 'Could not load alerts right now.',
    'alerts.profileIncomplete.title': 'Complete your profile',
    'alerts.profileIncomplete.bodyBoth': 'Add a shopping area and confirm your wall preference so Outfit Detective can tune your feed and suggestions.',
    'alerts.profileIncomplete.bodyLocation': 'Add a shopping area so Outfit Detective can tune your feed and shopping suggestions.',
    'alerts.profileIncomplete.bodyStyleGender': 'Confirm your wall style preference so Outfit Detective can tune your feed and suggested profiles.',
    'alerts.profileIncomplete.cta': 'Complete profile',
    'alerts.followedProfile.fallbackName': 'A profile',
    'alerts.followedProfile.banned.title': 'Followed profile unavailable',
    'alerts.followedProfile.banned.body': '{profile} was removed from your follows and cannot be followed right now for rights or content management reasons.',
    'alerts.followedProfile.banned.bodyReason': '{profile} was removed from your follows and cannot be followed right now for rights or content management reasons. Reason: {reason}',
    'alerts.followedProfile.deletedBanned.title': 'Followed profile deleted and blocked',
    'alerts.followedProfile.deletedBanned.body': '{profile} was deleted, removed from your follows, and cannot be followed right now for rights or content management reasons.',
    'alerts.followedProfile.deletedBanned.bodyReason': '{profile} was deleted, removed from your follows, and cannot be followed right now for rights or content management reasons. Reason: {reason}',
    'alerts.followedProfile.removed.title': 'Followed profile removed',
    'alerts.followedProfile.removed.body': '{profile} was deleted and removed from your follows for rights or content management reasons.',
    'alerts.followedProfile.removed.bodyReason': '{profile} was deleted and removed from your follows for rights or content management reasons. Reason: {reason}',
    'tryonGallery.title': 'My try-ons',
    'tryonGallery.emptyTitle': 'No try-ons yet',
    'tryonGallery.emptyCopy': 'Generate one from a look detail page and it will appear here.',
    'tryonGallery.untitled': 'Generated try-on',
    'tryonGallery.originalPost': 'Original post',
    'tryonGallery.created': 'Created',
    'tryonGallery.userPhoto': 'Your photo',
    'tryonGallery.email': 'Email',
    'tryonGallery.emailOn': 'Notification on',
    'tryonGallery.emailOff': 'No notification',
    'tryonGallery.openEvent': 'Fit source',
    'tryonGallery.openGeneratedImage': 'Open generated try-on image',
    'tryonGallery.openOriginalImage': 'Open original post image',
    'tryonGallery.delete': 'Delete',
    'tryonGallery.deleting': 'Deleting...',
    'tryonGallery.deleteConfirm': 'Delete this try-on? It will disappear from your gallery, shared link, and Fit Wall.',
    'tryonGallery.error.deleteFailed': 'I cannot delete this try-on right now.',
    'tryonGallery.error.try_on_preview_not_found': 'This try-on is already gone.',
    'tryonGallery.viewerLabel': 'Photo viewer',
    'tryonGallery.zoomIn': 'Zoom in',
    'tryonGallery.zoomOut': 'Zoom out',
    'tryonGallery.status.running': 'Generating',
    'tryonGallery.status.completed': 'Ready',
    'tryonGallery.status.failed': 'Failed',
    'shareCard.title': 'Share card',
    'shareCard.download': 'Download image',
    'shareCard.generating': 'Generating…',
    'shareCard.hint': 'Save and post on Instagram, Pinterest or WhatsApp.',
    'shareCard.downloadError': 'Could not generate the image. Try saving a screenshot instead.',
    'shareCard.openButton': 'Create share card',
    'tryonShare.button': 'Dress like {celebrity}',
    'tryonShare.title': 'Dress like {celebrity}',
    'tryonShare.text': 'Dress like {celebrity} with Outfit Detective.',
    'tryonShare.shopTitle': 'Look like {celebrity} for about {total}',
    'tryonShare.shopText': 'Built a {celebrity}-inspired fit for about {total} with Outfit Detective.',
    'tryonShare.chooseChannel': 'Share',
    'tryonShare.facebook': 'Share on Facebook',
    'tryonShare.pinterest': 'Share on Pinterest',
    'tryonShare.email': 'Share by email',
    'tryonShare.whatsapp': 'Share on WhatsApp',
    'tryonShare.x': 'Share on X',
    'tryonShare.copied': 'Share text copied',
    'tryonShare.copyFailed': 'Could not open sharing here. Try again from your browser share menu.',
    'tryonShare.celebrityFallback': 'this creator',
    'publicTryOn.ctaCreate': 'Create your version',
    'publicTryOn.ctaFind': 'Find looks like this',
    'tryonPublic.offTitle': 'Keep it private',
    'tryonPublic.offCopy': 'Your try-on stays in your vault. Flip it on to drop it into the Fit Wall.',
    'tryonPublic.onTitle': 'Live on the Fit Wall',
    'tryonPublic.onCopy': 'Public fit check is on. You can pull it back whenever the vibe changes.',
    'tryonPublic.publishConfirm': 'Make this try-on public? It will appear on the public Fit Wall, which may be visible to search engines, and anyone with the link can view it. You can turn it off later.',
    'tryonPublic.useName': 'Use my profile name',
    'tryonPublic.useNameCopy': 'People will see your profile name on this Fit Wall post.',
    'tryonPublic.aliasCopy': 'Keep me low-key as {alias}.',
    'tryonPublic.aliasPending': 'a random fit name',
    'tryonPublic.error.try_on_not_ready': 'This try-on needs to be ready before it can hit the Fit Wall.',
    'tryonPublic.error.public_consent_required': 'Confirm public sharing before publishing this try-on.',
    'tryonPublic.error.generic': 'Could not update Fit Wall sharing right now.',
    'tryonFeed.kicker': 'Public try-on feed',
    'tryonFeed.title': 'Fit Wall',
    'tryonFeed.copy': 'Real try-ons people chose to make public. Scroll the glow-ups, tap the original post, steal the inspo respectfully.',
    'tryonFeed.emptyTitle': 'Fit Wall is waiting for its first drop',
    'tryonFeed.emptyCopy': 'Make one of your try-ons public and start the wall.',
    'tryonFeed.emptyCta': 'Open my try-ons',
    'tryonFeed.creatorFallback': 'Fit Wall creator',
    'tryonFeed.fitCheck': 'Fit check',
    'tryonFeed.shareThis': 'Send this fit',
    'tryonFeed.rightTitle': 'How a fit lands here',
    'tryonFeed.rightCopy': 'Fit Wall try-ons are posted by users. Create a look, turn it public, and pull it back anytime. Low-key with a nickname or straight with your profile name.',
    'tryonFeed.rightCta': 'My try-ons',
    'tryonFeed.rightBadge': 'AI try-on · public by choice',
    'tryon.kicker': 'Try the vibe on me',
    'tryon.title': 'See the look on your photo',
    'tryon.copy': 'Choose one of your photos and create a personal preview inspired by this fit.',
    'tryon.ctaTitle': 'Try this fit on me',
    'tryon.ctaCopy': 'Pick a photo and see the outfit on you. Instant fit check.',
    'tryon.open': 'Try now',
    'tryon.hide': 'Hide',
    'tryon.needMatchTitle': 'Run Fit AI first',
    'tryon.needMatchCopy': 'We need the detected outfit pieces before generating a try-on preview.',
    'tryon.startFitAi': 'Start Fit AI',
    'tryon.viewFitAiStatus': 'View Fit AI status',
    'tryon.noPhotosTitle': 'Add a photo first',
    'tryon.noPhotosCopy': 'Upload a full-body photo so the preview has enough context.',
    'tryon.generate': 'Generate preview',
    'tryon.generateShop': 'Generate try-on',
    'tryon.generateReplica': 'Generate replica',
    'tryon.generating': 'Generating...',
    'tryon.queueing': 'Starting...',
    'tryon.notifyEmail': 'Email me when it is ready',
    'tryon.notifyEmailOnCopy': 'Turn this on and you can leave instead of waiting on this screen.',
    'tryon.notifyEmailOffCopy': 'No email reminder. Keep My try-ons open or come back later to check.',
    'tryon.modeLabel': 'Preview type',
    'tryon.modeTryonTitle': 'The real try-on',
    'tryon.modeTryonName': 'Build the cart fit',
    'tryon.modeTryonCopy': 'Pick shoppable pieces. Anything you skip stays from the original celebrity look.',
    'tryon.cartUnavailableKicker': 'Cart fit option',
    'tryon.cartUnavailableTitle': 'Build a cart fit after product matching',
    'tryon.cartUnavailableCopy': 'First find similar products for this look. Then you can pick shop pieces and mix them with the original fit.',
    'tryon.cartUnavailableCta': 'Find similar products',
    'tryon.cartUnavailableLoading': 'Finding products...',
    'tryon.modeReplicaTitle': 'The clean replica',
    'tryon.modeReplicaName': 'Copy the original fit',
    'tryon.modeReplicaCopy': 'Use the post image as the full outfit reference. No cart, just the vibe cloned.',
    'tryon.typeSelectedProducts': 'Try-on',
    'tryon.typeReplica': 'Replica',
    'tryon.shopBuilderTitle': 'Pick the pieces you actually want',
    'tryon.shopBuilderCopy': 'Choose one product per outfit piece. Skipped pieces stay borrowed from the original look.',
    'tryon.selectedCount': '{count} picked',
    'tryon.totalUnavailable': 'Total varies',
    'tryon.shopSelected': 'Using your shop pick',
    'tryon.shopOriginalFallback': 'No pick: keeps the original piece',
    'tryon.shopNoProducts': 'No usable products for this piece yet.',
    'tryon.shopResponsibleCopy': 'Prices and availability come from the listed stores and may change. This is an AI preview, not a fit guarantee.',
    'tryon.selectProductsFirstTitle': 'Pick at least one piece',
    'tryon.selectProductsFirstCopy': 'Choose a shoppable product above, then you can pick your photo and generate the try-on.',
    'tryon.selectedProductsTitle': 'Selected pieces: {total}',
    'tryon.newPreviewShopCopy': 'Choose your body photo, then we mix your picked products with the original fit.',
    'tryon.newPreviewReplicaCopy': 'Choose your body photo and we replicate the original celebrity look.',
    'tryon.existingKicker': 'Already generated',
    'tryon.existingTitle': 'You already have try-ons for this look',
    'tryon.existingCopy': 'You can reuse one of these results or create a new preview with another photo.',
    'tryon.pendingExistingCopy': 'A preview is already generating for this look. When it is ready, it will appear in My try-ons.',
    'tryon.originalPost': 'Original post',
    'tryon.newPreviewTitle': 'Pick your fit canvas',
    'tryon.newPreviewCopy': 'Choose the photo to use as your body reference.',
    'tryon.queuedTitle': 'Preview is generating',
    'tryon.queuedEmailTitle': 'Email reminder is on',
    'tryon.queuedCopy': 'You can close this window. The result will appear in My try-ons.',
    'tryon.queuedEmailCopy': 'You can close this window. We will email you and save it in My try-ons.',
    'tryon.pendingEmailOn': 'We will email you when the image is ready and save it in My try-ons.',
    'tryon.pendingEmailOff': 'No email will be sent. You can close this window; the preview will appear in My try-ons.',
    'tryon.readyNoticeTitle': 'Your try-on is ready',
    'tryon.readyNoticeCopy': 'The preview finished and is saved in My try-ons.',
    'tryon.readyNoticeCta': 'View My try-ons',
    'tryon.readyNoticeStay': 'Stay here',
    'tryon.openTryons': 'Open My try-ons',
    'tryon.resultAlt': 'Try-on preview',
    'tryon.error.photo_framing_not_enough': 'This look needs a full-body photo. Pick or upload one where the whole outfit is visible.',
    'tryon.error.ai_match_required': 'Run Fit AI before trying the look on.',
    'tryon.error.selected_products_required': 'Pick at least one shoppable piece for this try-on, or switch to Replica.',
    'tryon.error.try_on_input_safety_rejected': 'This try-on cannot be generated because one of the reference images did not pass the safety check.',
    'tryon.error.try_on_output_safety_rejected': 'The generated image did not pass the safety check, so we did not save it.',
    'tryon.error.try_on_safety_check_failed': 'We could not complete the safety check right now. Try again in a bit.',
    'tryon.error.generic': 'Preview failed right now. Try again in a bit.',
    'tryonInfo.term': 'try-on',
    'tryonInfo.label': 'What is a try-on?',
    'tryonInfo.tooltip': 'A try-on is a private AI preview that puts a detected outfit on one of your photos. Your photo stays for your personal previews unless you choose to share the generated result.',
    'publicTryOn.kicker': 'Shared try-on',
    'publicTryOn.title': 'Dressed like {celebrity}',
    'publicTryOn.copy': '{creator} made this AI try-on with Outfit Detective.',
    'publicTryOn.shopCopy': '{creator} built this cart-ready try-on with selected pieces for about {total}.',
    'publicTryOn.ctaKicker': 'No drama, no mall laps',
    'publicTryOn.ctaTitle': 'Find the look before the algorithm buries it.',
    'publicTryOn.ctaCopy': 'Follow creators, catch fresh outfits, try the vibe on your own photos, then find similar pieces.',
    'publicTryOn.cta': 'Get your own fit radar',
    'publicTryOn.missingTitle': 'This try-on is off the grid',
    'publicTryOn.missingCopy': 'The link may be wrong or the preview is no longer available.',
    'admin.kicker': 'Manual curation',
    'admin.title': 'Upload a look.',
    'admin.subtitle': 'For the MVP, manual curation is best: image, items and shopping links.',
    'login.legalPrefix': 'By signing in you agree to our',
    'login.legalLink': 'Privacy Policy',
    'login.legalAnd': 'and',
    'login.termsLink': 'Terms of Service',
    'legal.kicker': 'Legal',
    'legal.title': 'Legal documents',
    'legal.copy': 'Information about privacy, terms of service, and data requests.',
    'legal.privacyTitle': 'Privacy Policy',
    'legal.privacyCopy': 'Personal data processing notice.',
    'legal.termsTitle': 'Terms of Service',
    'legal.termsCopy': 'Service usage terms.',
    'legal.cookiesTitle': 'Cookies and local storage',
    'legal.cookiesCopy': 'Technical browser storage used for login and language preferences.',
    'legal.contactTitle': 'Contact and data requests',
    'legal.contactCopy': 'How to contact us for privacy, GDPR rights, and legal support.',
    'legal.available': 'Available',
    'legal.backToApp': 'Back to Outfit Detective',
  },
  it: {
    'app.feed': 'Feed',
    'app.discover': 'Discover',
    'app.photos': 'Le mie foto',
    'app.fitWall': 'Fit Wall',
    'nav.feed': 'Feed',
    'nav.fitWall': 'Wall',
    'nav.photos': 'Foto',
    'nav.discover': 'Scopri',
    'nav.help': 'Aiuto',
    'app.saved': 'Salvati',
    'app.profile': 'Profilo',
    'app.legal': 'Legal',
    'app.menu': 'Menu',
    'app.logout': 'Esci',
    'app.language': 'Lingua',
    'beta.badge': 'Beta',
    'beta.copy': 'Siamo ancora in beta. I match sono suggerimenti: utili per trovare un vibe simile, non copie esatte.',
    'copyright.source': 'Fonte: {source}',
    'copyright.sourceFallback': 'post social',
    'copyright.preview': 'Usiamo la preview per trovare alternative simili. I diritti restano ai proprietari.',
    'copyright.takedown': 'Vuoi rimuoverla?',
    'betaInvite.title': 'Invita un amico',
    'betaInvite.copy': 'Conosci qualcuno che salva outfit, segue creator o cerca prodotti simili? Mandagli Outfit Detective.',
    'betaInvite.whatsapp': 'WhatsApp',
    'betaInvite.email': 'Email',
    'betaInvite.copyLink': 'Copia link',
    'betaInvite.native': 'Condividi',
    'betaInvite.shareTitle': 'Prova Outfit Detective Beta',
    'betaInvite.shareText': 'Sto provando Outfit Detective Beta: un wall personale di look social, prodotti simili e try-on virtuale sulle tue foto. È ancora in beta, ma l’idea è forte.',
    'share.copy': 'Copia link',
    'share.copyDone': 'Copiato',
    'share.copyToast': 'Link copiato',
    'share.channel.whatsapp': 'WhatsApp',
    'share.channel.email': 'Email',
    'share.channel.native': 'Condividi',
    'share.channel.pinterest': 'Pinterest',
    'authPrompt.kicker': 'Login mini',
    'authPrompt.title': 'Tieni questo fit tuo',
    'authPrompt.copy': 'Lascia la mail e ti mandiamo un codice usa e getta. Niente password, niente registrazione pesante.',
    'authPrompt.signIn': 'Entra',
    'authPrompt.sendCode': 'Manda codice',
    'authPrompt.verify': 'Continua',
    'authPrompt.tryOnTitle': 'Guarda come ti sta',
    'authPrompt.tryOnCopy': 'Entra cosi le tue foto restano solo tue.',
    'authPrompt.matchTitle': 'Cerchiamo il look',
    'authPrompt.matchCopy': 'Entra al volo e lo cerchiamo per te.',
    'authPrompt.photosTitle': 'Le tue foto',
    'authPrompt.photosCopy': 'Qui tieni le foto su cui vuoi provare i look.',
    'authPrompt.savedTitle': 'Tieni i look buoni',
    'authPrompt.savedCopy': 'Entra cosi non li perdi.',
    'authPrompt.followTitle': 'Fallo piu tuo',
    'authPrompt.followCopy': 'Entra per seguire chi ti ispira davvero.',
    'publicIntro.tagline': 'Look pubblici social · Alternative simili · Fit-on-me privato',
    'publicIntro.imageAlt': 'Idea outfit',
    'publicIntro.demoLabel': 'Demo da look reale a fit-on-me',
    'publicIntro.sourceAlt': 'Look pubblico originale di ispirazione',
    'publicIntro.resultAlt': 'Preview fit-on-me',
    'publicIntro.mediaSource': 'look inspo',
    'publicIntro.mediaResult': 'su di me',
    'publicIntro.mediaCelebrities': 'Look pubblici',
    'publicIntro.mediaSimilar': 'Vestiti simili',
    'publicIntro.mediaTryOn': 'Idee try-on',
    'publicIntro.kicker': 'Anteprima pubblica',
    'publicIntro.title': 'Metti il look sulla tua foto',
    'publicIntro.copy': 'Scegli un look pubblico e Outfit Detective ti mostra come potrebbe stare su di te con vestiti simili.',
    'publicIntro.howCopy': 'Segui profili pubblici Instagram o TikTok, apri un look e lo provi sulla tua foto.',
    'publicIntro.featuresLabel': 'Funzioni Outfit Detective',
    'publicIntro.pillCelebrities': 'Post pubblici',
    'publicIntro.pillProducts': 'Prodotti simili',
    'publicIntro.pillTryOn': 'Idee virtual try-on',
    'publicIntro.primaryCta': 'Prova questo fit',
    'publicIntro.secondaryCta': 'Vedi i look pubblicati dagli altri',
    'how.metaTitle': 'Come funziona Outfit Detective',
    'how.metaDescription': 'Prova su di te look pubblici da Instagram e TikTok, trova alternative simili per ricreare il fit e tieni private le preview fit-on-me.',
    'how.kicker': 'Come funziona',
    'how.title': 'Da un look pubblico social a un fit che puoi provare su di te.',
    'how.copy': 'Outfit Detective legge gli indizi visivi nei look pubblici di Instagram e TikTok, separa i capi, trova alternative simili e ti aiuta a testare il vibe sulle tue foto.',
    'how.primaryCta': 'Inizia con email',
    'how.secondaryCta': 'Vedi il Fit Wall',
    'how.demoLabel': 'Demo generata del flusso Outfit Detective',
    'how.demoImageAlt': 'Demo generata di Outfit Detective con capi rilevati',
    'how.demoDetected': 'Capi rilevati',
    'how.demoPieces': 'Top, giacca, pantaloni, scarpe e accessori',
    'how.demoMatch': 'Stile simile',
    'how.demoMatchTitle': 'Alternative visive piu vicine',
    'how.demoMatchMeta': 'Colore, forma e vibe abbinati',
    'how.promiseLabel': 'Cosa fa Outfit Detective',
    'how.promise.1.value': '01',
    'how.promise.1.label': 'Legge un fit da un post pubblico Instagram o TikTok',
    'how.promise.2.value': '02',
    'how.promise.2.label': 'Trova alternative simili senza conoscere il brand',
    'how.promise.3.value': '03',
    'how.promise.3.label': 'Prova il look in privato sulle tue foto',
    'how.stepsKicker': 'Il flusso',
    'how.stepsTitle': 'Tre step per ricreare un look pubblico in modo sicuro.',
    'how.steps.1.title': 'Porta il look',
    'how.steps.1.copy': 'Outfit Detective parte dai look pubblicati dai creator su Instagram e TikTok. L app si concentra sull outfit e sui segnali di styling, non sulla raccolta di dati inutili.',
    'how.steps.2.title': 'Lascia che AI divida il fit',
    'how.steps.2.copy': 'Rileviamo capi visibili, colori, silhouette e segnali di styling, poi li trasformiamo in un breakdown pulito.',
    'how.steps.3.title': 'Fanne qualcosa',
    'how.steps.3.copy': 'Sfoglia prodotti simili, salva il look, condividilo o crea una preview fit-on-me per capire se il mood funziona su di te.',
    'how.showcaseBadge': 'Preview fit-on-me',
    'how.showcaseTitle': 'Carichi una tua foto e vedi una preview privata del look su di te.',
    'how.featureKicker': 'Cosa fa',
    'how.featureTitle': 'Un motore di ricerca stile con un po di gusto.',
    'how.feature.find.title': 'Trova vestiti simili',
    'how.feature.find.copy': 'Cerca in base a come appare il look, anche quando non conosci il brand originale.',
    'how.feature.explain.title': 'Capisci il look',
    'how.feature.explain.copy': 'Vedi capi e segnali di styling invece di una lista piatta di prodotti casuali.',
    'how.feature.try.title': 'Fit on me',
    'how.feature.try.copy': 'Usa le tue foto private per creare una preview ispirata all outfit scelto.',
    'how.feature.save.title': 'Salva e confronta',
    'how.feature.save.copy': 'Tieni insieme look, idee prodotto e try-on preview per decidere piu tardi.',
    'how.trustKicker': 'Confini chiari',
    'how.trustTitle': 'Utile, ma senza false certezze.',
    'how.trustCopy': 'Outfit Detective suggerisce stili visivamente simili, senza fingere che ogni prodotto sia identico o approvato da un creator.',
    'how.trust.1': 'Le alternative simili sono suggerimenti, non prodotti esatti garantiti.',
    'how.trust.2': 'Le foto fit-on-me restano personali e servono per il tuo flusso preview.',
    'how.trust.3': 'Quando possibile, l app spiega segnali come colore, silhouette e categoria.',
    'how.finalKicker': 'Pronto quando scatta la curiosita',
    'how.finalTitle': 'La prossima volta che un look ferma lo scroll, rendilo utile.',
    'how.finalCopy': 'Trasforma un look pubblico creator in breakdown, alternative simili e idea fit-on-me privata mentre l ispirazione e ancora fresca.',
    'how.finalCta': 'Inizia la beta',
    'how.finalSecondary': 'Sfoglia look pubblici',
    'common.follow': 'Segui',
    'common.unfollow': 'Smetti di seguire',
    'common.remove': 'Rimuovi',
    'common.open': 'Apri',
    'common.preview': 'Anteprima',
    'common.loading': 'Caricamento...',
    'common.previous': 'Precedente',
    'common.next': 'Successiva',
    'common.refresh': 'Aggiorna',
    'common.cancel': 'Annulla',
    'common.on': 'On',
    'common.off': 'Off',
    'common.profile': 'profilo',
    'common.profiles': 'profili',
    'login.kicker': 'Il tuo style feed',
    'login.title': 'Entra in Outfit Detective',
    'login.subtitle': 'Segui i profili pubblici che ami e ritrova i look in un feed personale.',
    'login.heroEyebrow': 'Outfit Detective',
    'login.heroTitle': 'Prova su di te i look che vedi su Instagram e TikTok.',
    'login.heroSubtitle': 'Segui look pubblici dei creator, tieni private le preview fit-on-me e trova alternative simili per ricreare il fit.',
    'login.heroBenefitsLabel': 'Vantaggi',
    'login.pillCelebs': 'Drop freschi',
    'login.pillDetect': 'Fit radar',
    'login.pillShop': 'Alternative simili',
    'login.proofFast': 'Nuovi drop',
    'login.proofFastLabel': 'look appena usciti',
    'login.proofSources': 'Instagram + TikTok',
    'login.proofSourcesLabel': 'insieme in un feed',
    'login.proofPersonal': 'Tuo',
    'login.proofPersonalLabel': 'solo i tuoi follow',
    'login.formTitle': 'Accedi a Outfit Detective',
    'login.formSubtitle': 'Inserisci la mail e ti mandiamo un codice monouso per entrare.',
    'login.email': 'La tua email',
    'login.emailPlaceholder': 'nome@email.com',
    'login.sendCode': 'Mandami il codice monouso',
    'login.sending': 'Invio...',
    'login.otp': 'Inserisci il codice',
    'login.changeEmail': 'Cambia email',
    'login.verify': 'Continua',
    'login.verifying': 'Verifica...',
    'login.codeSent': 'Controlla la mail. Il codice resta fresh per {minutes} minuti. Se non lo vedi, dai un occhio anche a spam o promozioni.',
    'login.pendingOtpRestored': 'Il codice che ti abbiamo mandato funziona ancora.',
    'login.pendingOtpExpired': 'Quel codice e scaduto. Richiedine uno nuovo per continuare.',
    'login.useReceivedCode': 'Ho gia un codice',
    'login.resendCode': 'Mandami un altro codice',
    'login.useDevOtp': 'Usa codice dev: {otp}',
    'login.success': 'Accesso effettuato.',
    'welcome.kicker': 'Ci sei',
    'welcome.title': 'Costruisci un feed che capisce davvero il fit.',
    'welcome.copy': 'Segui look pubblici di creator e celebrity, prova il fit in privato sulle tue foto e confronta alternative simili in piu fasce: luxury, mid e budget-friendly.',
    'welcome.pointFeedTitle': 'I tuoi profili',
    'welcome.pointFeedCopy': 'Scegli chi seguire. Noi teniamo il feed pulito e personale.',
    'welcome.pointDetectTitle': 'Radar outfit',
    'welcome.pointDetectCopy': 'Trasformiamo post pubblici in indizi outfit utili.',
    'welcome.pointBudgetTitle': 'Fasce prezzo',
    'welcome.pointBudgetCopy': 'Vibe simile, dal grail luxury alla scelta piu easy.',
    'welcome.cta': 'Trova profili',
    'welcome.secondary': 'Vai al feed',
    'welcome.dismiss': 'Chiudi benvenuto',
    'welcome.legalPrefix': 'Continuando accetti la nostra',
    'welcome.legalAnd': 'e i',
    'welcome.preferencesLabel': 'Preferenze stile',
    'welcome.pref.budget': 'Budget',
    'welcome.pref.street': 'Street',
    'welcome.pref.minimal': 'Minimal',
    'welcome.pref.luxury': 'Luxury',
    'welcome.pref.everyday': 'Everyday',
    'followRefresh.kicker': 'Bentornato',
    'followRefresh.title': 'Diamo una rinfrescata ai tuoi seguiti.',
    'followRefresh.copy': 'Mentre eri via abbiamo messo in pausa qualche controllo in background, quindi alcuni profili potrebbero non essere freschissimi.',
    'followRefresh.cta': 'Aggiorna i miei seguiti',
    'followRefresh.refreshing': 'Aggiorno...',
    'followRefresh.secondary': 'Non ora',
    'followRefresh.dismiss': 'Chiudi avviso aggiornamento',
    'followRefresh.success': 'Stiamo aggiornando {count} profili.',
    'followRefresh.fresh': 'I tuoi profili seguiti sembrano gia freschi.',
    'followRefresh.error': 'Non riesco ad avviare l aggiornamento adesso.',
    'feed.following': 'Following',
    'feed.yourList': 'La tua lista',
    'feed.findProfiles': 'Trova profili',
    'feed.searchList': 'Cerca nella lista',
    'feed.noListSearchResults': 'Nessun profilo trovato.',
    'followConfirm.title': 'Smettere di seguire?',
    'followConfirm.copy': '{name} uscira dalla tua lista e non apparira piu nel tuo feed personale.',
    'followConfirm.confirming': 'Tolgo il follow...',
    'feed.noProfiles': 'Ancora nessun profilo.',
    'feed.heroKicker': 'Personal fashion feed',
    'feed.heroTitle': 'Look nuovi dai profili che segui.',
    'feed.heroEmpty': 'Segui i profili che ti interessano per creare il tuo feed outfit.',
    'feed.heroCount': 'Stai seguendo {count} {label}.',
    'feed.apiError': 'API non raggiungibile: {error}',
    'feed.loading': 'Caricamento feed...',
    'feed.emptyTitle': "Il feed e' vuoto",
    'feed.emptyCopy': 'Aggiungi profili dalla sezione Discover per costruire un feed personale.',
    'feed.popularFallbackTitle': 'Look popolari mentre prepariamo il tuo feed',
    'feed.popularFallbackCopy': 'Segui qualche profilo per rendere questo feed davvero tuo. Per ora ti mostriamo look che stanno girando.',
    'feed.popularFallbackCta': 'Trova i tuoi style crush',
    'feed.personalPendingCopy': 'Stiamo preparando i contenuti dei profili che segui. I nuovi look appariranno qui automaticamente.',
    'feed.openDetail': 'Apri il look',
    'feed.suggested': 'Suggeriti',
    'feed.noSuggestions': 'Nessun suggerimento per ora.',
    'feed.news': 'Novita',
    'feed.cachedPosts': 'post cache',
    'feed.images': 'immagini',
    'feed.updated': 'Aggiornato {date}.',
    'feed.workerReady': 'Worker pronto per raccogliere nuovi look.',
    'feed.checkingServices': 'Controllo servizi...',
    'feed.aiAction': 'Ricrea questo look',
    'feed.save': 'Salva look',
    'feed.unsave': 'Rimuovi dai salvati',
    'feed.share': 'Condividi look',
    'feed.tryOn': 'Provalo su di me',
    'feed.shareTitle': 'Look di {name}',
    'feed.shareText': 'Ho trovato questo look: {title}',
    'feed.shareFallback': 'Look Outfit Detective',
    'feed.shareFailed': 'Non riesco a copiare il link di condivisione.',
    'shareLook.kicker': 'Invito beta',
    'shareLook.cardTitle': 'Condividi questo look',
    'shareLook.cardCopy': 'Manda il look a un amico o invitalo a provare Outfit Detective Beta.',
    'shareLook.button': 'Condividi look',
    'shareLook.title': 'Look di {celebrity} su Outfit Detective',
    'shareLook.text': 'Ho trovato questo look di {celebrity} su Outfit Detective Beta: look social, prodotti simili e try-on virtuale.',
    'feed.badgeFresh': 'nuovo dai tuoi follow',
    'feed.badgePopular': 'popolare ora',
    'feed.badgeMatched': '{count} pezzi matchati',
    'feed.starterKicker': 'Parti da qui',
    'feed.starterTitle': 'Rendi il feed tuo in tre tap.',
    'feed.starterFollow': 'Segui profili',
    'feed.starterMatch': 'Steal a fit',
    'feed.starterSave': 'Salva o condividi',
    'feed.starterCta': 'Trova profili',
    'aiDialog.kicker': 'Outfit Detective AI',
    'aiDialog.title': 'Compra il look con match piu chiari.',
    'aiDialog.copy': 'Leggiamo il post, capiamo i pezzi e li mettiamo in fila per vibe e budget.',
    'aiDialog.tagsLabel': 'Dettagli match',
    'aiDialog.tagThreeLanes': '3 fasce prezzo',
    'aiDialog.tagSmartMatch': 'prima il vibe',
    'aiDialog.laneExactTitle': 'Quasi proprio lui',
    'aiDialog.laneExactCopy': 'Lo mostriamo solo quando brand, modello, categoria e segnali visivi tornano con alta confidenza.',
    'aiDialog.laneSimilarTitle': 'Stessa energia',
    'aiDialog.laneSimilarCopy': 'Pezzi vicini allo stile del post, mood brand e silhouette.',
    'aiDialog.laneBudgetTitle': 'Smart save',
    'aiDialog.laneBudgetCopy': 'Il look, senza trauma al portafoglio.',
    'aiDialog.laneLuxuryTitle': 'Luxury lane',
    'aiDialog.laneLuxuryCopy': 'Find premium quando il fit merita budget da main character.',
    'aiDialog.loadingTitle': 'Detecting del fit',
    'aiDialog.loadingCopy': 'Puoi chiudere questo pop-up: noi continuiamo in background. Quando riapri qui o la pagina del post, i find saranno pronti appena arrivano.',
    'aiDialog.loadingPageCopy': 'Stiamo cucinando in background. Resta qui o torna dopo: i find arrivano in questa pagina appena sono pronti.',
    'aiDialog.liveTitle': 'Ricerca ancora attiva',
    'aiDialog.liveCopy': 'Qualche find e gia dentro. Puoi chiudere: noi continuiamo ad aggiornare in background.',
    'aiDialog.livePageCopy': 'Qualche find e gia dentro. Continuiamo ad aggiornare questa pagina in background.',
    'aiDialog.readyTitle': 'Match pronti',
    'aiDialog.readyCopy': 'Apri quello che ti ispira. Il ranking lo rifiniamo dopo i primi test.',
    'aiDialog.errorTitle': 'Match mancato',
    'aiDialog.errorCopy': 'Questo look non ha matchato adesso. Riprova tra poco.',
    'aiDialog.retry': 'Riprova',
    'aiDialog.resultCount': '{count} prodotti trovati',
    'aiDialog.emptyTitle': 'Nessun match pulito',
    'aiDialog.emptyCopy': 'L immagine non era abbastanza chiara per risultati shopping utili.',
    'aiDialog.noLaneResults': 'Nessun find forte in questa fascia.',
    'aiDialog.lookMatch': 'look match',
    'aiDialog.vibeMatch': 'vibe match',
    'aiDialog.error.ai_matching_disabled': 'Il matching AI non e ancora attivo.',
    'aiDialog.error.event_image_missing': 'Questo post non ha un immagine da analizzare.',
    'aiDialog.error.no_items_detected': 'Il fit non era abbastanza chiaro per rilevare pezzi utili.',
    'aiDialog.error.auth_required': 'Questo match non e ancora pronto in cache. Entra per avviare una nuova ricerca.',
    'aiDialog.openLook': 'Fit source',
    'aiDialog.close': 'Chiudi dialog AI',
    'aiDialog.best': 'Best pick',
    'aiDialog.bestPick': 'Best pick · {price}',
    'aiDialog.quick.budget': 'Budget da {price}',
    'aiDialog.quick.similar': 'Similar da {price}',
    'aiDialog.quick.luxury': 'Luxury da {price}',
    'profile.refreshFeed': 'Aggiorna feed',
    'profile.refreshingFeed': 'Aggiorno...',
    'profile.feedCollectingShort': 'Raccolgo...',
    'profile.feedCollectingTitle': 'Raccolgo i primi look',
    'profile.feedCollecting': 'Sto recuperando gli ultimi post per questo profilo. Puo richiedere qualche secondo.',
    'profile.feedUpdatingTitle': 'Aggiorniamo questo profilo',
    'profile.feedUpdating': 'Sto recuperando post e dati aggiornati. I nuovi look appariranno qui automaticamente.',
    'profile.feedUpdated': 'Feed aggiornato.',
    'profile.feedRefreshFailed': 'Non riesco ad aggiornare questo feed adesso.',
    'profile.followingStatus': 'Following',
    'profile.notFollowingStatus': 'Non seguito',
    'discover.title': 'Cerca chi vuoi seguire.',
    'discover.subtitle': 'Trova profili Instagram o TikTok e aggiungili alla tua lista personale.',
    'discover.searching': 'Ricerca in corso',
    'discover.noVerified': 'Nessun profilo verificato trovato per ora.',
    'discover.loadMore': 'Scorri per vedere altri risultati',
    'discover.loadingMore': 'Caricamento...',
    'discover.added': '{name} aggiunta alla tua lista.',
    'discover.removed': 'Non lo segui piu.',
    'discover.removeError': 'Non riesco ad aggiornare il follow adesso. Riprova tra poco.',
    'discover.pasteTitle': 'Incolla un profilo',
    'discover.pasteCopy': 'Funziona con link Instagram, link TikTok o @handle.',
    'discover.pastePlaceholder': 'https://instagram.com/... oppure @username',
    'discover.checkProfile': 'Controlla',
    'discover.checkingProfile': 'Controllo...',
    'discover.profilePreviewFailed': 'Non riesco a leggere questo profilo. Controlla piattaforma e handle.',
    'discover.closeBulk': 'Chiudi import lista',
    'discover.openBulk': 'Importa piu profili',
    'discover.bulkAdded': '{count} profili aggiunti alla tua lista.',
    'discover.addProfiles': 'Aggiungi profili',
    'discover.yourList': 'La tua lista',
    'discover.noFollowingTitle': 'Ancora nessun profilo',
    'discover.noFollowingCopy': 'Cerca un profilo sopra e premi Segui.',
    'discover.inFeed': 'Nel tuo feed',
    'discover.followersUnavailable': 'Follower non disponibili',
    'discover.followers': '{count} follower',
    'discover.verifiedBadge': 'badge verificato',
    'discover.publicProfile': 'profilo pubblico con immagine',
    'discover.profileDetailsLoading': 'Carico dettagli profilo...',
    'discover.closePreview': 'Chiudi anteprima',
    'discover.audience': 'Audience',
    'discover.posts': 'Post',
    'discover.videos': 'Video',
    'discover.totalLikes': 'Like totali',
    'discover.onPlatformSince': 'Su {platform} da',
    'discover.country': 'Paese',
    'discover.latestActivity': 'Ultima attivita',
    'discover.openProfile': 'Apri profilo',
    'discover.followError': 'Non riesco a seguire questo profilo adesso. Riprova tra poco.',
    'discover.blockedKicker': 'Avviso diritti',
    'discover.blockedTitle': 'Questo profilo non puo essere seguito',
    'discover.blockedCopy': 'Per motivi di diritti, {name} non e disponibile per il follow o per usarlo nel feed personale su Outfit Detective.',
    'discover.blockedReference': 'Riferimento',
    'discover.blockedClose': 'Ho capito',
    'detail.loading': 'Caricamento look...',
    'detail.back': 'Torna al feed',
    'detail.priceUnavailable': 'Prezzo non disponibile',
    'detail.aiTitle': 'Proposte shopping per questo look',
    'detail.aiEmptyTitle': 'Ancora nessun match salvato',
    'detail.aiEmptyCopy': 'Avvia il match AI per rilevare i pezzi e ottenere le fasce shopping.',
    'detail.refreshAi': 'Aggiorna Fit AI',
    'lookShare.open': 'Crea story card',
    'lookShare.title': 'Story card',
    'lookShare.total': 'Look trovato con circa {total}',
    'lookShare.noTotal': 'Look trovato con Outfit Detective',
    'lookShare.download': 'Scarica story card',
    'lookShare.hint': 'Usala come cover Reel/Story o dopo una registrazione schermo.',
    'similarRail.kicker': 'Shopping visuale',
    'similarRail.title': 'Acquista prodotti simili',
    'similarRail.emptyCopy': 'Avvia Fit AI per sbloccare una fila visuale di match puliti per questo look.',
    'similarRail.loading': 'Cerchiamo match visuali puliti...',
    'similarRail.start': 'Trova prodotti simili',
    'similarRail.showAll': 'Mostra tutti i match',
    'similarRail.showMore': 'Mostra di piu',
    'similarRail.lane.exact': 'Exact',
    'similarRail.lane.similar': 'Similar',
    'similarRail.lane.budget': 'Budget',
    'similarRail.lane.luxury': 'Luxury',
    'productInfo.kicker': 'Match prodotto',
    'productInfo.price': 'Prezzo',
    'productInfo.match': 'Capo matchato',
    'productInfo.matchFallback': 'Questo look',
    'productInfo.copy': 'Resta qui: guarda il pezzo, poi prova tutto il fit sulla tua foto.',
    'productInfo.tryOn': 'Prova il fit',
    'productInfo.openShop': 'Apri shop',
    'saved.kicker': 'Il tuo radar outfit',
    'saved.title': 'Look salvati',
    'saved.emptyTitle': 'Ancora nessun look salvato',
    'saved.emptyCopy': 'Salva i look da rivedere, condividere o provare piu tardi.',
    'saved.emptyCta': 'Torna al feed',
    'saved.open': 'Apri look',
    'saved.remove': 'Rimuovi',
    'photos.kicker': 'Foto try-on',
    'photos.title': 'Le mie foto',
    'photos.copy': 'Tieni pronte poche foto pulite per usare Vedilo su di me.',
    'photos.uploadTitle': 'Aggiungi una foto',
    'photos.uploadCopy': 'Funziona meglio con foto luminose a figura intera.',
    'photos.usageNoteBefore': 'Queste foto servono solo per creare i tuoi',
    'photos.usageNoteAfter': 'personali. Scegli scatti chiari a figura intera per trasferire meglio i vestiti; puoi rimuoverle quando vuoi.',
    'photos.pickFile': 'Scegli una foto',
    'photos.optimizedBeforeUpload': 'Pronta per il tuo try-on',
    'photos.titlePlaceholder': 'Nome foto',
    'photos.upload': 'Carica foto',
    'photos.uploading': 'Carico...',
    'photos.uploaded': 'Foto salvata.',
    'photos.processingConsent': 'Acconsento al trattamento di questa foto con i nostri sistemi AI e OpenAI per generare try-on. La foto resta privata.',
    'photos.emptyTitle': 'Ancora nessuna foto',
    'photos.emptyCopy': 'Carica una foto a figura intera per preview try-on migliori.',
    'photos.default': 'Default',
    'photos.makeDefault': 'Usa default',
    'photos.framing.full_body': 'Figura intera',
    'photos.framing.upper_body': 'Parte alta',
    'photos.framing.portrait': 'Ritratto',
    'photos.framing.unknown': 'Non sicuro',
    'photos.error.image_too_large': 'L immagine ottimizzata e ancora troppo grande. Prova un altra foto.',
    'photos.error.photo_processing_consent_required': 'Conferma il consenso al trattamento della foto prima di caricarla.',
    'photos.error.photo_safety_rejected': 'Questa foto non puo essere usata per il try-on. Carica una foto sicura, con adulto vestito.',
    'photos.error.photo_safety_rejected_with_reason': 'Questa foto non puo essere usata per il try-on perche sembra contenere {reason}. Carica una foto sicura, con adulto vestito.',
    'photos.error.reason.adult': 'contenuto per adulti o nudita',
    'photos.error.reason.racy': 'contenuto troppo esplicito',
    'photos.error.reason.violence': 'contenuto violento',
    'photos.error.reason.selfHarm': 'contenuto di autolesionismo',
    'photos.error.reason.minors': 'contenuto sessuale con minorenni',
    'photos.error.reason.generic': 'contenuti non conformi alle regole di sicurezza foto',
    'photos.error.photo_safety_check_failed': 'Non riusciamo a completare il controllo sicurezza della foto adesso. Riprova tra poco.',
    'tryon.cartPresetLabel': 'Scelte automatiche carrello',
    'tryon.cartPresetClosest': 'Piu simile',
    'tryon.cartPresetLuxury': 'Luxury',
    'tryon.cartPresetBudget': 'Economica',
    'account.kicker': 'Profilo',
    'account.title': 'Il tuo profilo Outfit Detective',
    'account.copy': 'Gestisci foto e strumenti personali.',
    'account.overview': 'Panoramica',
    'account.tryonTitle': 'Try-on pronto',
    'account.tryonCopy': 'Le tue foto alimentano preview personali dai look che scopri.',
    'account.privacyTitle': 'Decidi tu',
    'account.privacyCopy': 'Non ci servono dati personali per rendere utile l app. Queste preferenze sono opzionali, modificabili quando vuoi e servono solo a personalizzare la tua esperienza.',
    'account.legalLink': 'Privacy e impostazioni legali',
    'account.shopAreaTitle': 'La tua zona fit radar',
    'account.shopAreaCopy': 'Outfit Detective cerca abiti su piattaforme online e anche nei negozi fisici. Scegli una zona base e daremo priorita ai match abbastanza vicini da provarli, prenderli o riceverli piu in fretta.',
    'account.locationLabel': 'Zona base',
    'account.locationPlaceholder': 'Citta o zona, es. Milano, Porta Nuova',
    'account.locationResults': 'Suggerimenti localita',
    'account.locationSearching': 'Cerco localita...',
    'account.locationNoResults': 'Nessun match pulito. Prova citta piu paese.',
    'account.locationSelected': 'Zona verificata selezionata',
    'account.shoppingCountryFromLocation': 'Paese shopping: {country}, calcolato dalla tua zona fit radar.',
    'account.radiusLabel': 'Raggio ricerca: {km} km',
    'account.radiusAria': 'Raggio ricerca',
    'account.locationPrivacy': 'Non serve un indirizzo preciso. Basta citta o quartiere, e puoi cancellarlo quando vuoi.',
    'account.styleGenderTitle': 'Stile del wall',
    'account.styleGenderCopy': 'Scegli quali look vuoi vedere prima nel wall e nei profili suggeriti.',
    'account.styleGender.all': 'Tutti',
    'account.styleGender.menswear': 'Uomo',
    'account.styleGender.womenswear': 'Donna',
    'account.styleGenderConfirm': 'Confermo che voglio vedere tutti i look nel wall.',
    'account.styleGenderSummaryTitle': 'Preferenza wall',
    'account.styleGenderSummary.all': 'Wall e suggeriti possono includere ogni stile.',
    'account.styleGenderSummary.menswear': 'Wall e suggeriti danno priorita a uomini e look menswear.',
    'account.styleGenderSummary.womenswear': 'Wall e suggeriti danno priorita a donne e look womenswear.',
    'account.identityTitle': 'Come vuoi apparire',
    'account.identityCopy': 'Per cose pubbliche come Fit Wall e try-on condivisi, scegli nome reale o resta low-key con un nickname. La tua email rimane dietro le quinte.',
    'account.useNickname': 'Nickname',
    'account.useRealName': 'Nome reale',
    'account.realNameLabel': 'Nome reale',
    'account.realNamePlaceholder': 'Nome mostrato solo se lo scegli',
    'account.nicknameLabel': 'Nickname',
    'account.nicknamePlaceholder': 'Il tuo nome stile pubblico',
    'account.useSuggestion': 'Usa {nickname}',
    'account.save': 'Salva profilo',
    'account.saving': 'Salvo...',
    'account.saved': 'Profilo salvato.',
    'account.areaSummaryTitle': 'Priorita shopping',
    'account.areaSummarySet': 'Priorita a match online e negozi entro {km} km da {location}.',
    'account.areaSummaryEmpty': 'Nessuna zona impostata. Terremo risultati ampi e prima online.',
    'account.publicSummaryTitle': 'Nome pubblico',
    'account.publicSummaryCopy': 'I try-on pubblici appariranno come {name}.',
    'account.deleteTitle': 'Cancella account',
    'account.deleteCopy': 'Sospendiamo subito il tuo account. Hai 15 giorni per riattivarlo accedendo di nuovo; dopo eliminiamo definitivamente profilo, foto, try-on, follow, preferenze e sessioni.',
    'account.deleteConfirmLabel': 'Scrivi DELETE per confermare',
    'account.deleteConfirmMismatch': 'Scrivi DELETE esattamente per confermare la cancellazione.',
    'account.deleteButton': 'Cancella il mio account',
    'account.deleting': 'Programmo cancellazione...',
    'account.deleteError': 'Non riesco a programmare la cancellazione adesso.',
    'account.error.nickname_required': 'Scegli un nickname per continuare.',
    'account.error.display_name_required': 'Aggiungi un nome reale oppure passa a nickname.',
    'account.error.request_failed': 'Non riesco a salvare ora. Riprova.',
    'account.error.api_unreachable': 'API non raggiungibile ora.',
    'account.error.profile_not_found': 'Profilo non trovato.',
    'alerts.tab': 'Avvisi',
    'alerts.title': 'Avvisi',
    'alerts.copy': 'Aggiornamenti importanti sui profili che segui e sull attivita del tuo account.',
    'alerts.notice': 'Avviso',
    'alerts.emptyTitle': 'Nessun avviso',
    'alerts.emptyCopy': 'Quando cambia qualcosa di importante, lo troverai qui.',
    'alerts.markRead': 'Segna letto',
    'alerts.emailSent': 'Email inviata: {date}',
    'alerts.error': 'Non riesco a caricare gli avvisi adesso.',
    'alerts.profileIncomplete.title': 'Completa il tuo profilo',
    'alerts.profileIncomplete.bodyBoth': 'Aggiungi una zona shopping e conferma la preferenza wall, cosi Outfit Detective puo migliorare feed e suggeriti.',
    'alerts.profileIncomplete.bodyLocation': 'Aggiungi una zona shopping per migliorare feed e suggerimenti prodotto.',
    'alerts.profileIncomplete.bodyStyleGender': 'Conferma la preferenza stile del wall, cosi Outfit Detective puo migliorare feed e profili suggeriti.',
    'alerts.profileIncomplete.cta': 'Completa profilo',
    'alerts.followedProfile.fallbackName': 'Un profilo',
    'alerts.followedProfile.banned.title': 'Profilo seguito non piu disponibile',
    'alerts.followedProfile.banned.body': '{profile} e stato rimosso dai tuoi seguiti e non puo essere seguito al momento per motivi di diritti o gestione contenuti.',
    'alerts.followedProfile.banned.bodyReason': '{profile} e stato rimosso dai tuoi seguiti e non puo essere seguito al momento per motivi di diritti o gestione contenuti. Motivo: {reason}',
    'alerts.followedProfile.deletedBanned.title': 'Profilo seguito cancellato e bloccato',
    'alerts.followedProfile.deletedBanned.body': '{profile} e stato cancellato, rimosso dai tuoi seguiti e non puo essere seguito al momento per motivi di diritti o gestione contenuti.',
    'alerts.followedProfile.deletedBanned.bodyReason': '{profile} e stato cancellato, rimosso dai tuoi seguiti e non puo essere seguito al momento per motivi di diritti o gestione contenuti. Motivo: {reason}',
    'alerts.followedProfile.removed.title': 'Profilo seguito rimosso',
    'alerts.followedProfile.removed.body': '{profile} e stato cancellato e rimosso dai tuoi seguiti per motivi di diritti o gestione contenuti.',
    'alerts.followedProfile.removed.bodyReason': '{profile} e stato cancellato e rimosso dai tuoi seguiti per motivi di diritti o gestione contenuti. Motivo: {reason}',
    'tryonGallery.title': 'I miei try-on',
    'tryonGallery.emptyTitle': 'Ancora nessun try-on',
    'tryonGallery.emptyCopy': 'Generane uno dalla pagina dettaglio di un look e lo troverai qui.',
    'tryonGallery.untitled': 'Try-on generato',
    'tryonGallery.originalPost': 'Post originale',
    'tryonGallery.created': 'Creato',
    'tryonGallery.userPhoto': 'La tua foto',
    'tryonGallery.email': 'Email',
    'tryonGallery.emailOn': 'Avviso attivo',
    'tryonGallery.emailOff': 'Nessun avviso',
    'tryonGallery.openEvent': 'Fit source',
    'tryonGallery.openGeneratedImage': 'Apri immagine try-on generata',
    'tryonGallery.openOriginalImage': 'Apri immagine del post originale',
    'tryonGallery.delete': 'Elimina',
    'tryonGallery.deleting': 'Elimino...',
    'tryonGallery.deleteConfirm': 'Eliminare questo try-on? Sparira dalla tua galleria, dal link condiviso e dal Fit Wall.',
    'tryonGallery.error.deleteFailed': 'Non riesco a eliminare questo try-on adesso.',
    'tryonGallery.error.try_on_preview_not_found': 'Questo try-on e gia stato eliminato.',
    'tryonGallery.viewerLabel': 'Visualizzatore foto',
    'tryonGallery.zoomIn': 'Aumenta zoom',
    'tryonGallery.zoomOut': 'Riduci zoom',
    'tryonGallery.status.running': 'In generazione',
    'tryonGallery.status.completed': 'Pronto',
    'tryonGallery.status.failed': 'Fallito',
    'shareCard.title': 'Scheda condivisione',
    'shareCard.download': 'Scarica immagine',
    'shareCard.generating': 'Generazione…',
    'shareCard.hint': 'Salva e condividi su Instagram, Pinterest o WhatsApp.',
    'shareCard.downloadError': 'Impossibile generare l\'immagine. Prova a fare uno screenshot.',
    'shareCard.openButton': 'Crea scheda condivisione',
    'tryonShare.button': 'Vestiti come {celebrity}',
    'tryonShare.title': 'Vestiti come {celebrity}',
    'tryonShare.text': 'Vestiti come {celebrity} con Outfit Detective.',
    'tryonShare.shopTitle': 'Look like {celebrity} con circa {total}',
    'tryonShare.shopText': 'Ho costruito un fit ispirato a {celebrity} con circa {total} su Outfit Detective.',
    'tryonShare.chooseChannel': 'Condividi',
    'tryonShare.facebook': 'Condividi su Facebook',
    'tryonShare.pinterest': 'Condividi su Pinterest',
    'tryonShare.email': 'Condividi via email',
    'tryonShare.whatsapp': 'Condividi su WhatsApp',
    'tryonShare.x': 'Condividi su X',
    'tryonShare.copied': 'Testo copiato',
    'tryonShare.copyFailed': 'Non riesco ad aprire la condivisione qui. Riprova dal menu del browser.',
    'tryonShare.celebrityFallback': 'questo creator',
    'publicTryOn.ctaCreate': 'Crea la tua versione',
    'publicTryOn.ctaFind': 'Trova look simili',
    'tryonPublic.offTitle': 'Tienilo privato',
    'tryonPublic.offCopy': 'Il try-on resta nel tuo archivio. Accendilo se vuoi dropparlo nel Fit Wall.',
    'tryonPublic.onTitle': 'Live nel Fit Wall',
    'tryonPublic.onCopy': 'Fit check pubblico attivo. Puoi ritirarlo quando cambia il vibe.',
    'tryonPublic.publishConfirm': 'Rendere pubblico questo try-on? Apparira nel Fit Wall pubblico, che puo essere visibile ai motori di ricerca, e chi ha il link potra vederlo. Puoi disattivarlo piu tardi.',
    'tryonPublic.useName': 'Usa il mio nome profilo',
    'tryonPublic.useNameCopy': 'Nel Fit Wall le persone vedranno il tuo nome profilo.',
    'tryonPublic.aliasCopy': 'Tienimi low-key come {alias}.',
    'tryonPublic.aliasPending': 'un nickname random',
    'tryonPublic.error.try_on_not_ready': 'Questo try-on deve essere pronto prima di finire nel Fit Wall.',
    'tryonPublic.error.public_consent_required': 'Conferma la condivisione pubblica prima di pubblicare questo try-on.',
    'tryonPublic.error.generic': 'Non riesco ad aggiornare la condivisione Fit Wall adesso.',
    'tryonFeed.kicker': 'Feed pubblico try-on',
    'tryonFeed.title': 'Fit Wall',
    'tryonFeed.copy': 'Try-on reali che le persone hanno scelto di rendere pubblici. Scrolla i glow-up, apri il post originale, prendi inspo con stile.',
    'tryonFeed.emptyTitle': 'Il Fit Wall aspetta il primo drop',
    'tryonFeed.emptyCopy': 'Rendi pubblico uno dei tuoi try-on e apri le danze.',
    'tryonFeed.emptyCta': 'Apri i miei try-on',
    'tryonFeed.creatorFallback': 'Creator Fit Wall',
    'tryonFeed.fitCheck': 'Fit check',
    'tryonFeed.shareThis': 'Manda questo fit',
    'tryonFeed.rightTitle': 'Come arriva un fit qui',
    'tryonFeed.rightCopy': 'I try-on del Fit Wall sono pubblicati dagli utenti. Crei un look, lo rendi pubblico e puoi ritirarlo quando vuoi. Low-key con nickname o diretto col nome profilo.',
    'tryonFeed.rightCta': 'I miei try-on',
    'tryonFeed.rightBadge': 'AI try-on · pubblico per scelta',
    'tryon.kicker': 'Vedilo su di me',
    'tryon.title': 'Guarda il look sulla tua foto',
    'tryon.copy': 'Scegli una tua foto e crea una preview personale ispirata a questo fit.',
    'tryon.ctaTitle': 'Vedilo su di me',
    'tryon.ctaCopy': 'Scegli una tua foto e guarda il fit addosso. Fit check subito.',
    'tryon.open': 'Prova ora',
    'tryon.hide': 'Nascondi',
    'tryon.needMatchTitle': 'Prima avvia Fit AI',
    'tryon.needMatchCopy': 'Ci servono i pezzi rilevati prima di generare la preview try-on.',
    'tryon.startFitAi': 'Avvia Fit AI',
    'tryon.viewFitAiStatus': 'Vedi stato Fit AI',
    'tryon.noPhotosTitle': 'Aggiungi prima una foto',
    'tryon.noPhotosCopy': 'Carica una foto a figura intera, cosi la preview ha abbastanza contesto.',
    'tryon.generate': 'Genera preview',
    'tryon.generateShop': 'Genera try-on',
    'tryon.generateReplica': 'Genera replica',
    'tryon.generating': 'Genero...',
    'tryon.queueing': 'Avvio...',
    'tryon.notifyEmail': 'Avvisami via email quando e pronta',
    'tryon.notifyEmailOnCopy': 'Attivalo e puoi uscire dalla schermata invece di aspettare qui.',
    'tryon.notifyEmailOffCopy': 'Nessun avviso email. Tieni aperti i miei try-on o torna piu tardi a controllare.',
    'tryon.modeLabel': 'Tipo preview',
    'tryon.modeTryonTitle': 'Il vero try-on',
    'tryon.modeTryonName': 'Costruisci il fit carrello',
    'tryon.modeTryonCopy': 'Scegli i pezzi shop. Quello che salti resta dal look originale della celebrity.',
    'tryon.cartUnavailableKicker': 'Opzione fit carrello',
    'tryon.cartUnavailableTitle': 'Costruisci il fit carrello dopo il match prodotti',
    'tryon.cartUnavailableCopy': 'Prima troviamo prodotti simili per questo look. Poi potrai scegliere i pezzi shop e mixarli con il fit originale.',
    'tryon.cartUnavailableCta': 'Trova prodotti simili',
    'tryon.cartUnavailableLoading': 'Cerco prodotti...',
    'tryon.modeReplicaTitle': 'La replica pulita',
    'tryon.modeReplicaName': 'Copia il fit originale',
    'tryon.modeReplicaCopy': 'Usa il post come riferimento outfit completo. Niente carrello, solo vibe clonato.',
    'tryon.typeSelectedProducts': 'Try-on',
    'tryon.typeReplica': 'Replica',
    'tryon.shopBuilderTitle': 'Scegli i pezzi che vuoi davvero',
    'tryon.shopBuilderCopy': 'Seleziona un prodotto per capo. I capi saltati restano presi dal look originale.',
    'tryon.selectedCount': '{count} scelti',
    'tryon.totalUnavailable': 'Totale variabile',
    'tryon.shopSelected': 'Usiamo il tuo pezzo shop',
    'tryon.shopOriginalFallback': 'Non scelto: resta il pezzo originale',
    'tryon.shopNoProducts': 'Ancora nessun prodotto utile per questo capo.',
    'tryon.shopResponsibleCopy': 'Prezzi e disponibilita arrivano dagli store e possono cambiare. Questa e una preview AI, non una garanzia di vestibilita.',
    'tryon.selectProductsFirstTitle': 'Scegli almeno un capo',
    'tryon.selectProductsFirstCopy': 'Seleziona un prodotto shop qui sopra, poi potrai scegliere la foto e generare il try-on.',
    'tryon.selectedProductsTitle': 'Pezzi scelti: {total}',
    'tryon.newPreviewShopCopy': 'Scegli la tua foto: mixiamo i prodotti scelti con il fit originale.',
    'tryon.newPreviewReplicaCopy': 'Scegli la tua foto e replichiamo il look originale della celebrity.',
    'tryon.existingKicker': 'Gia generati',
    'tryon.existingTitle': 'Hai gia dei try-on per questo look',
    'tryon.existingCopy': 'Puoi riusare uno di questi risultati oppure creare una nuova preview con un altra foto.',
    'tryon.pendingExistingCopy': 'C e gia una preview in generazione per questo look. Appena pronta la trovi nei miei try-on.',
    'tryon.originalPost': 'Post originale',
    'tryon.newPreviewTitle': 'Scegli la tua fit canvas',
    'tryon.newPreviewCopy': 'Scegli la foto da usare come riferimento del tuo corpo.',
    'tryon.queuedTitle': 'Preview in generazione',
    'tryon.queuedEmailTitle': 'Avviso email attivo',
    'tryon.queuedCopy': 'Puoi chiudere questa finestra. Il risultato apparira nei miei try-on.',
    'tryon.queuedEmailCopy': 'Puoi chiudere questa finestra. Ti avvisiamo via email e la salviamo nei miei try-on.',
    'tryon.pendingEmailOn': 'Ti invieremo una email quando l immagine e pronta e la salveremo nei miei try-on.',
    'tryon.pendingEmailOff': 'Non invieremo email. Puoi chiudere questa finestra: la preview apparira nei miei try-on.',
    'tryon.readyNoticeTitle': 'Il tuo try-on e pronto',
    'tryon.readyNoticeCopy': 'La preview e finita ed e salvata nei miei try-on.',
    'tryon.readyNoticeCta': 'Vedi i miei try-on',
    'tryon.readyNoticeStay': 'Resta qui',
    'tryon.openTryons': 'Apri i miei try-on',
    'tryon.resultAlt': 'Preview try-on',
    'tryon.error.photo_framing_not_enough': 'Questo look richiede una foto a figura intera. Scegli o carica una foto dove si vede tutto il fit.',
    'tryon.error.ai_match_required': 'Avvia Fit AI prima di provare il look.',
    'tryon.error.selected_products_required': 'Scegli almeno un capo shop per questo try-on, oppure passa a Replica.',
    'tryon.error.try_on_input_safety_rejected': 'Non possiamo generare questo try-on perche una delle immagini di riferimento non ha superato il controllo sicurezza.',
    'tryon.error.try_on_output_safety_rejected': 'L immagine generata non ha superato il controllo sicurezza, quindi non l abbiamo salvata.',
    'tryon.error.try_on_safety_check_failed': 'Non riusciamo a completare il controllo sicurezza adesso. Riprova tra poco.',
    'tryon.error.generic': 'La preview non e riuscita adesso. Riprova tra poco.',
    'tryonInfo.term': 'try-on',
    'tryonInfo.label': 'Cos e un try-on?',
    'tryonInfo.tooltip': 'Un try-on e una preview AI privata che mette un outfit rilevato su una delle tue foto. La tua foto resta per le preview personali; condividi solo il risultato generato se lo scegli tu.',
    'publicTryOn.kicker': 'Try-on condiviso',
    'publicTryOn.title': 'Vestiti come {celebrity}',
    'publicTryOn.copy': '{creator} ha creato questo try-on AI con Outfit Detective.',
    'publicTryOn.shopCopy': '{creator} ha costruito questo try-on pronto-carrello con pezzi selezionati per circa {total}.',
    'publicTryOn.ctaKicker': 'Zero sbatti, zero giri a vuoto',
    'publicTryOn.ctaTitle': 'Trova il look prima che l algoritmo lo seppellisca.',
    'publicTryOn.ctaCopy': 'Segui creator, salva i fit freschi, prova il vibe sulle tue foto e trova i pezzi simili.',
    'publicTryOn.cta': 'Crea il tuo fit radar',
    'publicTryOn.missingTitle': 'Questo try-on non e piu raggiungibile',
    'publicTryOn.missingCopy': 'Il link potrebbe essere sbagliato o la preview non e piu disponibile.',
    'admin.kicker': 'Manual curation',
    'admin.title': 'Carica un look.',
    'admin.subtitle': "Per l'MVP conviene partire curati a mano: immagine, item e link shopping.",
    'login.legalPrefix': 'Accedendo accetti la nostra',
    'login.legalLink': 'Privacy Policy',
    'login.legalAnd': 'e i',
    'login.termsLink': 'Terms of Service',
    'legal.kicker': 'Legal',
    'legal.title': 'Documenti legali',
    'legal.copy': 'Informazioni su privacy, termini del servizio e richieste sui dati.',
    'legal.privacyTitle': 'Privacy Policy',
    'legal.privacyCopy': 'Informativa sul trattamento dei dati personali.',
    'legal.termsTitle': 'Terms of Service',
    'legal.termsCopy': 'Condizioni di utilizzo del servizio.',
    'legal.cookiesTitle': 'Cookie e storage locale',
    'legal.cookiesCopy': 'Storage tecnico nel browser usato per login e preferenze lingua.',
    'legal.contactTitle': 'Contatti e richieste dati',
    'legal.contactCopy': 'Come contattarci per privacy, diritti GDPR e supporto legale.',
    'legal.available': 'Disponibile',
    'legal.backToApp': 'Torna a Outfit Detective',
  },
};

const languageOverrides = {
  de: {
    'app.logout': 'Abmelden',
    'app.language': 'Sprache',
    'beta.badge': 'Beta',
    'beta.copy': 'Noch in der Beta. Die Matches sind Vorschlaege: hilfreich fuer einen aehnlichen Look, keine exakten Kopien.',
    'copyright.source': 'Quelle: {source}',
    'copyright.sourceFallback': 'Social-Post',
    'copyright.preview': 'Wir nutzen die Vorschau, um aehnliche Alternativen zu finden. Die Rechte bleiben bei den Inhabern.',
    'copyright.takedown': 'Entfernung wuenschen?',
    'betaInvite.title': 'Freund einladen',
    'betaInvite.copy': 'Kennst du jemanden, der Outfits speichert, Creator verfolgt oder aehnliche Produkte sucht? Schick ihm Outfit Detective.',
    'betaInvite.whatsapp': 'WhatsApp',
    'betaInvite.email': 'E-Mail',
    'betaInvite.copyLink': 'Link kopieren',
    'betaInvite.native': 'Teilen',
    'betaInvite.shareTitle': 'Outfit Detective Beta testen',
    'betaInvite.shareText': 'Ich teste Outfit Detective Beta: ein persoenlicher Wall mit Social Looks, aehnlichen Produkten und virtuellem Try-on auf deinen Fotos. Noch Beta, aber die Idee ist stark.',
    'share.copy': 'Link kopieren',
    'share.copyDone': 'Kopiert',
    'share.copyToast': 'Link kopiert',
    'share.channel.whatsapp': 'WhatsApp',
    'share.channel.email': 'E-Mail',
    'share.channel.native': 'Teilen',
    'share.channel.pinterest': 'Pinterest',
    'shareLook.kicker': 'Beta-Einladung',
    'shareLook.cardTitle': 'Diesen Look teilen',
    'shareLook.cardCopy': 'Schick den Look an einen Freund oder lade ihn ein, Outfit Detective Beta zu testen.',
    'shareLook.button': 'Look teilen',
    'shareLook.title': '{celebrity} Look auf Outfit Detective',
    'shareLook.text': 'Ich habe diesen {celebrity} Look auf Outfit Detective Beta gefunden: Social Looks, aehnliche Produkte und virtuelles Try-on.',
    'authPrompt.kicker': 'Mini-Login',
    'authPrompt.title': 'Mach diesen Fit zu deinem',
    'authPrompt.copy': 'E-Mail rein, Einmal-Code bekommen. Kein Passwort, keine schwere Registrierung.',
    'authPrompt.signIn': 'Anmelden',
    'authPrompt.sendCode': 'Code senden',
    'authPrompt.verify': 'Weiter',
    'authPrompt.tryOnTitle': 'Schau, wie es dir steht',
    'authPrompt.tryOnCopy': 'Melde dich an, damit deine Fotos privat bleiben.',
    'authPrompt.matchTitle': 'Lass uns den Look finden',
    'authPrompt.matchCopy': 'Melde dich kurz an, dann suchen wir ihn fuer dich.',
    'authPrompt.photosTitle': 'Deine Fotos',
    'authPrompt.photosCopy': 'Hier liegen die Fotos, auf denen du Looks testen willst.',
    'authPrompt.savedTitle': 'Behalte die guten Looks',
    'authPrompt.savedCopy': 'Melde dich an, damit sie nicht verloren gehen.',
    'authPrompt.followTitle': 'Mach es mehr zu dir',
    'authPrompt.followCopy': 'Folge den Leuten, die deinen Stil wirklich inspirieren.',
    'publicIntro.tagline': 'Oeffentliche Social Looks · Aehnliche Alternativen · Privates Fit-on-me',
    'publicIntro.imageAlt': 'Outfit-Idee',
    'publicIntro.demoLabel': 'Demo vom realen Look zum Fit-on-me',
    'publicIntro.sourceAlt': 'Originale oeffentliche Outfit-Inspiration',
    'publicIntro.resultAlt': 'Fit-on-me Preview',
    'publicIntro.mediaSource': 'fit inspo',
    'publicIntro.mediaResult': 'on me',
    'publicIntro.mediaCelebrities': 'Oeffentliche Looks',
    'publicIntro.mediaSimilar': 'Aehnliche Kleidung',
    'publicIntro.mediaTryOn': 'Try-on-Ideen',
    'publicIntro.kicker': 'Oeffentliche Vorschau',
    'publicIntro.title': 'Setz den Look auf dein Foto',
    'publicIntro.copy': 'Waehle einen oeffentlichen Look, und Outfit Detective zeigt dir, wie er mit aehnlicher Kleidung an dir aussehen koennte.',
    'publicIntro.howCopy': 'Folge oeffentlichen Instagram- oder TikTok-Profilen, oeffne einen Look und teste ihn auf deinem Foto.',
    'publicIntro.featuresLabel': 'Outfit Detective Funktionen',
    'publicIntro.pillCelebrities': 'Oeffentliche Posts',
    'publicIntro.pillProducts': 'Aehnliche Produkte',
    'publicIntro.pillTryOn': 'Virtual Try-on Ideen',
    'publicIntro.primaryCta': 'Diesen Fit testen',
    'publicIntro.secondaryCta': 'Looks anderer Nutzer ansehen',
    'how.metaTitle': 'So funktioniert Outfit Detective',
    'how.metaDescription': 'Teste oeffentliche Looks von Instagram und TikTok, finde aehnliche Alternativen zum Nachstylen und halte Fit-on-me Previews privat.',
    'how.kicker': 'So funktioniert es',
    'how.title': 'Vom oeffentlichen Social Look zu einem Fit, den du an dir testen kannst.',
    'how.copy': 'Outfit Detective liest visuelle Hinweise in oeffentlichen Instagram- und TikTok-Looks, trennt die Pieces, findet aehnliche Alternativen und hilft dir, den Vibe auf deinen eigenen Fotos zu testen.',
    'how.primaryCta': 'Mit E-Mail starten',
    'how.secondaryCta': 'Fit Wall ansehen',
    'how.demoLabel': 'Generierte Demo des Outfit Detective Flows',
    'how.demoImageAlt': 'Generierte Outfit Detective Demo mit erkannten Pieces',
    'how.demoDetected': 'Erkannte Pieces',
    'how.demoPieces': 'Top, Jacke, Hose, Schuhe und Accessoires',
    'how.demoMatch': 'Aehnlicher Stil',
    'how.demoMatchTitle': 'Naechste visuelle Alternativen',
    'how.demoMatchMeta': 'Farbe, Form und Vibe gematcht',
    'how.promiseLabel': 'Was Outfit Detective macht',
    'how.promise.1.value': '01',
    'how.promise.1.label': 'Liest den Fit aus einem oeffentlichen Instagram- oder TikTok-Post',
    'how.promise.2.value': '02',
    'how.promise.2.label': 'Findet aehnliche Alternativen ohne Brand-Wissen',
    'how.promise.3.value': '03',
    'how.promise.3.label': 'Testet den Look privat auf deinen Fotos',
    'how.stepsKicker': 'Der Flow',
    'how.stepsTitle': 'Drei Schritte, um einen oeffentlichen Look sicher nachzustylen.',
    'how.steps.1.title': 'Bring den Look',
    'how.steps.1.copy': 'Outfit Detective startet mit Looks, die Creator auf Instagram und TikTok veroeffentlichen. Die App fokussiert Outfit und Styling-Signale, nicht unnoetige Daten.',
    'how.steps.2.title': 'AI teilt den Fit',
    'how.steps.2.copy': 'Wir erkennen sichtbare Pieces, Farben, Silhouette und Styling-Signale und machen daraus einen klaren Breakdown.',
    'how.steps.3.title': 'Mach etwas daraus',
    'how.steps.3.copy': 'Sieh aehnliche Produkte, speichere den Look, teile ihn oder erstelle eine Fit-on-me Preview.',
    'how.showcaseBadge': 'Fit-on-me Preview',
    'how.showcaseTitle': 'Lade dein Foto hoch und sieh eine private Preview des Creator-Looks an dir.',
    'how.featureKicker': 'Was es kann',
    'how.featureTitle': 'Eine Style-Suche mit etwas Geschmack.',
    'how.feature.find.title': 'Aehnliche Kleidung finden',
    'how.feature.find.copy': 'Suche nach dem Aussehen des Outfits, auch wenn du die Original-Brand nicht kennst.',
    'how.feature.explain.title': 'Den Look verstehen',
    'how.feature.explain.copy': 'Sieh Pieces und Styling-Signale statt einer flachen Liste zufaelliger Produkte.',
    'how.feature.try.title': 'Fit on me',
    'how.feature.try.copy': 'Nutze deine privaten Fotos fuer eine Preview inspiriert vom gewaehlten Outfit.',
    'how.feature.save.title': 'Speichern und vergleichen',
    'how.feature.save.copy': 'Behalte Looks, Produktideen und Try-on Previews zusammen fuer spaetere Entscheidungen.',
    'how.trustKicker': 'Klare Grenzen',
    'how.trustTitle': 'Nuetzlich, aber ohne falsche Sicherheit.',
    'how.trustCopy': 'Outfit Detective schlaegt visuell aehnliche Styles vor und tut nicht so, als waere jedes Produkt exakt oder endorsed.',
    'how.trust.1': 'Aehnliche Alternativen sind Vorschlaege, keine garantierten exakten Produkte.',
    'how.trust.2': 'Fit-on-me Fotos bleiben persoenlich und dienen deinem Preview-Flow.',
    'how.trust.3': 'Wenn moeglich erklaert die App Signale wie Farbe, Silhouette und Kategorie.',
    'how.finalKicker': 'Bereit, wenn Neugier kommt',
    'how.finalTitle': 'Mach den naechsten Look, der deinen Scroll stoppt, nuetzlich.',
    'how.finalCopy': 'Verwandle einen oeffentlichen Creator-Look in Breakdown, aehnliche Alternativen und eine private Fit-on-me Idee, solange die Inspiration frisch ist.',
    'how.finalCta': 'Beta starten',
    'how.finalSecondary': 'Oeffentliche Looks ansehen',
    'app.photos': 'Meine Fotos',
    'app.profile': 'Profil',
    'app.menu': 'Menü',
    'common.follow': 'Folgen',
    'common.unfollow': 'Nicht mehr folgen',
    'common.remove': 'Entfernen',
    'profile.followingStatus': 'Folge ich',
    'profile.notFollowingStatus': 'Nicht gefolgt',
    'common.open': 'Öffnen',
    'common.preview': 'Vorschau',
    'common.cancel': 'Abbrechen',
    'followConfirm.title': 'Profil nicht mehr folgen?',
    'followConfirm.copy': '{name} verschwindet aus deiner Liste und deinem persönlichen Feed.',
    'followConfirm.confirming': 'Entfolge...',
    'login.heroTitle': 'Teste die Looks, die du auf Instagram und TikTok siehst.',
    'login.heroSubtitle': 'Folge oeffentlichen Creator-Looks, halte Fit-on-me Previews privat und finde aehnliche Alternativen, um den Fit nachzustylen.',
    'login.pillCelebs': 'Fresh Drops',
    'login.pillDetect': 'Fit Radar',
    'login.pillShop': 'Aehnliche Finds',
    'login.proofFast': 'Neue Drops',
    'login.proofFastLabel': 'Looks, sobald sie landen',
    'login.proofSources': 'Instagram + TikTok',
    'login.proofSourcesLabel': 'zusammen in einem Feed',
    'login.proofPersonal': 'Deins',
    'login.proofPersonalLabel': 'nur deine Follows',
    'login.kicker': 'Dein Style-Feed',
    'login.formTitle': 'Bei Outfit Detective anmelden',
    'login.formSubtitle': 'Gib deine E-Mail ein und wir schicken dir einen Einmal-Code zum Login.',
    'login.email': 'Deine E-Mail',
    'login.emailPlaceholder': 'name@email.com',
    'login.title': 'Bei Outfit Detective anmelden',
    'login.subtitle': 'Folge deinen Lieblingsprofilen und sammle die besten Looks in deinem persönlichen Feed.',
    'login.sendCode': 'Einmal-Code senden',
    'login.sending': 'Senden...',
    'login.otp': 'Code eingeben',
    'login.changeEmail': 'E-Mail wechseln',
    'login.verify': 'Weiter',
    'login.verifying': 'Pruefe...',
    'login.codeSent': 'Check deine Mail. Der Code bleibt {minutes} Minuten fresh. Falls du ihn nicht siehst, schau auch in Spam oder Promotions.',
    'login.pendingOtpRestored': 'Der Code, den wir dir geschickt haben, funktioniert noch.',
    'login.pendingOtpExpired': 'Dieser Code ist abgelaufen. Fordere einen neuen an, um weiterzumachen.',
    'login.useReceivedCode': 'Ich habe schon einen Code',
    'login.resendCode': 'Schick mir einen neuen Code',
    'feed.yourList': 'Deine Liste',
    'feed.findProfiles': 'Profile finden',
    'feed.searchList': 'Liste durchsuchen',
    'feed.noListSearchResults': 'Kein Profil passt zu dieser Suche.',
    'feed.noProfiles': 'Noch keine Profile.',
    'feed.heroTitle': 'Neue Looks von den Profilen, denen du folgst.',
    'feed.heroEmpty': 'Folge interessanten Profilen, um deinen Outfit-Feed aufzubauen.',
    'feed.heroCount': 'Du folgst {count} {label}.',
    'feed.emptyTitle': 'Der Feed ist leer',
    'feed.emptyCopy': 'Füge Profile über Discover hinzu, um deinen persönlichen Feed aufzubauen.',
    'feed.popularFallbackTitle': 'Beliebte Looks, waehrend dein Feed bereit wird',
    'feed.popularFallbackCopy': 'Folge Profilen, damit der Feed persoenlich wird. Bis dahin zeigen wir beliebte Looks.',
    'feed.popularFallbackCta': 'Finde deine Style-Crushes',
    'feed.personalPendingCopy': 'Die Inhalte deiner gefolgten Profile werden vorbereitet. Neue Looks erscheinen hier automatisch.',
    'feed.suggested': 'Vorgeschlagen',
    'feed.news': 'Updates',
    'feed.aiAction': 'Look nachstylen',
    'feed.openDetail': 'Look öffnen',
    'aiDialog.kicker': 'Outfit Detective AI',
    'aiDialog.title': 'Shoppe den Look ohne Scroll-Spirale.',
    'aiDialog.copy': 'Wir lesen den Post, erkennen die Pieces und sortieren Matches nach Vibe und Budget.',
    'aiDialog.tagsLabel': 'Match-Details',
    'aiDialog.tagThreeLanes': '3 Preis-Lanes',
    'aiDialog.tagSmartMatch': 'Vibe zuerst',
    'aiDialog.laneExactTitle': 'Fast genau das Piece',
    'aiDialog.laneExactCopy': 'Nur sichtbar, wenn Brand, Modell, Kategorie und visuelle Hinweise sehr sicher matchen.',
    'aiDialog.laneSimilarTitle': 'Gleiche Energie',
    'aiDialog.laneSimilarCopy': 'Pieces nah am Post-Style, Brand-Mood und der Silhouette.',
    'aiDialog.laneBudgetTitle': 'Smart save',
    'aiDialog.laneBudgetCopy': 'Der Look, ohne Wallet-Schock.',
    'aiDialog.laneLuxuryTitle': 'Luxury lane',
    'aiDialog.laneLuxuryCopy': 'Premium-Finds, wenn der Fit Main-Character-Budget verdient.',
    'aiDialog.loadingTitle': 'Fit wird erkannt',
    'aiDialog.loadingCopy': 'Du kannst das Fenster schliessen. Wir laufen im Hintergrund weiter; beim naechsten Oeffnen hier oder im Post sind die Finds da, sobald sie ready sind.',
    'aiDialog.loadingPageCopy': 'Wir laufen im Hintergrund weiter. Bleib hier oder komm spaeter zurueck: die Finds erscheinen hier, sobald sie ready sind.',
    'aiDialog.liveTitle': 'Suche laeuft noch',
    'aiDialog.liveCopy': 'Ein paar Finds sind schon da. Du kannst schliessen; wir aktualisieren im Hintergrund weiter.',
    'aiDialog.livePageCopy': 'Ein paar Finds sind schon da. Wir aktualisieren diese Seite im Hintergrund weiter.',
    'aiDialog.readyTitle': 'Matches bereit',
    'aiDialog.readyCopy': 'Öffne, was dich catcht. Das Ranking feilen wir nach den ersten Tests.',
    'aiDialog.errorTitle': 'Match verpasst',
    'aiDialog.errorCopy': 'Dieser Look hat gerade nicht gematcht. Versuch es gleich nochmal.',
    'aiDialog.retry': 'Nochmal versuchen',
    'aiDialog.emptyTitle': 'Noch kein cleaner Match',
    'aiDialog.emptyCopy': 'Das Bild war nicht klar genug für brauchbare Shopping-Ergebnisse.',
    'aiDialog.noLaneResults': 'Noch keine starken Finds in dieser Lane.',
    'aiDialog.lookMatch': 'Look Match',
    'aiDialog.vibeMatch': 'Vibe Match',
    'aiDialog.openLook': 'Fit source',
    'aiDialog.close': 'AI Dialog schließen',
    'discover.title': 'Suche, wem du folgen möchtest.',
    'discover.subtitle': 'Finde Instagram- oder TikTok-Profile und füge sie deiner persönlichen Liste hinzu.',
    'discover.added': '{name} wurde deiner Liste hinzugefügt.',
    'discover.pasteTitle': 'Profil einfügen',
    'discover.openBulk': 'Mehr Profile importieren',
    'discover.yourList': 'Deine Liste',
    'discover.noFollowingTitle': 'Noch keine Profile',
    'discover.inFeed': 'In deinem Feed',
    'discover.followersUnavailable': 'Follower nicht verfügbar',
    'discover.followError': 'Ich kann diesem Profil gerade nicht folgen. Versuch es bald erneut.',
    'discover.blockedKicker': 'Rechtehinweis',
    'discover.blockedTitle': 'Diesem Profil kann nicht gefolgt werden',
    'discover.blockedCopy': 'Aus Rechte-Gründen ist {name} nicht zum Folgen oder für deinen persönlichen Feed auf Outfit Detective verfügbar.',
    'discover.blockedReference': 'Referenz',
    'discover.blockedClose': 'Verstanden',
    'detail.back': 'Zurück zum Feed',
    'detail.aiTitle': 'Shopping-Matches für diesen Look',
    'detail.aiEmptyTitle': 'Noch keine Matches gespeichert',
    'detail.aiEmptyCopy': 'Starte das AI Match, um Pieces und Shopping-Lanes zu bekommen.',
    'photos.kicker': 'Try-on-Fotos',
    'photos.title': 'Meine Fotos',
    'photos.copy': 'Halte ein paar klare Fit-Fotos bereit, damit du Looks direkt an dir sehen kannst.',
    'photos.uploadTitle': 'Foto hinzufügen',
    'photos.uploadCopy': 'Am besten funktionieren helle Ganzkörperfotos.',
    'photos.usageNoteBefore': 'Diese Fotos nutzen wir nur für deine persönlichen',
    'photos.usageNoteAfter': 'Previews. Klare Ganzkörperfotos helfen beim besseren Kleidungstransfer; du kannst sie jederzeit entfernen.',
    'photos.pickFile': 'Foto auswählen',
    'photos.optimizedBeforeUpload': 'Bereit für deinen Try-on',
    'photos.titlePlaceholder': 'Fotoname',
    'photos.upload': 'Foto hochladen',
    'photos.uploading': 'Wird hochgeladen...',
    'photos.uploaded': 'Foto gespeichert.',
    'photos.processingConsent': 'Ich stimme zu, dass dieses Foto mit unseren AI-Systemen und OpenAI verarbeitet wird, um Try-ons zu erstellen. Das Foto bleibt privat.',
    'photos.emptyTitle': 'Noch keine Fotos',
    'photos.emptyCopy': 'Lade ein Ganzkörperfoto hoch, um bessere Try-on-Previews zu bekommen.',
    'photos.default': 'Standard',
    'photos.makeDefault': 'Als Standard nutzen',
    'photos.framing.full_body': 'Ganzkörper',
    'photos.framing.upper_body': 'Oberkörper',
    'photos.framing.portrait': 'Porträt',
    'photos.framing.unknown': 'Nicht sicher',
    'photos.error.image_too_large': 'Das optimierte Bild ist noch zu groß. Versuch ein anderes Foto.',
    'photos.error.photo_processing_consent_required': 'Bitte bestätige die Fotoverarbeitung, bevor du hochlädst.',
    'photos.error.photo_safety_rejected': 'Dieses Foto kann nicht fuer Try-on genutzt werden. Bitte lade ein sicheres Foto mit bekleideter erwachsener Person hoch.',
    'photos.error.photo_safety_check_failed': 'Der Foto-Sicherheitscheck ist gerade nicht moeglich. Versuch es gleich nochmal.',
    'account.shopAreaTitle': 'Deine Fit-Radar-Zone',
    'account.shopAreaCopy': 'Outfit Detective sucht online und in echten Stores. Waehle eine Basiszone und wir priorisieren Matches, die nah genug zum Anprobieren, Abholen oder schnellen Liefern sind.',
    'account.locationLabel': 'Basiszone',
    'account.locationPlaceholder': 'Stadt oder Zone, z. B. Berlin Mitte',
    'account.locationResults': 'Standort-Vorschlaege',
    'account.locationSearching': 'Orte suchen...',
    'account.locationNoResults': 'Noch kein sauberer Treffer. Versuche Stadt plus Land.',
    'account.locationSelected': 'Verifizierte Zone ausgewaehlt',
    'account.radiusLabel': 'Suchradius: {km} km',
    'account.locationPrivacy': 'Keine genaue Adresse noetig. Stadt oder Viertel reicht, und du kannst es jederzeit loeschen.',
    'account.styleGenderTitle': 'Wall-Stil',
    'account.styleGenderCopy': 'Waehle, welche Looks zuerst in deinem Wall und in Vorschlaegen erscheinen.',
    'account.styleGender.all': 'Alle',
    'account.styleGender.menswear': 'Maenner',
    'account.styleGender.womenswear': 'Frauen',
    'account.styleGenderConfirm': 'Ich bestaetige, dass ich alle Looks in meinem Wall sehen moechte.',
    'account.styleGenderSummaryTitle': 'Wall-Praeferenz',
    'account.styleGenderSummary.all': 'Wall und Vorschlaege koennen jeden Stil enthalten.',
    'account.styleGenderSummary.menswear': 'Wall und Vorschlaege priorisieren Maenner und Menswear-Looks.',
    'account.styleGenderSummary.womenswear': 'Wall und Vorschlaege priorisieren Frauen und Womenswear-Looks.',
    'account.identityTitle': 'So erscheinst du',
    'account.identityCopy': 'Fuer oeffentliche Dinge wie Fit Wall und geteilte Try-ons waehle echten Namen oder bleib low-key mit Nickname. Deine E-Mail bleibt backstage.',
    'account.useNickname': 'Nickname',
    'account.useRealName': 'Echter Name',
    'account.realNameLabel': 'Echter Name',
    'account.realNamePlaceholder': 'Nur sichtbar, wenn du echten Namen waehlst',
    'account.nicknameLabel': 'Nickname',
    'account.useSuggestion': '{nickname} nutzen',
    'account.save': 'Profil speichern',
    'account.saving': 'Speichern...',
    'account.saved': 'Profil gespeichert.',
    'account.areaSummaryTitle': 'Shopping-Prioritaet',
    'account.areaSummarySet': 'Prioritaet fuer Online-Matches und Stores in {km} km um {location}.',
    'account.areaSummaryEmpty': 'Noch keine Zone gesetzt. Ergebnisse bleiben breit und online-first.',
    'account.publicSummaryTitle': 'Oeffentlicher Name',
    'account.publicSummaryCopy': 'Oeffentliche Try-ons erscheinen als {name}.',
    'tryon.kicker': 'Try the vibe on me',
    'tryon.title': 'Sieh den Look auf deinem Foto',
    'tryon.ctaTitle': 'Diesen Fit an mir sehen',
    'tryon.ctaCopy': 'Wähle ein Foto und sieh den Look an dir. Sofortiger Fit-Check.',
    'tryon.open': 'Jetzt testen',
    'tryon.hide': 'Ausblenden',
    'tryon.existingKicker': 'Schon generiert',
    'tryon.existingTitle': 'Du hast bereits Try-ons fuer diesen Look',
    'tryon.existingCopy': 'Nutze eines dieser Ergebnisse oder erstelle eine neue Preview mit einem anderen Foto.',
    'tryon.originalPost': 'Original-Post',
    'tryonGallery.openEvent': 'Fit source',
    'tryonShare.button': 'Style dich wie {celebrity}',
    'tryonShare.title': 'Style dich wie {celebrity}',
    'tryonShare.text': 'Style dich wie {celebrity} mit Outfit Detective.',
    'tryonShare.chooseChannel': 'Teilen',
    'tryonShare.facebook': 'Auf Facebook teilen',
    'tryonShare.pinterest': 'Auf Pinterest teilen',
    'tryonShare.email': 'Per E-Mail teilen',
    'tryonShare.whatsapp': 'Auf WhatsApp teilen',
    'tryonShare.x': 'Auf X teilen',
    'tryonShare.copied': 'Text kopiert',
    'tryonShare.copyFailed': 'Teilen ist hier nicht verfuegbar. Bitte nutze das Browser-Menue.',
    'tryonShare.celebrityFallback': 'dieser Creator',
    'tryon.newPreviewTitle': 'Neue Preview erstellen',
    'tryon.newPreviewCopy': 'Waehle das Foto als Koerperreferenz.',
    'tryon.error.photo_framing_not_enough': 'Dieser Look braucht ein Ganzkörperfoto.',
    'login.legalPrefix': 'Mit der Anmeldung akzeptierst du unsere',
    'login.success': 'Du bist angemeldet.',
    'login.legalLink': 'Datenschutzerklärung',
    'login.legalAnd': 'und die',
    'login.termsLink': 'Nutzungsbedingungen',
    'app.saved': 'Gespeichert',
    'welcome.preferencesLabel': 'Style-Vorlieben',
    'welcome.pref.budget': 'Budget',
    'welcome.pref.street': 'Street',
    'welcome.pref.minimal': 'Minimal',
    'welcome.pref.luxury': 'Luxury',
    'welcome.pref.everyday': 'Everyday',
    'feed.save': 'Look speichern',
    'feed.unsave': 'Aus Gespeichert entfernen',
    'feed.share': 'Look teilen',
    'feed.tryOn': 'An mir testen',
    'feed.shareTitle': 'Look von {name}',
    'feed.shareText': 'Ich habe diesen Look gefunden: {title}',
    'feed.shareFallback': 'Outfit Detective Look',
    'feed.shareFailed': 'Der Share-Link konnte nicht kopiert werden.',
    'feed.badgeFresh': 'neu von deinen Follows',
    'feed.badgePopular': 'gerade beliebt',
    'feed.badgeMatched': '{count} Pieces gematcht',
    'feed.starterKicker': 'Starte hier',
    'feed.starterTitle': 'Mach den Feed in drei Taps zu deinem.',
    'feed.starterFollow': 'Profile folgen',
    'feed.starterMatch': 'Look shoppen',
    'feed.starterSave': 'Speichern oder teilen',
    'feed.starterCta': 'Profile finden',
    'aiDialog.best': 'Top-Pick',
    'aiDialog.bestPick': 'Top-Pick · {price}',
    'aiDialog.quick.budget': 'Budget ab {price}',
    'aiDialog.quick.similar': 'Ähnlich ab {price}',
    'aiDialog.quick.luxury': 'Luxury ab {price}',
    'lookShare.open': 'Story-Card erstellen',
    'lookShare.title': 'Story-Card',
    'lookShare.total': 'Look gefunden für ca. {total}',
    'lookShare.noTotal': 'Look mit Outfit Detective gefunden',
    'lookShare.download': 'Story-Card herunterladen',
    'lookShare.hint': 'Nutze sie als Reel/Story-Cover oder nach deiner Bildschirmaufnahme.',
    'similarRail.kicker': 'Visual Shopping',
    'similarRail.title': 'Ähnliche Produkte shoppen',
    'similarRail.emptyCopy': 'Starte Fit AI, um eine visuelle Reihe sauberer Produkt-Matches für diesen Look zu öffnen.',
    'similarRail.loading': 'Saubere visuelle Matches werden gesucht...',
    'similarRail.start': 'Ähnliche Produkte finden',
    'similarRail.showAll': 'Alle Matches anzeigen',
    'similarRail.showMore': 'Mehr anzeigen',
    'similarRail.lane.exact': 'Genau',
    'similarRail.lane.similar': 'Ähnlich',
    'similarRail.lane.budget': 'Budget',
    'similarRail.lane.luxury': 'Luxury',
    'productInfo.kicker': 'Produkt-Match',
    'productInfo.price': 'Preis',
    'productInfo.match': 'Gematchtes Teil',
    'productInfo.matchFallback': 'Dieser Look',
    'productInfo.copy': 'Bleib erst hier: prüfe das Teil und teste danach den ganzen Fit auf deinem Foto.',
    'productInfo.tryOn': 'Fit testen',
    'productInfo.openShop': 'Shop öffnen',
    'saved.kicker': 'Dein Outfit-Radar',
    'saved.title': 'Gespeicherte Looks',
    'saved.emptyTitle': 'Noch keine Looks gespeichert',
    'saved.emptyCopy': 'Speichere Looks, die du später wieder ansehen, teilen oder anprobieren willst.',
    'saved.emptyCta': 'Zurück zum Feed',
    'saved.open': 'Look öffnen',
    'saved.remove': 'Entfernen',
    'publicTryOn.ctaCreate': 'Erstelle deine Version',
    'publicTryOn.ctaFind': 'Ähnliche Looks finden',
    'app.feed': 'Feed',
    'app.discover': 'Entdecken',
    'app.fitWall': 'Fit Wall',
    'nav.feed': 'Feed',
    'nav.fitWall': 'Wall',
    'nav.photos': 'Fotos',
    'nav.discover': 'Suche',
    'nav.help': 'Hilfe',
    'app.legal': 'Rechtliches',
    'common.loading': 'Lädt...',
    'common.previous': 'Zurück',
    'common.next': 'Weiter',
    'feed.following': 'Folge ich',
    'feed.apiError': 'API nicht erreichbar: {error}',
    'feed.loading': 'Feed wird geladen...',
    'feed.noSuggestions': 'Derzeit keine Vorschläge.',
    'discover.removeError': 'Konnte gerade nicht aktualisieren.',
    'discover.loadMore': 'Mehr laden',
    'discover.loadingMore': 'Lade mehr...',
    'detail.loading': 'Look wird geladen...',
    'detail.priceUnavailable': 'Preis nicht verfügbar',
    'detail.refreshAi': 'Fit AI aktualisieren',
    'aiDialog.resultCount': '{count} Produkt-Matches',
    'aiDialog.error.ai_matching_disabled': 'AI Matching ist noch nicht aktiv.',
    'aiDialog.error.event_image_missing': 'Dieser Post hat kein Bild zur Analyse.',
    'aiDialog.error.no_items_detected': 'Der Fit war nicht klar genug, um brauchbare Pieces zu erkennen.',
    'aiDialog.error.auth_required': 'Dieses Match ist noch nicht im Cache. Melde dich an, um eine neue Suche zu starten.',
    'shareCard.generating': 'Wird erstellt...',
    'shareCard.downloadError': 'Die Karte konnte nicht erstellt werden.',
    'tryon.copy': 'Wähle ein Foto von dir und erstelle eine persönliche Preview inspiriert von diesem Fit.',
    'tryon.needMatchTitle': 'Starte zuerst Fit AI',
    'tryon.needMatchCopy': 'Wir brauchen die erkannten Pieces, bevor wir die Try-on-Preview erstellen.',
    'tryon.startFitAi': 'Fit AI starten',
    'tryon.viewFitAiStatus': 'Fit-AI-Status ansehen',
    'tryon.noPhotosTitle': 'Füge zuerst ein Foto hinzu',
    'tryon.noPhotosCopy': 'Lade ein Ganzkörperfoto hoch, damit die Preview genug Kontext hat.',
    'tryon.generateShop': 'Try-on erstellen',
    'tryon.generateReplica': 'Replica erstellen',
    'tryon.queueing': 'Wird gestartet...',
    'tryon.notifyEmail': 'Benachrichtige mich per E-Mail, wenn es fertig ist',
    'tryon.notifyEmailOnCopy': 'Aktiviere das, dann kannst du gehen, statt auf diesem Screen zu warten.',
    'tryon.notifyEmailOffCopy': 'Keine E-Mail-Erinnerung. Lass Meine Try-ons offen oder komm später zurück.',
    'tryon.modeLabel': 'Preview-Typ',
    'tryon.modeTryonTitle': 'Der echte Try-on',
    'tryon.modeTryonName': 'Baue den Warenkorb-Fit',
    'tryon.modeTryonCopy': 'Wähle Shopping-Pieces. Übersprungene Pieces bleiben aus dem Original-Look.',
    'tryon.cartUnavailableKicker': 'Warenkorb-Fit Option',
    'tryon.cartUnavailableTitle': 'Baue den Warenkorb-Fit nach dem Produkt-Match',
    'tryon.cartUnavailableCopy': 'Finde zuerst ähnliche Produkte für diesen Look. Danach kannst du Shop-Pieces wählen und sie mit dem Original-Fit mixen.',
    'tryon.cartUnavailableCta': 'Ähnliche Produkte finden',
    'tryon.cartUnavailableLoading': 'Produkte werden gesucht...',
    'tryon.modeReplicaTitle': 'Die cleane Replica',
    'tryon.modeReplicaName': 'Kopiere den Original-Fit',
    'tryon.modeReplicaCopy': 'Nutze den Post als kompletten Outfit-Referenz. Kein Warenkorb, nur der geklonte Vibe.',
    'tryon.shopBuilderTitle': 'Wähle die Pieces, die du wirklich willst',
    'tryon.shopBuilderCopy': 'Wähle ein Produkt pro Piece. Übersprungene Pieces bleiben aus dem Original-Look.',
    'tryon.selectedCount': '{count} gewählt',
    'tryon.totalUnavailable': 'Variabler Gesamtpreis',
    'tryon.shopSelected': 'Wir nutzen dein Shopping-Piece',
    'tryon.shopOriginalFallback': 'Nicht gewählt: bleibt das Original-Piece',
    'tryon.shopNoProducts': 'Noch kein brauchbares Produkt für dieses Piece.',
    'tryon.shopResponsibleCopy': 'Preise und Verfügbarkeit kommen von den Stores und können sich ändern. Das ist eine AI-Preview, keine Passform-Garantie.',
    'tryon.selectProductsFirstTitle': 'Wähle mindestens ein Piece',
    'tryon.selectProductsFirstCopy': 'Wähle oben ein shoppbares Produkt, dann kannst du dein Foto auswählen und den Try-on erstellen.',
    'tryon.selectedProductsTitle': 'Gewählte Pieces: {total}',
    'tryon.newPreviewShopCopy': 'Wähle dein Foto: Wir mischen die gewählten Produkte mit dem Original-Fit.',
    'tryon.newPreviewReplicaCopy': 'Wähle dein Foto und wir replizieren den Original-Look der Celebrity.',
    'tryon.pendingExistingCopy': 'Für diesen Look wird bereits eine Preview erstellt. Sobald sie fertig ist, findest du sie in Meine Try-ons.',
    'tryon.queuedTitle': 'Preview wird erstellt',
    'tryon.pendingEmailOn': 'Wir senden dir eine E-Mail, wenn das Bild fertig ist, und speichern es in Meine Try-ons.',
    'tryon.pendingEmailOff': 'Keine E-Mail. Du kannst schließen: Die Preview erscheint in Meine Try-ons.',
    'tryon.readyNoticeTitle': 'Dein Try-on ist fertig',
    'tryon.readyNoticeCopy': 'Die Preview ist fertig und in Meine Try-ons gespeichert.',
    'tryon.readyNoticeCta': 'Meine Try-ons ansehen',
    'tryon.readyNoticeStay': 'Hier bleiben',
    'tryon.openTryons': 'Meine Try-ons öffnen',
    'tryon.resultAlt': 'Try-on Preview',
    'tryon.error.ai_match_required': 'Starte Fit AI, bevor du den Look probierst.',
    'tryon.error.selected_products_required': 'Wähle mindestens ein Shopping-Piece oder wechsle zu Replica.',
    'tryon.error.try_on_input_safety_rejected': 'Dieser Try-on kann nicht erstellt werden, weil ein Referenzbild den Sicherheitscheck nicht bestanden hat.',
    'tryon.error.try_on_output_safety_rejected': 'Das generierte Bild hat den Sicherheitscheck nicht bestanden und wurde nicht gespeichert.',
    'tryon.error.try_on_safety_check_failed': 'Der Sicherheitscheck konnte gerade nicht abgeschlossen werden. Versuch es gleich nochmal.',
    'tryon.error.generic': 'Die Preview hat gerade nicht geklappt. Versuch es gleich nochmal.',
    'tryonGallery.originalPost': 'Original-Post',
    'tryonGallery.userPhoto': 'Dein Foto',
    'tryonGallery.status.running': 'In Arbeit',
    'tryonGallery.status.completed': 'Fertig',
    'tryonGallery.status.failed': 'Fehlgeschlagen',
    'tryonShare.shopTitle': 'Look wie {celebrity} für ca. {total}',
    'tryonShare.shopText': 'Ich habe mit Outfit Detective einen von {celebrity} inspirierten Fit für ca. {total} gebaut.',
    'tryonFeed.creatorFallback': 'Fit-Wall-Creator',
    'publicTryOn.kicker': 'Geteilter Try-on',
    'publicTryOn.title': 'Style dich wie {celebrity}',
    'publicTryOn.copy': '{creator} hat diesen AI Try-on mit Outfit Detective erstellt.',
    'publicTryOn.shopCopy': '{creator} hat diesen Warenkorb-Try-on mit ausgewählten Pieces für ca. {total} gebaut.',
    'publicTryOn.ctaKicker': 'Null Stress, mehr Stilgefühl',
    'publicTryOn.ctaTitle': 'Finde den Look, bevor der Algorithmus ihn begräbt.',
    'publicTryOn.ctaCopy': 'Folge Creators, speichere frische Fits, teste den Vibe auf deinen Fotos und finde aehnliche Pieces.',
    'publicTryOn.cta': 'Erstelle dein Fit Radar',
    'publicTryOn.missingTitle': 'Dieser Try-on ist nicht mehr erreichbar',
    'publicTryOn.missingCopy': 'Der Link ist vielleicht falsch oder die Preview ist nicht mehr verfügbar.',
    'common.refresh': 'Aktualisieren',
    'common.on': 'An',
    'common.off': 'Aus',
    'common.profile': 'Profil',
    'common.profiles': 'Profile',
    'welcome.kicker': 'Du bist drin',
    'welcome.title': 'Baue einen Feed, der den Fit wirklich versteht.',
    'welcome.copy': 'Folge oeffentlichen Creator-Looks, teste den Fit privat auf deinen Fotos und vergleiche aehnliche Alternativen in Luxury-, Mid- und Budget-Optionen.',
    'welcome.pointFeedTitle': 'Deine Profile',
    'welcome.pointFeedCopy': 'Wähle die Profile. Wir halten den Style-Feed fokussiert.',
    'welcome.pointDetectTitle': 'Fit Radar',
    'welcome.pointDetectCopy': 'Wir verwandeln oeffentliche Posts in brauchbare Outfit-Hinweise.',
    'welcome.pointBudgetTitle': 'Preis-Lanes',
    'welcome.pointBudgetCopy': 'Aehnlicher Vibe, von Luxury-Grails bis bezahlbaren Finds.',
    'welcome.cta': 'Profile finden',
    'welcome.secondary': 'Feed zeigen',
    'welcome.dismiss': 'Willkommen schließen',
    'welcome.legalPrefix': 'Mit dem Fortfahren akzeptierst du unsere',
    'welcome.legalAnd': 'und die',
    'followRefresh.kicker': 'Willkommen zurück',
    'followRefresh.title': 'Wir frischen deine Follows auf.',
    'followRefresh.copy': 'Während du weg warst, haben wir einige Hintergrundchecks pausiert. Manche gefolgten Profile sind deshalb vielleicht noch nicht ganz aktuell.',
    'followRefresh.cta': 'Meine Follows aktualisieren',
    'followRefresh.refreshing': 'Aktualisiere...',
    'followRefresh.secondary': 'Nicht jetzt',
    'followRefresh.dismiss': 'Hinweis schließen',
    'followRefresh.success': 'Wir aktualisieren jetzt {count} Profile.',
    'followRefresh.fresh': 'Deine gefolgten Profile sehen schon aktuell aus.',
    'followRefresh.error': 'Die Aktualisierung konnte gerade nicht gestartet werden.',
    'feed.heroKicker': 'Persönlicher Fashion-Feed',
    'feed.cachedPosts': 'gecachte Posts',
    'feed.images': 'Bilder',
    'feed.updated': 'Aktualisiert {date}.',
    'feed.workerReady': 'Worker bereit, neue Looks zu sammeln.',
    'feed.checkingServices': 'Services werden geprüft...',
    'tryonGallery.title': 'Meine Try-ons',
    'tryonGallery.emptyTitle': 'Noch keine Try-ons',
    'tryonGallery.emptyCopy': 'Erstelle deine erste Preview aus einem Look.',
    'tryonGallery.untitled': 'Unbenannter Try-on',
    'tryonGallery.created': 'Erstellt {date}',
    'tryonGallery.email': 'E-Mail',
    'tryonGallery.emailOn': 'E-Mail an',
    'tryonGallery.emailOff': 'E-Mail aus',
    'tryonGallery.openGeneratedImage': 'Generiertes Bild öffnen',
    'tryonGallery.openOriginalImage': 'Originalbild öffnen',
    'tryonGallery.delete': 'Löschen',
    'tryonGallery.deleting': 'Wird gelöscht...',
    'tryonGallery.deleteConfirm': 'Diesen Try-on löschen?',
    'tryonGallery.error.deleteFailed': 'Try-on konnte nicht gelöscht werden.',
    'tryonGallery.error.try_on_preview_not_found': 'Try-on nicht gefunden.',
    'tryonGallery.viewerLabel': 'Bildansicht',
    'tryonGallery.zoomIn': 'Vergrößern',
    'tryonGallery.zoomOut': 'Verkleinern',
    'shareCard.title': 'Share Card',
    'shareCard.download': 'Karte herunterladen',
    'shareCard.hint': 'Speichere sie und poste sie in Stories oder Reels.',
    'shareCard.openButton': 'Share Card erstellen',
    'tryonFeed.kicker': 'Öffentlicher Try-on-Feed',
    'tryonFeed.title': 'Fit Wall',
    'tryonFeed.copy': 'Echte Try-ons, die Nutzer öffentlich gemacht haben. Scrolle Glow-ups, öffne den Original-Post und hol dir Inspo.',
    'tryonFeed.emptyTitle': 'Die Fit Wall wartet auf den ersten Drop',
    'tryonFeed.emptyCopy': 'Mach einen deiner Try-ons öffentlich und starte den Feed.',
    'tryonFeed.emptyCta': 'Meine Try-ons öffnen',
    'tryonFeed.fitCheck': 'Fit Check',
    'tryonFeed.shareThis': 'Diesen Fit teilen',
    'tryonFeed.rightTitle': 'Wie ein Fit hier landet',
    'tryonFeed.rightCopy': 'Fit Wall Try-ons werden von Nutzern gepostet. Erstelle einen Look, schalte ihn öffentlich und nimm ihn jederzeit zurück. Low-key mit Nickname oder direkt mit Profilnamen.',
    'tryonFeed.rightCta': 'Meine Try-ons',
    'tryonFeed.rightBadge': 'AI Try-on · freiwillig öffentlich',
    'tryon.generate': 'Preview erstellen',
    'tryon.generating': 'Wird erstellt...',
    'tryon.typeSelectedProducts': 'Try-on',
    'tryon.typeReplica': 'Replica',
    'tryon.queuedEmailTitle': 'E-Mail-Erinnerung ist aktiv',
    'tryon.queuedCopy': 'Du kannst dieses Fenster schließen. Das Ergebnis erscheint in Meine Try-ons.',
    'tryon.queuedEmailCopy': 'Du kannst dieses Fenster schließen. Wir benachrichtigen dich per E-Mail und speichern es in Meine Try-ons.',
    'legal.kicker': 'Legal',
    'legal.title': 'Rechtliche Dokumente',
    'legal.copy': 'Informationen zu Datenschutz, Nutzungsbedingungen und Datenanfragen.',
    'legal.privacyTitle': 'Datenschutzerklärung',
    'legal.privacyCopy': 'Hinweise zur Verarbeitung personenbezogener Daten.',
    'legal.termsTitle': 'Nutzungsbedingungen',
    'legal.termsCopy': 'Bedingungen für die Nutzung des Dienstes.',
    'legal.cookiesTitle': 'Cookies und lokaler Speicher',
    'legal.cookiesCopy': 'Technischer Browser-Speicher für Login und Spracheinstellungen.',
    'legal.contactTitle': 'Kontakt und Datenanfragen',
    'legal.contactCopy': 'So erreichst du uns für Datenschutz, DSGVO-Rechte und rechtliche Unterstützung.',
    'legal.available': 'Verfügbar',
    'legal.backToApp': 'Zurück zu Outfit Detective',
    'alerts.tab': 'Hinweise',
    'alerts.title': 'Hinweise',
    'alerts.copy': 'Wichtige Updates zu Profilen, denen du folgst, und zu deinem Account.',
    'alerts.notice': 'Hinweis',
    'alerts.emptyTitle': 'Noch keine Hinweise',
    'alerts.emptyCopy': 'Wenn sich etwas Wichtiges aendert, findest du es hier.',
    'alerts.markRead': 'Als gelesen markieren',
    'alerts.emailSent': 'E-Mail gesendet: {date}',
    'alerts.error': 'Hinweise koennen gerade nicht geladen werden.',
    'alerts.profileIncomplete.title': 'Profil vervollstaendigen',
    'alerts.profileIncomplete.bodyBoth': 'Fuege eine Shopping-Zone hinzu und bestaetige deine Wall-Praeferenz, damit Feed und Vorschlaege besser werden.',
    'alerts.profileIncomplete.bodyLocation': 'Fuege eine Shopping-Zone hinzu, damit Feed und Shopping-Vorschlaege besser werden.',
    'alerts.profileIncomplete.bodyStyleGender': 'Bestaetige deine Wall-Stilpraeferenz, damit Feed und Profilvorschlaege besser werden.',
    'alerts.profileIncomplete.cta': 'Profil vervollstaendigen',
    'alerts.followedProfile.fallbackName': 'Ein Profil',
    'alerts.followedProfile.banned.title': 'Gefolgtes Profil nicht verfuegbar',
    'alerts.followedProfile.banned.body': '{profile} wurde aus deinen Follows entfernt und kann aktuell aus Rechte- oder Content-Gruenden nicht gefolgt werden.',
    'alerts.followedProfile.banned.bodyReason': '{profile} wurde aus deinen Follows entfernt und kann aktuell aus Rechte- oder Content-Gruenden nicht gefolgt werden. Grund: {reason}',
    'alerts.followedProfile.deletedBanned.title': 'Gefolgtes Profil geloescht und blockiert',
    'alerts.followedProfile.deletedBanned.body': '{profile} wurde geloescht, aus deinen Follows entfernt und kann aktuell aus Rechte- oder Content-Gruenden nicht gefolgt werden.',
    'alerts.followedProfile.deletedBanned.bodyReason': '{profile} wurde geloescht, aus deinen Follows entfernt und kann aktuell aus Rechte- oder Content-Gruenden nicht gefolgt werden. Grund: {reason}',
    'alerts.followedProfile.removed.title': 'Gefolgtes Profil entfernt',
    'alerts.followedProfile.removed.body': '{profile} wurde aus Rechte- oder Content-Gruenden geloescht und aus deinen Follows entfernt.',
    'alerts.followedProfile.removed.bodyReason': '{profile} wurde aus Rechte- oder Content-Gruenden geloescht und aus deinen Follows entfernt. Grund: {reason}',
  },
  fr: {
    'app.logout': 'Déconnexion',
    'app.language': 'Langue',
    'beta.badge': 'Beta',
    'beta.copy': 'Encore en beta. Les matches sont des suggestions : utiles pour retrouver un style proche, pas des copies exactes.',
    'copyright.source': 'Source : {source}',
    'copyright.sourceFallback': 'post social',
    'copyright.preview': 'Nous utilisons l apercu pour trouver des alternatives similaires. Les droits restent aux proprietaires.',
    'copyright.takedown': 'Demander le retrait ?',
    'betaInvite.title': 'Inviter un ami',
    'betaInvite.copy': 'Vous connaissez quelqu’un qui sauvegarde des outfits, suit des creators ou cherche des produits similaires ? Envoyez-lui Outfit Detective.',
    'betaInvite.whatsapp': 'WhatsApp',
    'betaInvite.email': 'Email',
    'betaInvite.copyLink': 'Copier le lien',
    'betaInvite.native': 'Partager',
    'betaInvite.shareTitle': 'Tester Outfit Detective Beta',
    'betaInvite.shareText': 'Je teste Outfit Detective Beta : un wall personnel de looks sociaux, produits similaires et try-on virtuel sur vos photos. C’est encore en beta, mais l’idée est forte.',
    'share.copy': 'Copier le lien',
    'share.copyDone': 'Copié',
    'share.copyToast': 'Lien copié',
    'share.channel.whatsapp': 'WhatsApp',
    'share.channel.email': 'Email',
    'share.channel.native': 'Partager',
    'share.channel.pinterest': 'Pinterest',
    'shareLook.kicker': 'Invitation beta',
    'shareLook.cardTitle': 'Partager ce look',
    'shareLook.cardCopy': 'Envoyez ce look à un ami ou invitez-le à essayer Outfit Detective Beta.',
    'shareLook.button': 'Partager le look',
    'shareLook.title': 'Look de {celebrity} sur Outfit Detective',
    'shareLook.text': 'J’ai trouvé ce look de {celebrity} sur Outfit Detective Beta : looks sociaux, produits similaires et try-on virtuel.',
    'authPrompt.kicker': 'Mini login',
    'authPrompt.title': 'Gardez ce fit pour vous',
    'authPrompt.copy': 'Entrez votre email et recevez un code a usage unique. Pas de mot de passe, pas d inscription lourde.',
    'authPrompt.signIn': 'Connexion',
    'authPrompt.sendCode': 'Envoyer le code',
    'authPrompt.verify': 'Continuer',
    'authPrompt.tryOnTitle': 'Voyez ce que ça donne sur vous',
    'authPrompt.tryOnCopy': 'Connectez-vous pour garder vos photos privées.',
    'authPrompt.matchTitle': 'On cherche le look',
    'authPrompt.matchCopy': 'Connectez-vous vite fait, on lance la recherche pour vous.',
    'authPrompt.photosTitle': 'Vos photos',
    'authPrompt.photosCopy': 'Gardez ici les photos sur lesquelles essayer des looks.',
    'authPrompt.savedTitle': 'Gardez les bons looks',
    'authPrompt.savedCopy': 'Connectez-vous pour ne pas les perdre.',
    'authPrompt.followTitle': 'Rendez-le plus à vous',
    'authPrompt.followCopy': 'Suivez les personnes qui inspirent vraiment votre style.',
    'publicIntro.tagline': 'Looks sociaux publics · Alternatives similaires · Fit-on-me prive',
    'publicIntro.imageAlt': 'Idee outfit',
    'publicIntro.demoLabel': 'Demo du look reel au fit-on-me',
    'publicIntro.sourceAlt': 'Inspiration outfit publique originale',
    'publicIntro.resultAlt': 'Preview fit-on-me',
    'publicIntro.mediaSource': 'fit inspo',
    'publicIntro.mediaResult': 'sur moi',
    'publicIntro.mediaCelebrities': 'Looks publics',
    'publicIntro.mediaSimilar': 'Vetements similaires',
    'publicIntro.mediaTryOn': 'Idees try-on',
    'publicIntro.kicker': 'Apercu public',
    'publicIntro.title': 'Mettez le look sur votre photo',
    'publicIntro.copy': 'Choisissez un look public, et Outfit Detective vous montre comment il pourrait rendre sur vous avec des vetements similaires.',
    'publicIntro.howCopy': 'Suivez des profils publics Instagram ou TikTok, ouvrez un look et testez-le sur votre photo.',
    'publicIntro.featuresLabel': 'Fonctions Outfit Detective',
    'publicIntro.pillCelebrities': 'Posts publics',
    'publicIntro.pillProducts': 'Produits similaires',
    'publicIntro.pillTryOn': 'Idees virtual try-on',
    'publicIntro.primaryCta': 'Essayer ce fit',
    'publicIntro.secondaryCta': 'Voir les looks publies par les autres',
    'how.metaTitle': 'Comment fonctionne Outfit Detective',
    'how.metaDescription': 'Essayez des looks publics d Instagram et TikTok, trouvez des alternatives similaires pour recréer le fit et gardez les previews fit-on-me privees.',
    'how.kicker': 'Comment ca marche',
    'how.title': 'Du look social public a un fit que vous pouvez essayer sur vous.',
    'how.copy': 'Outfit Detective lit les indices visuels dans des looks publics Instagram et TikTok, separe les pieces, trouve des alternatives similaires et vous aide a tester le vibe sur vos propres photos.',
    'how.primaryCta': 'Commencer avec email',
    'how.secondaryCta': 'Voir le Fit Wall',
    'how.demoLabel': 'Demo generee du flow Outfit Detective',
    'how.demoImageAlt': 'Demo generee Outfit Detective avec pieces detectees',
    'how.demoDetected': 'Pieces detectees',
    'how.demoPieces': 'Top, veste, pantalon, chaussures et accessoires',
    'how.demoMatch': 'Style similaire',
    'how.demoMatchTitle': 'Alternatives visuelles les plus proches',
    'how.demoMatchMeta': 'Couleur, forme et vibe alignes',
    'how.promiseLabel': 'Ce que fait Outfit Detective',
    'how.promise.1.value': '01',
    'how.promise.1.label': 'Lit le fit depuis un post public Instagram ou TikTok',
    'how.promise.2.value': '02',
    'how.promise.2.label': 'Trouve des alternatives similaires sans connaitre la marque',
    'how.promise.3.value': '03',
    'how.promise.3.label': 'Teste le look en prive sur vos photos',
    'how.stepsKicker': 'Le flow',
    'how.stepsTitle': 'Trois etapes pour recréer un look public avec de bonnes limites.',
    'how.steps.1.title': 'Apportez le look',
    'how.steps.1.copy': 'Outfit Detective part des looks publies par des creators sur Instagram et TikTok. L app se concentre sur l outfit et les signaux de styling, pas sur des donnees inutiles.',
    'how.steps.2.title': 'L AI separe le fit',
    'how.steps.2.copy': 'Nous detectons les pieces visibles, couleurs, silhouette et signaux de styling, puis les transformons en breakdown clair.',
    'how.steps.3.title': 'Passez a l action',
    'how.steps.3.copy': 'Explorez des produits similaires, sauvegardez le look, partagez-le ou creez une preview fit-on-me.',
    'how.showcaseBadge': 'Preview fit-on-me',
    'how.showcaseTitle': 'Ajoutez votre photo et voyez une preview privee du look creator sur vous.',
    'how.featureKicker': 'Ce que ca fait',
    'how.featureTitle': 'Un moteur de recherche style avec un peu de gout.',
    'how.feature.find.title': 'Trouver des vetements similaires',
    'how.feature.find.copy': 'Cherchez selon l apparence du look, meme sans connaitre la marque originale.',
    'how.feature.explain.title': 'Comprendre le look',
    'how.feature.explain.copy': 'Voyez les pieces et signaux de styling au lieu d une liste plate de produits aleatoires.',
    'how.feature.try.title': 'Fit on me',
    'how.feature.try.copy': 'Utilisez vos photos privees pour creer une preview inspiree par l outfit choisi.',
    'how.feature.save.title': 'Sauvegarder et comparer',
    'how.feature.save.copy': 'Gardez looks, idees produits et previews try-on ensemble pour decider plus tard.',
    'how.trustKicker': 'Limites claires',
    'how.trustTitle': 'Utile, sans fausse certitude.',
    'how.trustCopy': 'Outfit Detective suggere des styles visuellement similaires sans pretendre que chaque produit est exact ou approuve par un creator.',
    'how.trust.1': 'Les alternatives similaires sont des suggestions, pas des produits exacts garantis.',
    'how.trust.2': 'Les photos fit-on-me restent personnelles et servent a votre preview.',
    'how.trust.3': 'Quand possible, l app explique couleur, silhouette et categorie.',
    'how.finalKicker': 'Pret quand la curiosite arrive',
    'how.finalTitle': 'La prochaine fois qu un look arrete votre scroll, rendez-le utile.',
    'how.finalCopy': 'Transformez un look public de creator en breakdown, alternatives similaires et idee fit-on-me privee pendant que l inspiration est encore fraiche.',
    'how.finalCta': 'Commencer la beta',
    'how.finalSecondary': 'Explorer les looks publics',
    'app.photos': 'Mes photos',
    'app.profile': 'Profil',
    'app.menu': 'Menu',
    'common.follow': 'Suivre',
    'common.unfollow': 'Ne plus suivre',
    'common.remove': 'Retirer',
    'profile.followingStatus': 'Suivi',
    'profile.notFollowingStatus': 'Non suivi',
    'common.open': 'Ouvrir',
    'common.preview': 'Aperçu',
    'common.cancel': 'Annuler',
    'followConfirm.title': 'Ne plus suivre ce profil ?',
    'followConfirm.copy': '{name} quittera votre liste et votre feed personnel.',
    'followConfirm.confirming': 'Retrait...',
    'login.heroTitle': 'Essayez sur vous les looks vus sur Instagram et TikTok.',
    'login.heroSubtitle': 'Suivez des looks publics de creators, gardez les previews fit-on-me privees et trouvez des alternatives similaires pour recréer le fit.',
    'login.pillCelebs': 'Fresh drops',
    'login.pillDetect': 'Fit radar',
    'login.pillShop': 'Alternatives similaires',
    'login.proofFast': 'Nouveaux drops',
    'login.proofFastLabel': 'looks des qu ils sortent',
    'login.proofSources': 'Instagram + TikTok',
    'login.proofSourcesLabel': 'ensemble dans un feed',
    'login.proofPersonal': 'À vous',
    'login.proofPersonalLabel': 'seulement vos follows',
    'login.kicker': 'Votre style feed',
    'login.formTitle': 'Connexion a Outfit Detective',
    'login.formSubtitle': 'Entrez votre email et nous vous envoyons un code de connexion a usage unique.',
    'login.email': 'Votre email',
    'login.emailPlaceholder': 'nom@email.com',
    'login.title': 'Connexion à Outfit Detective',
    'login.subtitle': 'Suivez les profils que vous aimez et retrouvez les meilleurs looks dans votre feed personnel.',
    'login.sendCode': 'Envoyer le code',
    'login.sending': 'Envoi...',
    'login.otp': 'Entrez le code',
    'login.changeEmail': 'Changer d’email',
    'login.verify': 'Continuer',
    'login.verifying': 'Verification...',
    'login.codeSent': 'Regardez vos mails. Le code reste fresh pendant {minutes} minutes. Si vous ne le voyez pas, jetez aussi un oeil aux spams ou promotions.',
    'login.pendingOtpRestored': 'Le code envoyé fonctionne encore.',
    'login.pendingOtpExpired': 'Ce code a expire. Demandez-en un nouveau pour continuer.',
    'login.useReceivedCode': 'J ai deja un code',
    'login.resendCode': 'M’envoyer un autre code',
    'feed.yourList': 'Votre liste',
    'feed.findProfiles': 'Trouver des profils',
    'feed.searchList': 'Rechercher dans la liste',
    'feed.noListSearchResults': 'Aucun profil ne correspond.',
    'feed.noProfiles': 'Aucun profil pour le moment.',
    'feed.heroTitle': 'Nouveaux looks des profils que vous suivez.',
    'feed.heroEmpty': 'Suivez des profils pour construire votre feed outfit.',
    'feed.heroCount': 'Vous suivez {count} {label}.',
    'feed.emptyTitle': 'Le feed est vide',
    'feed.emptyCopy': 'Ajoutez des profils depuis Discover pour créer votre feed personnel.',
    'feed.openDetail': 'Ouvrir le look',
    'feed.popularFallbackTitle': 'Looks populaires pendant que votre feed se prepare',
    'feed.popularFallbackCopy': 'Suivez des profils pour personnaliser ce feed. En attendant, voici des looks populaires.',
    'feed.popularFallbackCta': 'Trouver vos style crushs',
    'feed.personalPendingCopy': 'Les contenus de vos profils suivis se preparent. Les nouveaux looks apparaitront ici automatiquement.',
    'feed.suggested': 'Suggestions',
    'feed.news': 'Nouveautés',
    'feed.aiAction': 'Recréer ce look',
    'aiDialog.kicker': 'Outfit Detective AI',
    'aiDialog.title': 'Shoppez le look avec des matches plus clairs.',
    'aiDialog.copy': 'On lit le post, on repere les pieces, puis on trie les matchs par vibe et budget.',
    'aiDialog.tagsLabel': 'Details du match',
    'aiDialog.tagThreeLanes': '3 gammes prix',
    'aiDialog.tagSmartMatch': 'vibe d abord',
    'aiDialog.laneExactTitle': 'Presque la vraie piece',
    'aiDialog.laneExactCopy': 'Affiche seulement quand marque, modele, categorie et indices visuels matchent avec forte confiance.',
    'aiDialog.laneSimilarTitle': 'Meme energie',
    'aiDialog.laneSimilarCopy': 'Pieces proches du style du post, du mood marque et de la silhouette.',
    'aiDialog.laneBudgetTitle': 'Smart save',
    'aiDialog.laneBudgetCopy': 'Le look, sans drama cote wallet.',
    'aiDialog.laneLuxuryTitle': 'Luxury lane',
    'aiDialog.laneLuxuryCopy': 'Finds premium quand le fit merite un budget main character.',
    'aiDialog.loadingTitle': 'Detection du fit',
    'aiDialog.loadingCopy': 'Vous pouvez fermer ce pop-up. On continue en background; rouvrez ici ou la page du post et les finds seront la des qu ils sont prets.',
    'aiDialog.loadingPageCopy': 'On continue en background. Restez ici ou revenez plus tard: les finds apparaitront ici des qu ils sont prets.',
    'aiDialog.liveTitle': 'Recherche encore active',
    'aiDialog.liveCopy': 'Quelques finds sont deja la. Vous pouvez fermer; on continue les mises a jour en background.',
    'aiDialog.livePageCopy': 'Quelques finds sont deja la. On continue a mettre cette page a jour en background.',
    'aiDialog.readyTitle': 'Matchs prets',
    'aiDialog.readyCopy': 'Ouvrez ce qui vous parle. On affinera le ranking apres les premiers tests.',
    'aiDialog.errorTitle': 'Match rate',
    'aiDialog.errorCopy': 'Ce look n a pas matche maintenant. Reessayez dans un instant.',
    'aiDialog.retry': 'Reessayer',
    'aiDialog.emptyTitle': 'Pas encore de match clean',
    'aiDialog.emptyCopy': 'L image n etait pas assez claire pour des resultats shopping utiles.',
    'aiDialog.noLaneResults': 'Pas encore de find solide dans cette gamme.',
    'aiDialog.lookMatch': 'look match',
    'aiDialog.vibeMatch': 'vibe match',
    'aiDialog.openLook': 'Fit source',
    'aiDialog.close': 'Fermer le dialog AI',
    'discover.title': 'Cherchez qui vous voulez suivre.',
    'discover.subtitle': 'Trouvez des profils Instagram ou TikTok et ajoutez-les à votre liste personnelle.',
    'discover.added': '{name} a été ajouté à votre liste.',
    'discover.pasteTitle': 'Coller un profil',
    'discover.openBulk': 'Importer plus de profils',
    'discover.yourList': 'Votre liste',
    'discover.noFollowingTitle': 'Aucun profil pour le moment',
    'discover.inFeed': 'Dans votre feed',
    'discover.followersUnavailable': 'Followers indisponibles',
    'discover.followError': 'Impossible de suivre ce profil maintenant. Réessayez bientôt.',
    'discover.blockedKicker': 'Avis droits',
    'discover.blockedTitle': 'Ce profil ne peut pas être suivi',
    'discover.blockedCopy': 'Pour des raisons de droits, {name} n est pas disponible pour le suivi ou pour votre feed personnel sur Outfit Detective.',
    'discover.blockedReference': 'Référence',
    'discover.blockedClose': 'Compris',
    'detail.back': 'Retour au feed',
    'detail.aiTitle': 'Matchs shopping pour ce look',
    'detail.aiEmptyTitle': 'Aucun match sauvegarde',
    'detail.aiEmptyCopy': 'Lancez le match AI pour detecter les pieces et obtenir les gammes shopping.',
    'photos.kicker': 'Photos try-on',
    'photos.title': 'Mes photos',
    'photos.copy': 'Gardez quelques photos nettes prêtes pour voir les looks sur vous.',
    'photos.uploadTitle': 'Ajouter une photo',
    'photos.uploadCopy': 'Les meilleurs résultats viennent de photos lumineuses en pied.',
    'photos.usageNoteBefore': 'Ces photos servent uniquement à créer vos',
    'photos.usageNoteAfter': 'personnels. Choisissez des photos nettes en pied pour mieux transférer les vêtements; vous pouvez les supprimer à tout moment.',
    'photos.pickFile': 'Choisir une photo',
    'photos.optimizedBeforeUpload': 'Prête pour votre try-on',
    'photos.titlePlaceholder': 'Nom de la photo',
    'photos.upload': 'Importer la photo',
    'photos.uploading': 'Importation...',
    'photos.uploaded': 'Photo enregistrée.',
    'photos.processingConsent': 'J’accepte le traitement de cette photo avec nos systèmes AI et OpenAI pour générer des try-ons. La photo reste privée.',
    'photos.emptyTitle': 'Aucune photo pour le moment',
    'photos.emptyCopy': 'Importez une photo en pied pour obtenir de meilleurs aperçus try-on.',
    'photos.default': 'Par défaut',
    'photos.makeDefault': 'Utiliser par défaut',
    'photos.framing.full_body': 'En pied',
    'photos.framing.upper_body': 'Haut du corps',
    'photos.framing.portrait': 'Portrait',
    'photos.framing.unknown': 'Pas sûr',
    'photos.error.image_too_large': 'L’image optimisée est encore trop lourde. Essayez une autre photo.',
    'photos.error.photo_processing_consent_required': 'Confirmez le consentement au traitement de la photo avant l’import.',
    'photos.error.photo_safety_rejected': 'Cette photo ne peut pas être utilisée pour le try-on. Importez une photo sûre avec un adulte habillé.',
    'photos.error.photo_safety_check_failed': 'Le contrôle de sécurité de la photo est indisponible pour le moment. Réessayez bientôt.',
    'account.kicker': 'Profil',
    'account.title': 'Votre profil Outfit Detective',
    'account.copy': 'Gérez vos photos et outils personnels.',
    'account.overview': 'Vue d’ensemble',
    'account.tryonTitle': 'Prêt pour le try-on',
    'account.tryonCopy': 'Vos photos alimentent les previews personnelles des looks que vous découvrez.',
    'account.privacyTitle': 'Vous gardez le contrôle',
    'account.privacyCopy': 'Nous n’avons pas besoin de vos données personnelles pour rendre l’app utile. Ces réglages sont optionnels, modifiables à tout moment, et servent uniquement à ajuster votre expérience.',
    'account.legalLink': 'Paramètres confidentialité et légaux',
    'account.shopAreaTitle': 'Votre zone fit radar',
    'account.shopAreaCopy': 'Outfit Detective cherche sur les plateformes en ligne et dans les boutiques physiques. Choisissez une zone de base et nous prioriserons les matchs assez proches pour essayer, retirer ou recevoir vite.',
    'account.locationLabel': 'Zone de base',
    'account.locationPlaceholder': 'Ville ou quartier, ex. Paris, Marais',
    'account.locationResults': 'Suggestions de lieu',
    'account.locationSearching': 'Recherche de lieux...',
    'account.locationNoResults': 'Aucun match propre. Essayez ville plus pays.',
    'account.locationSelected': 'Zone verifiee selectionnee',
    'account.radiusLabel': 'Rayon de recherche : {km} km',
    'account.radiusAria': 'Rayon de recherche',
    'account.locationPrivacy': 'Pas besoin d adresse exacte. Une ville ou un quartier suffit, et vous pouvez l effacer quand vous voulez.',
    'account.styleGenderTitle': 'Style du wall',
    'account.styleGenderCopy': 'Choisissez les looks a voir en priorite dans votre wall et les profils suggeres.',
    'account.styleGender.all': 'Tous',
    'account.styleGender.menswear': 'Hommes',
    'account.styleGender.womenswear': 'Femmes',
    'account.styleGenderConfirm': 'Je confirme vouloir voir tous les looks dans mon wall.',
    'account.styleGenderSummaryTitle': 'Preference du wall',
    'account.styleGenderSummary.all': 'Votre wall et les suggestions peuvent inclure tous les styles.',
    'account.styleGenderSummary.menswear': 'Votre wall et les suggestions priorisent les hommes et les looks menswear.',
    'account.styleGenderSummary.womenswear': 'Votre wall et les suggestions priorisent les femmes et les looks womenswear.',
    'account.identityTitle': 'Comment vous apparaissez',
    'account.identityCopy': 'Pour les espaces publics comme Fit Wall et les try-ons partages, choisissez un vrai nom ou restez low-key avec un pseudo. Votre email reste en coulisses.',
    'account.useNickname': 'Pseudo',
    'account.useRealName': 'Vrai nom',
    'account.realNameLabel': 'Vrai nom',
    'account.realNamePlaceholder': 'Visible seulement si vous choisissez vrai nom',
    'account.nicknameLabel': 'Pseudo',
    'account.nicknamePlaceholder': 'Votre nom public de style',
    'account.useSuggestion': 'Utiliser {nickname}',
    'account.save': 'Enregistrer',
    'account.saving': 'Enregistrement...',
    'account.saved': 'Profil enregistre.',
    'account.areaSummaryTitle': 'Priorite shopping',
    'account.areaSummarySet': 'Priorite aux matchs en ligne et aux boutiques dans {km} km autour de {location}.',
    'account.areaSummaryEmpty': 'Aucune zone definie. Les resultats restent larges et online-first.',
    'account.publicSummaryTitle': 'Nom public',
    'account.publicSummaryCopy': 'Les try-ons publics apparaitront comme {name}.',
    'account.deleteTitle': 'Supprimer le compte',
    'account.deleteCopy': 'Cela suspend votre compte maintenant. Vous avez 15 jours pour le réactiver en vous reconnectant; après cela, nous supprimons définitivement votre profil, vos photos, try-ons, follows, préférences et sessions.',
    'account.deleteConfirmLabel': 'Tapez DELETE pour confirmer',
    'account.deleteConfirmMismatch': 'Tapez DELETE exactement pour confirmer la suppression du compte.',
    'account.deleteButton': 'Supprimer mon compte',
    'account.deleting': 'Suppression planifiée...',
    'account.deleteError': 'Impossible de planifier la suppression du compte maintenant.',
    'account.error.nickname_required': 'Choisissez un pseudo pour continuer.',
    'account.error.display_name_required': 'Ajoutez un vrai nom ou passez au pseudo.',
    'account.error.request_failed': 'Impossible d’enregistrer maintenant. Réessayez.',
    'account.error.api_unreachable': 'API indisponible pour le moment.',
    'account.error.profile_not_found': 'Profil introuvable.',
    'tryon.kicker': 'Try the vibe on me',
    'tryon.title': 'Voir le look sur votre photo',
    'tryon.ctaTitle': 'Voir ce fit sur moi',
    'tryon.ctaCopy': 'Choisissez une photo et voyez le look sur vous. Fit check direct.',
    'tryon.open': 'Tester',
    'tryon.hide': 'Masquer',
    'tryon.existingKicker': 'Deja genere',
    'tryon.existingTitle': 'Vous avez deja des try-ons pour ce look',
    'tryon.existingCopy': 'Vous pouvez reutiliser un resultat ou creer une nouvelle preview avec une autre photo.',
    'tryon.originalPost': 'Post original',
    'tryonGallery.openEvent': 'Fit source',
    'tryonShare.button': 'S habiller comme {celebrity}',
    'tryonShare.title': 'S habiller comme {celebrity}',
    'tryonShare.text': 'S habiller comme {celebrity} avec Outfit Detective.',
    'tryonShare.chooseChannel': 'Partager',
    'tryonShare.facebook': 'Partager sur Facebook',
    'tryonShare.pinterest': 'Partager sur Pinterest',
    'tryonShare.email': 'Partager par email',
    'tryonShare.whatsapp': 'Partager sur WhatsApp',
    'tryonShare.x': 'Partager sur X',
    'tryonShare.copied': 'Texte copie',
    'tryonShare.copyFailed': 'Le partage n est pas disponible ici. Essayez depuis le menu du navigateur.',
    'tryonShare.celebrityFallback': 'ce creator',
    'tryon.newPreviewTitle': 'Creer une nouvelle preview',
    'tryon.newPreviewCopy': 'Choisissez la photo a utiliser comme reference de corps.',
    'tryon.error.photo_framing_not_enough': 'Ce look demande une photo en pied.',
    'login.legalPrefix': 'En vous connectant, vous acceptez notre',
    'login.success': 'Connexion réussie.',
    'login.legalLink': 'Politique de confidentialité',
    'login.legalAnd': 'et les',
    'login.termsLink': 'Conditions d’utilisation',
    'app.saved': 'Sauvegardés',
    'welcome.preferencesLabel': 'Préférences de style',
    'welcome.pref.budget': 'Budget',
    'welcome.pref.street': 'Street',
    'welcome.pref.minimal': 'Minimal',
    'welcome.pref.luxury': 'Luxury',
    'welcome.pref.everyday': 'Everyday',
    'feed.save': 'Sauvegarder le look',
    'feed.unsave': 'Retirer des sauvegardés',
    'feed.share': 'Partager le look',
    'feed.tryOn': 'Essayer sur moi',
    'feed.shareTitle': 'Look de {name}',
    'feed.shareText': 'J ai trouvé ce look : {title}',
    'feed.shareFallback': 'Look Outfit Detective',
    'feed.shareFailed': 'Impossible de copier le lien de partage.',
    'feed.badgeFresh': 'nouveau de vos follows',
    'feed.badgePopular': 'populaire maintenant',
    'feed.badgeMatched': '{count} pièces matchées',
    'feed.starterKicker': 'Commencez ici',
    'feed.starterTitle': 'Rendez le feed personnel en trois taps.',
    'feed.starterFollow': 'Suivre des profils',
    'feed.starterMatch': 'Shopper le look',
    'feed.starterSave': 'Sauvegarder ou partager',
    'feed.starterCta': 'Trouver des profils',
    'aiDialog.best': 'Meilleur choix',
    'aiDialog.bestPick': 'Meilleur choix · {price}',
    'aiDialog.quick.budget': 'Budget dès {price}',
    'aiDialog.quick.similar': 'Similaire dès {price}',
    'aiDialog.quick.luxury': 'Luxury dès {price}',
    'lookShare.open': 'Créer une story card',
    'lookShare.title': 'Story card',
    'lookShare.total': 'Look trouvé pour environ {total}',
    'lookShare.noTotal': 'Look trouvé avec Outfit Detective',
    'lookShare.download': 'Télécharger la story card',
    'lookShare.hint': 'Utilisez-la comme cover Reel/Story ou après une capture vidéo.',
    'similarRail.kicker': 'Shopping visuel',
    'similarRail.title': 'Acheter des produits similaires',
    'similarRail.emptyCopy': 'Lancez Fit AI pour ouvrir une rangée visuelle de matchs produit propres pour ce look.',
    'similarRail.loading': 'Recherche de matchs visuels propres...',
    'similarRail.start': 'Trouver des produits similaires',
    'similarRail.showAll': 'Voir tous les matchs',
    'similarRail.showMore': 'Voir plus',
    'similarRail.lane.exact': 'Exact',
    'similarRail.lane.similar': 'Similaire',
    'similarRail.lane.budget': 'Budget',
    'similarRail.lane.luxury': 'Luxury',
    'productInfo.kicker': 'Match produit',
    'productInfo.price': 'Prix',
    'productInfo.match': 'Piece matchee',
    'productInfo.matchFallback': 'Ce look',
    'productInfo.copy': 'Restez ici: regardez la piece, puis testez tout le fit sur votre photo.',
    'productInfo.tryOn': 'Tester le fit',
    'productInfo.openShop': 'Ouvrir le shop',
    'saved.kicker': 'Votre radar outfit',
    'saved.title': 'Looks sauvegardés',
    'saved.emptyTitle': 'Aucun look sauvegardé',
    'saved.emptyCopy': 'Sauvegardez les looks à revoir, partager ou essayer plus tard.',
    'saved.emptyCta': 'Retour au feed',
    'saved.open': 'Ouvrir le look',
    'saved.remove': 'Retirer',
    'publicTryOn.ctaCreate': 'Créer votre version',
    'publicTryOn.ctaFind': 'Trouver des looks similaires',
    'app.feed': 'Feed',
    'app.discover': 'Découvrir',
    'app.fitWall': 'Fit Wall',
    'nav.feed': 'Feed',
    'nav.fitWall': 'Wall',
    'nav.photos': 'Photos',
    'nav.discover': 'Explorer',
    'nav.help': 'Aide',
    'app.legal': 'Légal',
    'common.loading': 'Chargement...',
    'common.previous': 'Précédent',
    'common.next': 'Suivant',
    'feed.following': 'Suivis',
    'feed.apiError': 'API indisponible : {error}',
    'feed.loading': 'Chargement du feed...',
    'feed.noSuggestions': 'Aucune suggestion pour le moment.',
    'discover.removeError': 'Impossible de mettre à jour maintenant.',
    'discover.loadMore': 'Voir plus',
    'discover.loadingMore': 'Chargement...',
    'detail.loading': 'Chargement du look...',
    'detail.priceUnavailable': 'Prix indisponible',
    'detail.refreshAi': 'Actualiser Fit AI',
    'aiDialog.resultCount': '{count} matchs produit',
    'aiDialog.error.ai_matching_disabled': 'Le matching AI n est pas encore actif.',
    'aiDialog.error.event_image_missing': 'Ce post n a pas d image à analyser.',
    'aiDialog.error.no_items_detected': 'Le fit n était pas assez clair pour détecter des pièces utiles.',
    'aiDialog.error.auth_required': 'Ce match n est pas encore en cache. Connectez-vous pour lancer une nouvelle recherche.',
    'shareCard.generating': 'Génération...',
    'shareCard.downloadError': 'Impossible de créer la carte.',
    'tryon.copy': 'Choisissez une photo de vous et créez une preview personnelle inspirée de ce fit.',
    'tryon.needMatchTitle': 'Lancez Fit AI d abord',
    'tryon.needMatchCopy': 'Il nous faut les pièces détectées avant de générer la preview try-on.',
    'tryon.startFitAi': 'Lancer Fit AI',
    'tryon.viewFitAiStatus': 'Voir le statut Fit AI',
    'tryon.noPhotosTitle': 'Ajoutez d abord une photo',
    'tryon.noPhotosCopy': 'Chargez une photo en pied pour donner assez de contexte à la preview.',
    'tryon.generateShop': 'Générer le try-on',
    'tryon.generateReplica': 'Générer la replica',
    'tryon.queueing': 'Démarrage...',
    'tryon.notifyEmail': 'Me prévenir par email quand c est prêt',
    'tryon.notifyEmailOnCopy': 'Activez-le pour quitter cet écran au lieu d attendre ici.',
    'tryon.notifyEmailOffCopy': 'Aucun rappel email. Gardez Mes try-ons ouverts ou revenez vérifier plus tard.',
    'tryon.modeLabel': 'Type de preview',
    'tryon.modeTryonTitle': 'Le vrai try-on',
    'tryon.modeTryonName': 'Construire le fit panier',
    'tryon.modeTryonCopy': 'Choisissez les pièces shopping. Les pièces non choisies restent celles du look original.',
    'tryon.cartUnavailableKicker': 'Option fit panier',
    'tryon.cartUnavailableTitle': 'Construisez le fit panier après le match produits',
    'tryon.cartUnavailableCopy': 'Trouvez d abord des produits similaires pour ce look. Ensuite, choisissez les pièces shop et mixez-les avec le fit original.',
    'tryon.cartUnavailableCta': 'Trouver des produits similaires',
    'tryon.cartUnavailableLoading': 'Recherche de produits...',
    'tryon.modeReplicaTitle': 'La replica propre',
    'tryon.modeReplicaName': 'Copier le fit original',
    'tryon.modeReplicaCopy': 'Utilisez le post comme référence complète. Pas de panier, juste le vibe cloné.',
    'tryon.shopBuilderTitle': 'Choisissez les pièces que vous voulez vraiment',
    'tryon.shopBuilderCopy': 'Sélectionnez un produit par pièce. Les pièces sautées restent celles du look original.',
    'tryon.selectedCount': '{count} choisis',
    'tryon.totalUnavailable': 'Total variable',
    'tryon.shopSelected': 'Nous utilisons votre pièce shopping',
    'tryon.shopOriginalFallback': 'Non choisi : garde la pièce originale',
    'tryon.shopNoProducts': 'Aucun produit utile pour cette pièce pour le moment.',
    'tryon.shopResponsibleCopy': 'Prix et disponibilité viennent des boutiques et peuvent changer. Ceci est une preview AI, pas une garantie de coupe.',
    'tryon.selectProductsFirstTitle': 'Choisissez au moins une pièce',
    'tryon.selectProductsFirstCopy': 'Sélectionnez un produit shopping ci-dessus, puis vous pourrez choisir votre photo et générer le try-on.',
    'tryon.selectedProductsTitle': 'Pièces choisies : {total}',
    'tryon.newPreviewShopCopy': 'Choisissez votre photo : nous mixons les produits choisis avec le fit original.',
    'tryon.newPreviewReplicaCopy': 'Choisissez votre photo et nous répliquons le look original de la celebrity.',
    'tryon.pendingExistingCopy': 'Une preview est déjà en génération pour ce look. Dès qu elle est prête, vous la trouverez dans Mes try-ons.',
    'tryon.queuedTitle': 'Preview en génération',
    'tryon.queuedEmailTitle': 'Rappel email activé',
    'tryon.pendingEmailOn': 'Nous vous enverrons un email quand l image sera prête et la sauvegarderons dans Mes try-ons.',
    'tryon.pendingEmailOff': 'Pas d email. Vous pouvez fermer : la preview apparaîtra dans Mes try-ons.',
    'tryon.readyNoticeTitle': 'Votre try-on est prêt',
    'tryon.readyNoticeCopy': 'La preview est terminée et sauvegardée dans Mes try-ons.',
    'tryon.readyNoticeCta': 'Voir mes try-ons',
    'tryon.readyNoticeStay': 'Rester ici',
    'tryon.openTryons': 'Ouvrir mes try-ons',
    'tryon.resultAlt': 'Preview try-on',
    'tryon.error.ai_match_required': 'Lancez Fit AI avant d essayer le look.',
    'tryon.error.selected_products_required': 'Choisissez au moins une pièce shopping ou passez en Replica.',
    'tryon.error.try_on_input_safety_rejected': 'Ce try-on ne peut pas être généré car une image de référence n’a pas passé le contrôle de sécurité.',
    'tryon.error.try_on_output_safety_rejected': 'L’image générée n’a pas passé le contrôle de sécurité, donc elle n’a pas été enregistrée.',
    'tryon.error.try_on_safety_check_failed': 'Le contrôle de sécurité n’a pas pu être terminé. Réessayez bientôt.',
    'tryon.error.generic': 'La preview n a pas fonctionné maintenant. Réessayez dans un instant.',
    'tryonGallery.originalPost': 'Post original',
    'tryonGallery.userPhoto': 'Votre photo',
    'tryonGallery.status.running': 'En cours',
    'tryonGallery.status.completed': 'Prêt',
    'tryonGallery.status.failed': 'Échec',
    'tryonShare.shopTitle': 'Look comme {celebrity} pour environ {total}',
    'tryonShare.shopText': 'J ai construit un fit inspiré par {celebrity} pour environ {total} avec Outfit Detective.',
    'tryonFeed.creatorFallback': 'Creator Fit Wall',
    'publicTryOn.kicker': 'Try-on partagé',
    'publicTryOn.title': 'S habiller comme {celebrity}',
    'publicTryOn.copy': '{creator} a créé ce try-on AI avec Outfit Detective.',
    'publicTryOn.shopCopy': '{creator} a construit ce try-on prêt-panier avec des pièces sélectionnées pour environ {total}.',
    'publicTryOn.ctaKicker': 'Zéro friction, plus de signal style',
    'publicTryOn.ctaTitle': 'Trouvez le look avant que l algorithme l enterre.',
    'publicTryOn.ctaCopy': 'Suivez des creators, sauvegardez les fits frais, testez le vibe sur vos photos et trouvez des pièces similaires.',
    'publicTryOn.cta': 'Créer mon fit radar',
    'publicTryOn.missingTitle': 'Ce try-on n est plus disponible',
    'publicTryOn.missingCopy': 'Le lien est peut-être incorrect ou la preview n est plus disponible.',
    'common.refresh': 'Actualiser',
    'common.on': 'Activé',
    'common.off': 'Désactivé',
    'common.profile': 'profil',
    'common.profiles': 'profils',
    'welcome.kicker': 'Vous êtes dedans',
    'welcome.title': 'Créez un feed qui comprend vraiment le fit.',
    'welcome.copy': 'Suivez des looks publics de creators, essayez le fit en prive sur vos photos puis comparez des alternatives similaires en luxury, mid et budget.',
    'welcome.pointFeedTitle': 'Vos profils',
    'welcome.pointFeedCopy': 'Choisissez les profils. Nous gardons le feed style focalisé.',
    'welcome.pointDetectTitle': 'Fit radar',
    'welcome.pointDetectCopy': 'Nous transformons les posts publics en indices outfit utiles.',
    'welcome.pointBudgetTitle': 'Gammes prix',
    'welcome.pointBudgetCopy': 'Vibe similaire, des grails luxury aux finds accessibles.',
    'welcome.cta': 'Trouver des profils',
    'welcome.secondary': 'Voir le feed',
    'welcome.dismiss': 'Fermer le bienvenue',
    'welcome.legalPrefix': 'En continuant vous acceptez notre',
    'welcome.legalAnd': 'et les',
    'followRefresh.kicker': 'Bon retour',
    'followRefresh.title': 'On rafraîchit vos profils suivis.',
    'followRefresh.copy': 'Pendant votre absence, nous avons mis quelques vérifications en pause. Certains profils suivis peuvent donc ne pas être parfaitement à jour.',
    'followRefresh.cta': 'Actualiser mes suivis',
    'followRefresh.refreshing': 'Actualisation...',
    'followRefresh.secondary': 'Pas maintenant',
    'followRefresh.dismiss': 'Fermer l avis',
    'followRefresh.success': 'Nous actualisons {count} profils maintenant.',
    'followRefresh.fresh': 'Vos profils suivis semblent déjà à jour.',
    'followRefresh.error': 'Impossible de lancer l actualisation maintenant.',
    'feed.heroKicker': 'Feed fashion personnel',
    'feed.cachedPosts': 'posts en cache',
    'feed.images': 'images',
    'feed.updated': 'Mis à jour {date}.',
    'feed.workerReady': 'Worker prêt à collecter de nouveaux looks.',
    'feed.checkingServices': 'Vérification des services...',
    'tryonGallery.title': 'Mes try-ons',
    'tryonGallery.emptyTitle': 'Aucun try-on pour le moment',
    'tryonGallery.emptyCopy': 'Créez votre première preview depuis un look.',
    'tryonGallery.untitled': 'Try-on sans titre',
    'tryonGallery.created': 'Créé',
    'tryonGallery.email': 'Email',
    'tryonGallery.emailOn': 'Email activé',
    'tryonGallery.emailOff': 'Email désactivé',
    'tryonGallery.openGeneratedImage': 'Ouvrir l image générée',
    'tryonGallery.openOriginalImage': 'Ouvrir l image originale',
    'tryonGallery.delete': 'Supprimer',
    'tryonGallery.deleting': 'Suppression...',
    'tryonGallery.deleteConfirm': 'Supprimer ce try-on ?',
    'tryonGallery.error.deleteFailed': 'Impossible de supprimer le try-on.',
    'tryonGallery.error.try_on_preview_not_found': 'Try-on introuvable.',
    'tryonGallery.viewerLabel': 'Visionneuse image',
    'tryonGallery.zoomIn': 'Zoom avant',
    'tryonGallery.zoomOut': 'Zoom arrière',
    'shareCard.title': 'Share card',
    'shareCard.download': 'Télécharger la carte',
    'shareCard.hint': 'Sauvegardez-la et postez-la en Stories ou Reels.',
    'shareCard.openButton': 'Créer une share card',
    'tryonFeed.kicker': 'Feed public try-on',
    'tryonFeed.title': 'Fit Wall',
    'tryonFeed.copy': 'Des try-ons réels rendus publics. Scrollez les glow-ups, ouvrez le post original et prenez de l inspo.',
    'tryonFeed.emptyTitle': 'Le Fit Wall attend le premier drop',
    'tryonFeed.emptyCopy': 'Rendez public un de vos try-ons pour lancer le feed.',
    'tryonFeed.emptyCta': 'Ouvrir mes try-ons',
    'tryonFeed.fitCheck': 'Fit check',
    'tryonFeed.shareThis': 'Partager ce fit',
    'tryonFeed.rightTitle': 'Comment un fit arrive ici',
    'tryonFeed.rightCopy': 'Les try-ons du Fit Wall sont publiés par les utilisateurs. Créez un look, passez-le en public et retirez-le quand vous voulez. Low-key avec pseudo ou direct avec votre nom de profil.',
    'tryonFeed.rightCta': 'Mes try-ons',
    'tryonFeed.rightBadge': 'AI try-on · public par choix',
    'tryon.generate': 'Générer preview',
    'tryon.generating': 'Génération...',
    'tryon.cartPresetLabel': 'Choix automatiques du panier',
    'tryon.cartPresetClosest': 'Le plus proche',
    'tryon.cartPresetLuxury': 'Luxury',
    'tryon.cartPresetBudget': 'Budget',
    'tryon.typeSelectedProducts': 'Try-on',
    'tryon.typeReplica': 'Replica',
    'tryon.queuedCopy': 'Vous pouvez fermer cette fenêtre. Le résultat apparaîtra dans Mes try-ons.',
    'tryon.queuedEmailCopy': 'Vous pouvez fermer cette fenêtre. Nous vous préviendrons par email et le sauvegarderons dans Mes try-ons.',
    'tryonPublic.offTitle': 'Garder privé',
    'tryonPublic.offCopy': 'Votre try-on reste dans votre espace. Activez-le pour le publier sur le Fit Wall.',
    'tryonPublic.onTitle': 'Live sur le Fit Wall',
    'tryonPublic.onCopy': 'Fit check public activé. Vous pouvez le retirer dès que le vibe change.',
    'tryonPublic.useName': 'Utiliser mon nom de profil',
    'tryonPublic.useNameCopy': 'Les gens verront votre nom de profil sur ce post Fit Wall.',
    'tryonPublic.aliasCopy': 'Gardez-moi low-key comme {alias}.',
    'tryonPublic.aliasPending': 'un nom fit aléatoire',
    'tryonPublic.error.try_on_not_ready': 'Ce try-on doit être prêt avant de rejoindre le Fit Wall.',
    'tryonPublic.error.generic': 'Impossible de mettre à jour le partage Fit Wall maintenant.',
    'legal.kicker': 'Legal',
    'legal.title': 'Documents légaux',
    'legal.copy': 'Informations sur la confidentialité, les conditions du service et les demandes de données.',
    'legal.privacyTitle': 'Politique de confidentialité',
    'legal.privacyCopy': 'Notice sur le traitement des données personnelles.',
    'legal.termsTitle': 'Conditions d’utilisation',
    'legal.termsCopy': 'Conditions d’utilisation du service.',
    'legal.cookiesTitle': 'Cookies et stockage local',
    'legal.cookiesCopy': 'Stockage technique du navigateur utilisé pour le login et la langue.',
    'legal.contactTitle': 'Contact et demandes de données',
    'legal.contactCopy': 'Comment nous contacter pour la confidentialité, les droits RGPD et le support légal.',
    'legal.available': 'Disponible',
    'legal.backToApp': 'Retour à Outfit Detective',
    'alerts.tab': 'Alertes',
    'alerts.title': 'Alertes',
    'alerts.copy': 'Mises a jour importantes sur les profils que vous suivez et votre compte.',
    'alerts.notice': 'Notification',
    'alerts.emptyTitle': 'Aucune alerte pour le moment',
    'alerts.emptyCopy': 'Quand quelque chose d important change, vous le trouverez ici.',
    'alerts.markRead': 'Marquer comme lu',
    'alerts.emailSent': 'Email envoye : {date}',
    'alerts.error': 'Impossible de charger les alertes pour le moment.',
    'alerts.profileIncomplete.title': 'Completez votre profil',
    'alerts.profileIncomplete.bodyBoth': 'Ajoutez une zone shopping et confirmez votre preference du wall afin d ameliorer le feed et les suggestions.',
    'alerts.profileIncomplete.bodyLocation': 'Ajoutez une zone shopping pour ameliorer le feed et les suggestions shopping.',
    'alerts.profileIncomplete.bodyStyleGender': 'Confirmez votre preference de style du wall afin d ameliorer le feed et les profils suggeres.',
    'alerts.profileIncomplete.cta': 'Completer le profil',
    'alerts.followedProfile.fallbackName': 'Un profil',
    'alerts.followedProfile.banned.title': 'Profil suivi indisponible',
    'alerts.followedProfile.banned.body': '{profile} a ete retire de vos suivis et ne peut pas etre suivi pour le moment pour des raisons de droits ou de gestion de contenu.',
    'alerts.followedProfile.banned.bodyReason': '{profile} a ete retire de vos suivis et ne peut pas etre suivi pour le moment pour des raisons de droits ou de gestion de contenu. Motif : {reason}',
    'alerts.followedProfile.deletedBanned.title': 'Profil suivi supprime et bloque',
    'alerts.followedProfile.deletedBanned.body': '{profile} a ete supprime, retire de vos suivis et ne peut pas etre suivi pour le moment pour des raisons de droits ou de gestion de contenu.',
    'alerts.followedProfile.deletedBanned.bodyReason': '{profile} a ete supprime, retire de vos suivis et ne peut pas etre suivi pour le moment pour des raisons de droits ou de gestion de contenu. Motif : {reason}',
    'alerts.followedProfile.removed.title': 'Profil suivi retire',
    'alerts.followedProfile.removed.body': '{profile} a ete supprime et retire de vos suivis pour des raisons de droits ou de gestion de contenu.',
    'alerts.followedProfile.removed.bodyReason': '{profile} a ete supprime et retire de vos suivis pour des raisons de droits ou de gestion de contenu. Motif : {reason}',
  },
  ro: {
    'app.logout': 'Ieșire',
    'app.language': 'Limbă',
    'beta.badge': 'Beta',
    'beta.copy': 'Suntem inca in beta. Matchurile sunt sugestii: utile pentru un look asemanator, nu copii exacte.',
    'copyright.source': 'Sursa: {source}',
    'copyright.sourceFallback': 'postare social',
    'copyright.preview': 'Folosim preview-ul pentru alternative similare. Drepturile raman la proprietari.',
    'copyright.takedown': 'Vrei eliminarea?',
    'betaInvite.title': 'Invită un prieten',
    'betaInvite.copy': 'Știi pe cineva care salvează outfituri, urmărește creatori sau caută produse similare? Trimite-i Outfit Detective.',
    'betaInvite.whatsapp': 'WhatsApp',
    'betaInvite.email': 'Email',
    'betaInvite.copyLink': 'Copiază link',
    'betaInvite.native': 'Distribuie',
    'betaInvite.shareTitle': 'Încearcă Outfit Detective Beta',
    'betaInvite.shareText': 'Testez Outfit Detective Beta: un wall personal cu lookuri sociale, produse similare și try-on virtual pe pozele tale. E încă beta, dar ideea e puternică.',
    'share.copy': 'Copiază link',
    'share.copyDone': 'Copiat',
    'share.copyToast': 'Link copiat',
    'share.channel.whatsapp': 'WhatsApp',
    'share.channel.email': 'Email',
    'share.channel.native': 'Distribuie',
    'share.channel.pinterest': 'Pinterest',
    'shareLook.kicker': 'Invitație beta',
    'shareLook.cardTitle': 'Distribuie acest look',
    'shareLook.cardCopy': 'Trimite lookul unui prieten sau invită-l să încerce Outfit Detective Beta.',
    'shareLook.button': 'Distribuie look',
    'shareLook.title': 'Look {celebrity} pe Outfit Detective',
    'shareLook.text': 'Am găsit acest look {celebrity} pe Outfit Detective Beta: lookuri sociale, produse similare și try-on virtual.',
    'authPrompt.kicker': 'Login mic',
    'authPrompt.title': 'Pastreaza fitul pentru tine',
    'authPrompt.copy': 'Pui emailul si primesti un cod unic. Fara parola, fara signup greu.',
    'authPrompt.signIn': 'Intra',
    'authPrompt.sendCode': 'Trimite cod',
    'authPrompt.verify': 'Continua',
    'authPrompt.tryOnTitle': 'Vezi cum îți stă',
    'authPrompt.tryOnCopy': 'Intră ca să-ți păstrăm pozele doar pentru tine.',
    'authPrompt.matchTitle': 'Hai să căutăm lookul',
    'authPrompt.matchCopy': 'Intră rapid și îl căutăm pentru tine.',
    'authPrompt.photosTitle': 'Pozele tale',
    'authPrompt.photosCopy': 'Aici ții pozele pe care vrei să probezi lookuri.',
    'authPrompt.savedTitle': 'Păstrează lookurile bune',
    'authPrompt.savedCopy': 'Intră ca să nu le pierzi.',
    'authPrompt.followTitle': 'Fă-l pe gustul tău',
    'authPrompt.followCopy': 'Intră ca să urmărești oamenii care te inspiră.',
    'publicIntro.tagline': 'Lookuri sociale publice · Alternative similare · Fit-on-me privat',
    'publicIntro.imageAlt': 'Idee de outfit',
    'publicIntro.demoLabel': 'Demo de la look real la fit-on-me',
    'publicIntro.sourceAlt': 'Outfit public original pentru inspiratie',
    'publicIntro.resultAlt': 'Preview fit-on-me',
    'publicIntro.mediaSource': 'look inspo',
    'publicIntro.mediaResult': 'pe mine',
    'publicIntro.mediaCelebrities': 'Lookuri publice',
    'publicIntro.mediaSimilar': 'Haine similare',
    'publicIntro.mediaTryOn': 'Idei try-on',
    'publicIntro.kicker': 'Preview public',
    'publicIntro.title': 'Pune lookul pe poza ta',
    'publicIntro.copy': 'Alegi un look public, iar Outfit Detective iti arata cum ar putea arata pe tine cu haine similare.',
    'publicIntro.howCopy': 'Urmaresti profiluri publice de Instagram sau TikTok, deschizi un look si il testezi pe poza ta.',
    'publicIntro.featuresLabel': 'Functii Outfit Detective',
    'publicIntro.pillCelebrities': 'Postări publice',
    'publicIntro.pillProducts': 'Produse similare',
    'publicIntro.pillTryOn': 'Idei virtual try-on',
    'publicIntro.primaryCta': 'Probeaza fitul',
    'publicIntro.secondaryCta': 'Vezi lookurile publicate de altii',
    'how.metaTitle': 'Cum functioneaza Outfit Detective',
    'how.metaDescription': 'Probează lookuri publice de pe Instagram și TikTok, găsește alternative similare ca să recreezi fitul și păstrează private previewurile fit-on-me.',
    'how.kicker': 'Cum functioneaza',
    'how.title': 'De la un look social public la un fit pe care îl poți proba pe tine.',
    'how.copy': 'Outfit Detective citește indiciile vizuale din lookuri publice de pe Instagram și TikTok, separă piesele, găsește alternative similare și te ajută să testezi vibeul pe pozele tale.',
    'how.primaryCta': 'Incepe cu email',
    'how.secondaryCta': 'Vezi Fit Wall',
    'how.demoLabel': 'Demo generat al fluxului Outfit Detective',
    'how.demoImageAlt': 'Demo generata Outfit Detective cu piese detectate',
    'how.demoDetected': 'Piese detectate',
    'how.demoPieces': 'Top, jacheta, pantaloni, pantofi si accesorii',
    'how.demoMatch': 'Stil similar',
    'how.demoMatchTitle': 'Cele mai apropiate alternative vizuale',
    'how.demoMatchMeta': 'Culoare, forma si vibe potrivite',
    'how.promiseLabel': 'Ce face Outfit Detective',
    'how.promise.1.value': '01',
    'how.promise.1.label': 'Citește fitul dintr-o postare publică Instagram sau TikTok',
    'how.promise.2.value': '02',
    'how.promise.2.label': 'Găsește alternative similare fără să știi brandul',
    'how.promise.3.value': '03',
    'how.promise.3.label': 'Testează lookul privat pe pozele tale',
    'how.stepsKicker': 'Fluxul',
    'how.stepsTitle': 'Trei pași ca să recreezi un look public în siguranță.',
    'how.steps.1.title': 'Adu lookul',
    'how.steps.1.copy': 'Outfit Detective porneste de la lookuri publicate de creatori pe Instagram si TikTok. Aplicatia se concentreaza pe outfit si semnale de styling, nu pe date inutile.',
    'how.steps.2.title': 'AI imparte fitul',
    'how.steps.2.copy': 'Detectam piesele vizibile, culorile, silueta si semnalele de styling, apoi le transformam intr-un breakdown clar.',
    'how.steps.3.title': 'Fa ceva cu el',
    'how.steps.3.copy': 'Vezi produse similare, salveaza lookul, distribuie-l sau creeaza o previzualizare fit-on-me.',
    'how.showcaseBadge': 'Preview fit-on-me',
    'how.showcaseTitle': 'Incarci poza ta si vezi o previzualizare privata a lookului de creator pe tine.',
    'how.featureKicker': 'Ce face',
    'how.featureTitle': 'Un motor de cautare de stil cu un pic de gust.',
    'how.feature.find.title': 'Gaseste haine similare',
    'how.feature.find.copy': 'Cauta dupa cum arata outfitul, chiar daca nu stii brandul original.',
    'how.feature.explain.title': 'Intelege lookul',
    'how.feature.explain.copy': 'Vezi piesele si semnalele de styling, nu o lista plata de produse aleatorii.',
    'how.feature.try.title': 'Fit on me',
    'how.feature.try.copy': 'Foloseste pozele tale private pentru o previzualizare inspirata de outfitul ales.',
    'how.feature.save.title': 'Salveaza si compara',
    'how.feature.save.copy': 'Pastreaza lookuri, idei de produse si previewuri try-on impreuna pentru mai tarziu.',
    'how.trustKicker': 'Limite clare',
    'how.trustTitle': 'Util, dar fara certitudini false.',
    'how.trustCopy': 'Outfit Detective sugereaza stiluri vizual similare, fara sa pretinda ca fiecare produs este exact sau aprobat de un creator.',
    'how.trust.1': 'Alternativele similare sunt sugestii, nu produse exacte garantate.',
    'how.trust.2': 'Pozele fit-on-me raman personale si sunt folosite pentru previewul tau.',
    'how.trust.3': 'Cand se poate, aplicatia explica semnale ca culoare, silueta si categorie.',
    'how.finalKicker': 'Gata cand apare curiozitatea',
    'how.finalTitle': 'Data viitoare când un look îți oprește scrollul, fă-l util.',
    'how.finalCopy': 'Transformă un look public de creator într-un breakdown, alternative similare și o idee fit-on-me privată cât timp inspirația e proaspătă.',
    'how.finalCta': 'Incepe beta',
    'how.finalSecondary': 'Exploreaza lookuri publice',
    'app.photos': 'Pozele mele',
    'app.profile': 'Profil',
    'app.menu': 'Meniu',
    'common.follow': 'Urmărește',
    'common.unfollow': 'Nu mai urmări',
    'common.remove': 'Elimină',
    'profile.followingStatus': 'Urmărit',
    'profile.notFollowingStatus': 'Neurmărit',
    'common.open': 'Deschide',
    'common.preview': 'Previzualizare',
    'common.cancel': 'Anulează',
    'followConfirm.title': 'Nu mai urmărești profilul?',
    'followConfirm.copy': '{name} va ieși din lista ta și din feedul personal.',
    'followConfirm.confirming': 'Se elimină...',
    'login.heroTitle': 'Probează pe tine lookurile pe care le vezi pe Instagram și TikTok.',
    'login.heroSubtitle': 'Urmărește lookuri publice de la creatori, păstrează private previewurile fit-on-me și găsește alternative similare ca să recreezi fitul.',
    'login.pillCelebs': 'Dropuri fresh',
    'login.pillDetect': 'Fit radar',
    'login.pillShop': 'Alternative similare',
    'login.proofFast': 'Dropuri noi',
    'login.proofFastLabel': 'lookuri cand apar',
    'login.proofSources': 'Instagram + TikTok',
    'login.proofSourcesLabel': 'impreuna intr-un feed',
    'login.proofPersonal': 'Al tău',
    'login.proofPersonalLabel': 'doar followurile tale',
    'login.kicker': 'Style feed-ul tău',
    'login.formTitle': 'Intra in Outfit Detective',
    'login.formSubtitle': 'Introdu emailul si iti trimitem un cod unic pentru login.',
    'login.email': 'Emailul tau',
    'login.emailPlaceholder': 'nume@email.com',
    'login.title': 'Intră în Outfit Detective',
    'login.subtitle': 'Urmărește profilurile preferate și găsește cele mai bune ținute într-un feed personal.',
    'login.sendCode': 'Trimite codul unic',
    'login.sending': 'Se trimite...',
    'login.otp': 'Introdu codul',
    'login.changeEmail': 'Schimbă emailul',
    'login.verify': 'Continuă',
    'login.verifying': 'Se verifică...',
    'login.codeSent': 'Verifică emailul. Codul rămâne fresh {minutes} minute. Dacă nu îl vezi, uită-te și în spam sau promoții.',
    'login.pendingOtpRestored': 'Codul trimis încă merge.',
    'login.pendingOtpExpired': 'Codul a expirat. Cere unul nou ca să continui.',
    'login.useReceivedCode': 'Am deja un cod',
    'login.resendCode': 'Trimite-mi alt cod',
    'feed.yourList': 'Lista ta',
    'feed.findProfiles': 'Caută profiluri',
    'feed.searchList': 'Caută în listă',
    'feed.noListSearchResults': 'Niciun profil găsit.',
    'feed.noProfiles': 'Niciun profil încă.',
    'feed.heroTitle': 'Ținute noi de la profilurile urmărite.',
    'feed.heroEmpty': 'Urmărește profilurile care te interesează pentru a crea feedul tău de outfituri.',
    'feed.heroCount': 'Urmărești {count} {label}.',
    'feed.emptyTitle': 'Feedul este gol',
    'feed.emptyCopy': 'Adaugă profiluri din Discover pentru a construi un feed personal.',
    'feed.popularFallbackTitle': 'Lookuri populare pana feedul tau e gata',
    'feed.popularFallbackCopy': 'Urmareste profiluri ca feedul sa devina personal. Pana atunci iti aratam lookuri populare.',
    'feed.popularFallbackCta': 'Gaseste-ti style crush-urile',
    'feed.personalPendingCopy': 'Pregatim continutul profilurilor urmarite. Lookurile noi vor aparea aici automat.',
    'feed.suggested': 'Sugestii',
    'feed.news': 'Noutăți',
    'feed.aiAction': 'Recreează lookul',
    'aiDialog.kicker': 'Outfit Detective AI',
    'aiDialog.title': 'Cumpara lookul cu matchuri clare.',
    'aiDialog.copy': 'Citim postarea, prindem piesele si aranjam matchurile dupa vibe si buget.',
    'aiDialog.tagsLabel': 'Detalii match',
    'aiDialog.tagThreeLanes': '3 bugete',
    'aiDialog.tagSmartMatch': 'vibe-ul primul',
    'aiDialog.laneExactTitle': 'Aproape piesa reala',
    'aiDialog.laneExactCopy': 'Apare doar cand brandul, modelul, categoria si indiciile vizuale se potrivesc cu incredere mare.',
    'aiDialog.laneSimilarTitle': 'Aceeasi energie',
    'aiDialog.laneSimilarCopy': 'Piese apropiate de stilul postarii, moodul brandului si silueta.',
    'aiDialog.laneBudgetTitle': 'Smart save',
    'aiDialog.laneBudgetCopy': 'Lookul, fara soc la portofel.',
    'aiDialog.laneLuxuryTitle': 'Luxury lane',
    'aiDialog.laneLuxuryCopy': 'Finduri premium cand fitul merita buget de main character.',
    'aiDialog.loadingTitle': 'Detectam fitul',
    'aiDialog.loadingCopy': 'Poti inchide pop-upul. Noi continuam in background; cand revii aici sau pe pagina postarii, findurile apar imediat ce sunt gata.',
    'aiDialog.loadingPageCopy': 'Noi continuam in background. Ramai aici sau revino mai tarziu: findurile apar pe pagina asta imediat ce sunt gata.',
    'aiDialog.liveTitle': 'Cautarea inca merge',
    'aiDialog.liveCopy': 'Cateva finduri sunt deja aici. Poti inchide; noi continuam actualizarile in background.',
    'aiDialog.livePageCopy': 'Cateva finduri sunt deja aici. Continuam sa actualizam pagina asta in background.',
    'aiDialog.readyTitle': 'Matchuri gata',
    'aiDialog.readyCopy': 'Deschide ce iti place. Rankingul il rafinam dupa primele teste.',
    'aiDialog.errorTitle': 'Match ratat',
    'aiDialog.errorCopy': 'Lookul nu a matchuit acum. Incearca din nou putin mai tarziu.',
    'aiDialog.retry': 'Incearca din nou',
    'aiDialog.emptyTitle': 'Inca fara match curat',
    'aiDialog.emptyCopy': 'Imaginea nu a fost destul de clara pentru rezultate shopping utile.',
    'aiDialog.noLaneResults': 'Inca nu avem finduri bune in bugetul asta.',
    'aiDialog.lookMatch': 'look match',
    'aiDialog.vibeMatch': 'vibe match',
    'aiDialog.openLook': 'Fit source',
    'aiDialog.close': 'Inchide dialogul AI',
    'discover.title': 'Caută pe cine vrei să urmărești.',
    'discover.subtitle': 'Găsește profiluri Instagram sau TikTok și adaugă-le în lista ta.',
    'discover.added': '{name} a fost adăugat în lista ta.',
    'discover.pasteTitle': 'Lipește un profil',
    'discover.openBulk': 'Importă mai multe profiluri',
    'discover.yourList': 'Lista ta',
    'discover.noFollowingTitle': 'Niciun profil încă',
    'discover.inFeed': 'În feedul tău',
    'discover.followersUnavailable': 'Followeri indisponibili',
    'discover.followError': 'Nu pot urmări acest profil acum. Încearcă din nou puțin mai târziu.',
    'discover.blockedKicker': 'Notificare drepturi',
    'discover.blockedTitle': 'Acest profil nu poate fi urmărit',
    'discover.blockedCopy': 'Din motive de drepturi, {name} nu este disponibil pentru follow sau pentru feedul tău personal pe Outfit Detective.',
    'discover.blockedReference': 'Referință',
    'discover.blockedClose': 'Am înțeles',
    'detail.back': 'Înapoi la feed',
    'detail.aiTitle': 'Matchuri shopping pentru look',
    'detail.aiEmptyTitle': 'Inca fara matchuri salvate',
    'detail.aiEmptyCopy': 'Porneste matchul AI pentru piese si bugete shopping.',
    'feed.openDetail': 'Deschide lookul',
    'photos.kicker': 'Poze try-on',
    'photos.title': 'Pozele mele',
    'photos.copy': 'Ține câteva poze clare pregătite ca să vezi lookurile direct pe tine.',
    'photos.uploadTitle': 'Adaugă o poză',
    'photos.uploadCopy': 'Cele mai bune rezultate ies cu poze luminoase, din cap până-n picioare.',
    'photos.usageNoteBefore': 'Folosim pozele doar ca să creăm',
    'photos.usageNoteAfter': 'tale. Alege poze clare, din cap până-n picioare; le poți șterge oricând.',
    'photos.pickFile': 'Alege o poză',
    'photos.optimizedBeforeUpload': 'Gata pentru try-on',
    'photos.titlePlaceholder': 'Numele pozei',
    'photos.upload': 'Încarcă poza',
    'photos.uploading': 'Se încarcă...',
    'photos.uploaded': 'Poză salvată.',
    'photos.processingConsent': 'Sunt de acord ca această poză să fie procesată cu sistemele noastre AI și OpenAI pentru generarea de try-on-uri. Poza rămâne privată.',
    'photos.emptyTitle': 'Încă nu ai poze',
    'photos.emptyCopy': 'Încarcă o poză din cap până-n picioare pentru preview-uri try-on mai bune.',
    'photos.default': 'Implicită',
    'photos.makeDefault': 'Setează implicită',
    'photos.framing.full_body': 'Corp întreg',
    'photos.framing.upper_body': 'Partea de sus',
    'photos.framing.portrait': 'Portret',
    'photos.framing.unknown': 'Nu sunt sigur',
    'photos.error.image_too_large': 'Imaginea optimizată este încă prea mare. Încearcă altă poză.',
    'photos.error.photo_processing_consent_required': 'Confirmă acordul pentru procesarea pozei înainte de încărcare.',
    'photos.error.photo_safety_rejected': 'Această poză nu poate fi folosită pentru try-on. Încarcă o poză sigură, cu un adult îmbrăcat.',
    'photos.error.photo_safety_check_failed': 'Nu putem rula verificarea de siguranță a pozei acum. Încearcă puțin mai târziu.',
    'account.kicker': 'Profil',
    'account.title': 'Profilul tău Outfit Detective',
    'account.copy': 'Gestionează pozele și uneltele personale.',
    'account.overview': 'Prezentare',
    'account.tryonTitle': 'Gata pentru try-on',
    'account.tryonCopy': 'Pozele tale alimentează preview-urile personale din lookurile pe care le descoperi.',
    'account.privacyTitle': 'Tu controlezi',
    'account.privacyCopy': 'Nu avem nevoie de datele tale personale ca aplicația să fie utilă. Aceste setări sunt opționale, editabile oricând și folosite doar pentru a-ți ajusta experiența.',
    'account.legalLink': 'Setări de confidențialitate și legal',
    'account.shopAreaTitle': 'Zona ta fit radar',
    'account.shopAreaCopy': 'Outfit Detective cauta pe platforme online si in magazine fizice. Alege o zona de baza si prioritizam matchurile suficient de aproape pentru probat, ridicat sau livrare rapida.',
    'account.locationLabel': 'Zona de baza',
    'account.locationPlaceholder': 'Oras sau zona, ex. Bucuresti, Floreasca',
    'account.locationResults': 'Sugestii locatie',
    'account.locationSearching': 'Caut locatii...',
    'account.locationNoResults': 'Inca niciun match clar. Incearca oras plus tara.',
    'account.locationSelected': 'Zona verificata selectata',
    'account.shoppingCountryFromLocation': 'Tara pentru shopping: {country}, calculata din zona ta fit radar.',
    'account.radiusLabel': 'Raza cautare: {km} km',
    'account.radiusAria': 'Raza de cautare',
    'account.locationPrivacy': 'Nu avem nevoie de adresa exacta. Orasul sau cartierul este suficient si il poti sterge oricand.',
    'account.styleGenderTitle': 'Stilul wallului',
    'account.styleGenderCopy': 'Alege ce lookuri vrei sa vezi primele in wall si in profilurile sugerate.',
    'account.styleGender.all': 'Toate',
    'account.styleGender.menswear': 'Barbati',
    'account.styleGender.womenswear': 'Femei',
    'account.styleGenderConfirm': 'Confirm ca vreau sa vad toate lookurile in wall.',
    'account.styleGenderSummaryTitle': 'Preferinta wall',
    'account.styleGenderSummary.all': 'Wallul si sugestiile pot include orice stil.',
    'account.styleGenderSummary.menswear': 'Wallul si sugestiile prioritizeaza barbati si lookuri menswear.',
    'account.styleGenderSummary.womenswear': 'Wallul si sugestiile prioritizeaza femei si lookuri womenswear.',
    'account.identityTitle': 'Cum apari public',
    'account.identityCopy': 'Pentru zone publice ca Fit Wall si try-onuri partajate, alege numele real sau ramai low-key cu nickname. Emailul ramane in backstage.',
    'account.useNickname': 'Nickname',
    'account.useRealName': 'Nume real',
    'account.realNameLabel': 'Nume real',
    'account.realNamePlaceholder': 'Vizibil doar daca alegi nume real',
    'account.nicknameLabel': 'Nickname',
    'account.nicknamePlaceholder': 'Numele tau public de stil',
    'account.useSuggestion': 'Foloseste {nickname}',
    'account.save': 'Salveaza profilul',
    'account.saving': 'Salvez...',
    'account.saved': 'Profil salvat.',
    'account.areaSummaryTitle': 'Prioritate shopping',
    'account.areaSummarySet': 'Prioritate pentru matchuri online si magazine in {km} km de {location}.',
    'account.areaSummaryEmpty': 'Nicio zona setata. Rezultatele raman largi si online-first.',
    'account.publicSummaryTitle': 'Nume public',
    'account.publicSummaryCopy': 'Try-onurile publice vor aparea ca {name}.',
    'account.deleteTitle': 'Sterge contul',
    'account.deleteCopy': 'Asta suspenda contul acum. Ai 15 zile sa il reactivezi intrand din nou; dupa aceea stergem definitiv profilul, pozele, try-onurile, followurile, preferintele si sesiunile.',
    'account.deleteConfirmLabel': 'Scrie DELETE pentru confirmare',
    'account.deleteConfirmMismatch': 'Scrie exact DELETE pentru a confirma stergerea contului.',
    'account.deleteButton': 'Sterge contul meu',
    'account.deleting': 'Programam stergerea...',
    'account.deleteError': 'Nu pot programa stergerea contului acum.',
    'account.error.nickname_required': 'Alege un nickname ca sa continui.',
    'account.error.display_name_required': 'Adauga un nume real sau treci pe nickname.',
    'account.error.request_failed': 'Nu pot salva acum. Incearca din nou.',
    'account.error.api_unreachable': 'API indisponibil momentan.',
    'account.error.profile_not_found': 'Profil negasit.',
    'tryon.kicker': 'Try the vibe on me',
    'tryon.title': 'Vezi lookul pe poza ta',
    'tryon.ctaTitle': 'Vezi fitul pe mine',
    'tryon.ctaCopy': 'Alege o poza si vezi outfitul pe tine. Fit check instant.',
    'tryon.open': 'Incearca',
    'tryon.hide': 'Ascunde',
    'tryon.existingKicker': 'Deja generate',
    'tryon.existingTitle': 'Ai deja try-on-uri pentru acest look',
    'tryon.existingCopy': 'Poti refolosi unul dintre rezultate sau crea un preview nou cu alta poza.',
    'tryon.originalPost': 'Postarea originala',
    'tryonGallery.openEvent': 'Fit source',
    'tryonShare.button': 'Imbraca-te precum {celebrity}',
    'tryonShare.title': 'Imbraca-te precum {celebrity}',
    'tryonShare.text': 'Imbraca-te precum {celebrity} cu Outfit Detective.',
    'tryonShare.chooseChannel': 'Distribuie',
    'tryonShare.facebook': 'Distribuie pe Facebook',
    'tryonShare.pinterest': 'Distribuie pe Pinterest',
    'tryonShare.email': 'Distribuie prin email',
    'tryonShare.whatsapp': 'Distribuie pe WhatsApp',
    'tryonShare.x': 'Distribuie pe X',
    'tryonShare.copied': 'Text copiat',
    'tryonShare.copyFailed': 'Partajarea nu este disponibila aici. Incearca din meniul browserului.',
    'tryonShare.celebrityFallback': 'creatorul acesta',
    'tryon.newPreviewTitle': 'Creeaza un preview nou',
    'tryon.newPreviewCopy': 'Alege poza folosita ca referinta pentru corp.',
    'tryon.error.photo_framing_not_enough': 'Lookul are nevoie de o poza full-body.',
    'login.legalPrefix': 'Conectându-te, accepți',
    'login.success': 'Te-ai conectat.',
    'login.legalLink': 'Politica de confidențialitate',
    'login.legalAnd': 'si',
    'login.termsLink': 'Termenii serviciului',
    'app.saved': 'Salvate',
    'welcome.preferencesLabel': 'Preferințe de stil',
    'welcome.pref.budget': 'Budget',
    'welcome.pref.street': 'Street',
    'welcome.pref.minimal': 'Minimal',
    'welcome.pref.luxury': 'Luxury',
    'welcome.pref.everyday': 'Everyday',
    'feed.save': 'Salvează lookul',
    'feed.unsave': 'Elimină din salvate',
    'feed.share': 'Distribuie lookul',
    'feed.tryOn': 'Încearcă pe mine',
    'feed.shareTitle': 'Look de la {name}',
    'feed.shareText': 'Am găsit acest look: {title}',
    'feed.shareFallback': 'Look Outfit Detective',
    'feed.shareFailed': 'Nu am putut copia linkul de distribuire.',
    'feed.badgeFresh': 'nou de la followurile tale',
    'feed.badgePopular': 'popular acum',
    'feed.badgeMatched': '{count} piese potrivite',
    'feed.starterKicker': 'Începe aici',
    'feed.starterTitle': 'Fă feedul al tău în trei tapuri.',
    'feed.starterFollow': 'Urmărește profiluri',
    'feed.starterMatch': 'Cumpără lookul',
    'feed.starterSave': 'Salvează sau distribuie',
    'feed.starterCta': 'Caută profiluri',
    'aiDialog.best': 'Alegerea top',
    'aiDialog.bestPick': 'Alegerea top · {price}',
    'aiDialog.quick.budget': 'Budget de la {price}',
    'aiDialog.quick.similar': 'Similar de la {price}',
    'aiDialog.quick.luxury': 'Luxury de la {price}',
    'lookShare.open': 'Creează story card',
    'lookShare.title': 'Story card',
    'lookShare.total': 'Look găsit cu aproximativ {total}',
    'lookShare.noTotal': 'Look găsit cu Outfit Detective',
    'lookShare.download': 'Descarcă story card',
    'lookShare.hint': 'Folosește-l ca cover Reel/Story sau după o înregistrare de ecran.',
    'similarRail.kicker': 'Shopping vizual',
    'similarRail.title': 'Cumpără produse similare',
    'similarRail.emptyCopy': 'Pornește Fit AI ca să vezi un rail vizual cu matchuri curate pentru acest look.',
    'similarRail.loading': 'Căutăm matchuri vizuale curate...',
    'similarRail.start': 'Găsește produse similare',
    'similarRail.showAll': 'Arată toate matchurile',
    'similarRail.showMore': 'Arată mai mult',
    'similarRail.lane.exact': 'Exact',
    'similarRail.lane.similar': 'Similar',
    'similarRail.lane.budget': 'Budget',
    'similarRail.lane.luxury': 'Luxury',
    'productInfo.kicker': 'Match produs',
    'productInfo.price': 'Pret',
    'productInfo.match': 'Piesa potrivita',
    'productInfo.matchFallback': 'Acest look',
    'productInfo.copy': 'Ramai aici: vezi piesa, apoi incearca tot fitul pe poza ta.',
    'productInfo.tryOn': 'Incearca fitul',
    'productInfo.openShop': 'Deschide shop',
    'saved.kicker': 'Radarul tău outfit',
    'saved.title': 'Lookuri salvate',
    'saved.emptyTitle': 'Încă nu ai lookuri salvate',
    'saved.emptyCopy': 'Salvează lookurile pe care vrei să le revezi, distribui sau încerci mai târziu.',
    'saved.emptyCta': 'Înapoi la feed',
    'saved.open': 'Deschide lookul',
    'saved.remove': 'Elimină',
    'publicTryOn.ctaCreate': 'Creează versiunea ta',
    'publicTryOn.ctaFind': 'Găsește lookuri similare',
    'app.feed': 'Feed',
    'app.discover': 'Descoperă',
    'app.fitWall': 'Fit Wall',
    'nav.feed': 'Feed',
    'nav.fitWall': 'Wall',
    'nav.photos': 'Poze',
    'nav.discover': 'Descopera',
    'nav.help': 'Ajutor',
    'app.legal': 'Legal',
    'common.loading': 'Se încarcă...',
    'common.previous': 'Înapoi',
    'common.next': 'Înainte',
    'feed.following': 'Urmărite',
    'feed.apiError': 'API indisponibil: {error}',
    'feed.loading': 'Se încarcă feedul...',
    'feed.noSuggestions': 'Nicio sugestie momentan.',
    'discover.removeError': 'Nu se poate actualiza acum.',
    'discover.loadMore': 'Încarcă mai mult',
    'discover.loadingMore': 'Se încarcă...',
    'detail.loading': 'Se încarcă lookul...',
    'detail.priceUnavailable': 'Preț indisponibil',
    'detail.refreshAi': 'Actualizează Fit AI',
    'aiDialog.resultCount': '{count} matchuri produs',
    'aiDialog.error.ai_matching_disabled': 'Matchingul AI nu este activ încă.',
    'aiDialog.error.event_image_missing': 'Această postare nu are imagine de analizat.',
    'aiDialog.error.no_items_detected': 'Fitul nu a fost destul de clar pentru piese utile.',
    'aiDialog.error.auth_required': 'Acest match nu este inca in cache. Intra ca sa pornesti o cautare noua.',
    'shareCard.generating': 'Se generează...',
    'shareCard.downloadError': 'Nu am putut crea cardul.',
    'tryon.copy': 'Alege o poză cu tine și creează un preview personal inspirat de acest fit.',
    'tryon.needMatchTitle': 'Pornește mai întâi Fit AI',
    'tryon.needMatchCopy': 'Avem nevoie de piesele detectate înainte să generăm previewul try-on.',
    'tryon.startFitAi': 'Pornește Fit AI',
    'tryon.viewFitAiStatus': 'Vezi status Fit AI',
    'tryon.noPhotosTitle': 'Adaugă mai întâi o poză',
    'tryon.noPhotosCopy': 'Încarcă o poză full-body, ca previewul să aibă context suficient.',
    'tryon.generateShop': 'Generează try-on',
    'tryon.generateReplica': 'Generează replica',
    'tryon.queueing': 'Pornesc...',
    'tryon.notifyEmail': 'Anunță-mă pe email când e gata',
    'tryon.notifyEmailOnCopy': 'Activează și poți pleca din ecran în loc să aștepți aici.',
    'tryon.notifyEmailOffCopy': 'Fără reminder pe email. Ține Try-onurile mele deschise sau revino mai târziu.',
    'tryon.modeLabel': 'Tip preview',
    'tryon.modeTryonTitle': 'Try-on real',
    'tryon.modeTryonName': 'Construiește fitul coș',
    'tryon.modeTryonCopy': 'Alege piesele shop. Ce sari rămâne din lookul original.',
    'tryon.cartUnavailableKicker': 'Opțiune fit coș',
    'tryon.cartUnavailableTitle': 'Construiește fitul coș după matchul de produse',
    'tryon.cartUnavailableCopy': 'Mai întâi găsim produse similare pentru acest look. Apoi poți alege piesele shop și le mixăm cu fitul original.',
    'tryon.cartUnavailableCta': 'Găsește produse similare',
    'tryon.cartUnavailableLoading': 'Caut produse...',
    'tryon.modeReplicaTitle': 'Replica curată',
    'tryon.modeReplicaName': 'Copiază fitul original',
    'tryon.modeReplicaCopy': 'Folosește postarea ca referință completă. Fără coș, doar vibe clonat.',
    'tryon.shopBuilderTitle': 'Alege piesele pe care chiar le vrei',
    'tryon.shopBuilderCopy': 'Selectează un produs per piesă. Piesele sărite rămân din lookul original.',
    'tryon.selectedCount': '{count} alese',
    'tryon.totalUnavailable': 'Total variabil',
    'tryon.shopSelected': 'Folosim piesa ta shop',
    'tryon.shopOriginalFallback': 'Neales: rămâne piesa originală',
    'tryon.shopNoProducts': 'Încă nu avem produs util pentru această piesă.',
    'tryon.shopResponsibleCopy': 'Prețurile și disponibilitatea vin de la magazine și se pot schimba. Acesta este un preview AI, nu o garanție de potrivire.',
    'tryon.selectProductsFirstTitle': 'Alege cel puțin o piesă',
    'tryon.selectProductsFirstCopy': 'Selectează un produs shop de mai sus, apoi poți alege poza și genera try-onul.',
    'tryon.selectedProductsTitle': 'Piese alese: {total}',
    'tryon.newPreviewShopCopy': 'Alege poza ta: mixăm produsele alese cu fitul original.',
    'tryon.newPreviewReplicaCopy': 'Alege poza ta și replicăm lookul original al celebrity.',
    'tryon.pendingExistingCopy': 'Există deja un preview în generare pentru acest look. Când e gata îl găsești în Try-onurile mele.',
    'tryon.queuedTitle': 'Preview în generare',
    'tryon.queuedEmailTitle': 'Reminderul pe email este activ',
    'tryon.pendingEmailOn': 'Îți trimitem email când imaginea e gata și o salvăm în Try-onurile mele.',
    'tryon.pendingEmailOff': 'Nu trimitem email. Poți închide: previewul apare în Try-onurile mele.',
    'tryon.readyNoticeTitle': 'Try-onul tau e gata',
    'tryon.readyNoticeCopy': 'Previewul s-a terminat si este salvat in Try-onurile mele.',
    'tryon.readyNoticeCta': 'Vezi try-onurile mele',
    'tryon.readyNoticeStay': 'Raman aici',
    'tryon.openTryons': 'Deschide try-onurile mele',
    'tryon.resultAlt': 'Preview try-on',
    'tryon.error.ai_match_required': 'Pornește Fit AI înainte să încerci lookul.',
    'tryon.error.selected_products_required': 'Alege cel puțin o piesă shop sau treci pe Replica.',
    'tryon.error.try_on_input_safety_rejected': 'Acest try-on nu poate fi generat deoarece una dintre imaginile de referință nu a trecut verificarea de siguranță.',
    'tryon.error.try_on_output_safety_rejected': 'Imaginea generată nu a trecut verificarea de siguranță, așa că nu am salvat-o.',
    'tryon.error.try_on_safety_check_failed': 'Nu am putut finaliza verificarea de siguranță acum. Încearcă puțin mai târziu.',
    'tryon.error.generic': 'Previewul nu a reușit acum. Încearcă din nou puțin mai târziu.',
    'tryonGallery.originalPost': 'Postarea originală',
    'tryonGallery.userPhoto': 'Poza ta',
    'tryonGallery.status.running': 'În lucru',
    'tryonGallery.status.completed': 'Gata',
    'tryonGallery.status.failed': 'Eșuat',
    'tryonShare.shopTitle': 'Look precum {celebrity} cu aproximativ {total}',
    'tryonShare.shopText': 'Am construit un fit inspirat de {celebrity} cu aproximativ {total} pe Outfit Detective.',
    'tryonFeed.creatorFallback': 'Creator Fit Wall',
    'publicTryOn.kicker': 'Try-on distribuit',
    'publicTryOn.title': 'Îmbracă-te precum {celebrity}',
    'publicTryOn.copy': '{creator} a creat acest try-on AI cu Outfit Detective.',
    'publicTryOn.shopCopy': '{creator} a construit acest try-on pregătit de coș cu piese selectate pentru aproximativ {total}.',
    'publicTryOn.ctaKicker': 'Zero stres, mai mult semnal de stil',
    'publicTryOn.ctaTitle': 'Găsește lookul înainte să-l îngroape algoritmul.',
    'publicTryOn.ctaCopy': 'Urmărește creatori, salvează fituri fresh, testează vibeul pe pozele tale și găsește piese similare.',
    'publicTryOn.cta': 'Creează fit radarul meu',
    'publicTryOn.missingTitle': 'Acest try-on nu mai este disponibil',
    'publicTryOn.missingCopy': 'Linkul poate fi greșit sau previewul nu mai este disponibil.',
    'common.refresh': 'Actualizează',
    'common.on': 'Pornit',
    'common.off': 'Oprit',
    'common.profile': 'profil',
    'common.profiles': 'profiluri',
    'welcome.kicker': 'Ai intrat',
    'welcome.title': 'Construiește un feed care chiar înțelege fitul.',
    'welcome.copy': 'Urmărește lookuri publice de la creatori, probează fitul privat pe pozele tale și compară alternative similare pe luxury, mid și budget.',
    'welcome.pointFeedTitle': 'Profilurile tale',
    'welcome.pointFeedCopy': 'Alege profilurile. Noi păstrăm feedul curat și personal.',
    'welcome.pointDetectTitle': 'Fit radar',
    'welcome.pointDetectCopy': 'Transformăm postările publice în indicii outfit utile.',
    'welcome.pointBudgetTitle': 'Bugete',
    'welcome.pointBudgetCopy': 'Vibe similar, de la luxury grails la variante accesibile.',
    'welcome.cta': 'Caută profiluri',
    'welcome.secondary': 'Arată feedul',
    'welcome.dismiss': 'Închide welcome',
    'welcome.legalPrefix': 'Continuând accepți',
    'welcome.legalAnd': 'și',
    'followRefresh.kicker': 'Bine ai revenit',
    'followRefresh.title': 'Actualizăm profilurile urmărite.',
    'followRefresh.copy': 'Cât ai lipsit, am pus pe pauză câteva verificări de fundal, deci unele profiluri urmărite pot să nu fie complet la zi.',
    'followRefresh.cta': 'Actualizează urmăririle',
    'followRefresh.refreshing': 'Actualizez...',
    'followRefresh.secondary': 'Nu acum',
    'followRefresh.dismiss': 'Închide notificarea',
    'followRefresh.success': 'Actualizăm acum {count} profiluri.',
    'followRefresh.fresh': 'Profilurile urmărite par deja la zi.',
    'followRefresh.error': 'Nu pot porni actualizarea acum.',
    'feed.heroKicker': 'Feed fashion personal',
    'feed.cachedPosts': 'postări cache',
    'feed.images': 'imagini',
    'feed.updated': 'Actualizat {date}.',
    'feed.workerReady': 'Worker pregătit să colecteze lookuri noi.',
    'feed.checkingServices': 'Verific servicii...',
    'tryonGallery.title': 'Try-onurile mele',
    'tryonGallery.emptyTitle': 'Încă nu ai try-onuri',
    'tryonGallery.emptyCopy': 'Creează primul preview dintr-un look.',
    'tryonGallery.untitled': 'Try-on fără titlu',
    'tryonGallery.created': 'Creat',
    'tryonGallery.email': 'Email',
    'tryonGallery.emailOn': 'Email pornit',
    'tryonGallery.emailOff': 'Email oprit',
    'tryonGallery.openGeneratedImage': 'Deschide imaginea generată',
    'tryonGallery.openOriginalImage': 'Deschide imaginea originală',
    'tryonGallery.delete': 'Șterge',
    'tryonGallery.deleting': 'Șterg...',
    'tryonGallery.deleteConfirm': 'Ștergi acest try-on?',
    'tryonGallery.error.deleteFailed': 'Nu am putut șterge try-onul.',
    'tryonGallery.error.try_on_preview_not_found': 'Try-on negăsit.',
    'tryonGallery.viewerLabel': 'Viewer imagine',
    'tryonGallery.zoomIn': 'Mărește',
    'tryonGallery.zoomOut': 'Micșorează',
    'shareCard.title': 'Share card',
    'shareCard.download': 'Descarcă cardul',
    'shareCard.hint': 'Salvează-l și postează-l în Stories sau Reels.',
    'shareCard.openButton': 'Creează share card',
    'tryonFeed.kicker': 'Feed public try-on',
    'tryonFeed.title': 'Fit Wall',
    'tryonFeed.copy': 'Try-onuri reale pe care oamenii le-au făcut publice. Scrollează glow-upuri, deschide postarea originală și ia inspirație.',
    'tryonFeed.emptyTitle': 'Fit Wall așteaptă primul drop',
    'tryonFeed.emptyCopy': 'Fă public unul dintre try-onurile tale ca să pornești feedul.',
    'tryonFeed.emptyCta': 'Deschide try-onurile mele',
    'tryonFeed.fitCheck': 'Fit check',
    'tryonFeed.shareThis': 'Distribuie fitul',
    'tryonFeed.rightTitle': 'Cum ajunge un fit aici',
    'tryonFeed.rightCopy': 'Try-onurile din Fit Wall sunt publicate de utilizatori. Generezi un look, îl activezi public și îl poți retrage oricând. Low-key cu nickname sau direct cu numele tău.',
    'tryonFeed.rightCta': 'Try-onurile mele',
    'tryonFeed.rightBadge': 'AI try-on · public voluntar',
    'tryon.generate': 'Generează preview',
    'tryon.generating': 'Generez...',
    'tryon.cartPresetLabel': 'Alegeri automate cos',
    'tryon.cartPresetClosest': 'Cel mai apropiat',
    'tryon.cartPresetLuxury': 'Luxury',
    'tryon.cartPresetBudget': 'Budget',
    'tryon.typeSelectedProducts': 'Try-on',
    'tryon.typeReplica': 'Replica',
    'tryon.queuedCopy': 'Poți închide această fereastră. Rezultatul va apărea în Try-onurile mele.',
    'tryon.queuedEmailCopy': 'Poți închide această fereastră. Te anunțăm prin email și îl salvăm în Try-onurile mele.',
    'tryonPublic.offTitle': 'Păstrează privat',
    'tryonPublic.offCopy': 'Try-onul rămâne în arhiva ta. Pornește-l ca să apară în Fit Wall.',
    'tryonPublic.onTitle': 'Live în Fit Wall',
    'tryonPublic.onCopy': 'Fit check public activ. Îl poți retrage oricând se schimbă vibe-ul.',
    'tryonPublic.useName': 'Folosește numele meu de profil',
    'tryonPublic.useNameCopy': 'Oamenii vor vedea numele tău de profil în această postare Fit Wall.',
    'tryonPublic.aliasCopy': 'Rămâi low-key ca {alias}.',
    'tryonPublic.aliasPending': 'un nume fit aleatoriu',
    'tryonPublic.error.try_on_not_ready': 'Acest try-on trebuie să fie gata înainte să ajungă în Fit Wall.',
    'tryonPublic.error.generic': 'Nu pot actualiza partajarea în Fit Wall acum.',
    'legal.kicker': 'Legal',
    'legal.title': 'Documente legale',
    'legal.copy': 'Informatii despre confidentialitate, termenii serviciului si solicitari privind datele.',
    'legal.privacyTitle': 'Politica de confidentialitate',
    'legal.privacyCopy': 'Informare privind prelucrarea datelor personale.',
    'legal.termsTitle': 'Termenii serviciului',
    'legal.termsCopy': 'Conditii de utilizare a serviciului.',
    'legal.cookiesTitle': 'Cookie-uri si stocare locala',
    'legal.cookiesCopy': 'Stocare tehnica in browser folosita pentru login si preferinta de limba.',
    'legal.contactTitle': 'Contact si solicitari privind datele',
    'legal.contactCopy': 'Cum ne contactezi pentru confidentialitate, drepturi GDPR si suport legal.',
    'legal.available': 'Disponibil',
    'legal.backToApp': 'Inapoi la Outfit Detective',
    'alerts.tab': 'Alerte',
    'alerts.title': 'Alerte',
    'alerts.copy': 'Actualizari importante despre profilurile pe care le urmaresti si activitatea contului.',
    'alerts.notice': 'Notificare',
    'alerts.emptyTitle': 'Nu ai alerte inca',
    'alerts.emptyCopy': 'Cand se schimba ceva important, vei gasi aici.',
    'alerts.markRead': 'Marcheaza citit',
    'alerts.emailSent': 'Email trimis: {date}',
    'alerts.error': 'Nu putem incarca alertele acum.',
    'alerts.profileIncomplete.title': 'Completeaza profilul',
    'alerts.profileIncomplete.bodyBoth': 'Adauga o zona de shopping si confirma preferinta pentru wall ca sa primesti feed si sugestii mai bune.',
    'alerts.profileIncomplete.bodyLocation': 'Adauga o zona de shopping ca sa primesti feed si sugestii de produse mai bune.',
    'alerts.profileIncomplete.bodyStyleGender': 'Confirma preferinta de stil pentru wall ca sa primesti feed si profiluri sugerate mai bune.',
    'alerts.profileIncomplete.cta': 'Completeaza profilul',
    'alerts.followedProfile.fallbackName': 'Un profil',
    'alerts.followedProfile.banned.title': 'Profil urmarit indisponibil',
    'alerts.followedProfile.banned.body': '{profile} a fost eliminat din lista ta de urmariri si nu poate fi urmarit momentan din motive de drepturi sau gestionare continut.',
    'alerts.followedProfile.banned.bodyReason': '{profile} a fost eliminat din lista ta de urmariri si nu poate fi urmarit momentan din motive de drepturi sau gestionare continut. Motiv: {reason}',
    'alerts.followedProfile.deletedBanned.title': 'Profil urmarit sters si blocat',
    'alerts.followedProfile.deletedBanned.body': '{profile} a fost sters, eliminat din lista ta de urmariri si nu poate fi urmarit momentan din motive de drepturi sau gestionare continut.',
    'alerts.followedProfile.deletedBanned.bodyReason': '{profile} a fost sters, eliminat din lista ta de urmariri si nu poate fi urmarit momentan din motive de drepturi sau gestionare continut. Motiv: {reason}',
    'alerts.followedProfile.removed.title': 'Profil urmarit eliminat',
    'alerts.followedProfile.removed.body': '{profile} a fost sters si eliminat din lista ta de urmariri din motive de drepturi sau gestionare continut.',
    'alerts.followedProfile.removed.bodyReason': '{profile} a fost sters si eliminat din lista ta de urmariri din motive de drepturi sau gestionare continut. Motiv: {reason}',
  },
  es: {
    'app.logout': 'Salir',
    'app.language': 'Idioma',
    'beta.badge': 'Beta',
    'beta.copy': 'Aun estamos en beta. Los matches son sugerencias: utiles para encontrar un estilo parecido, no copias exactas.',
    'copyright.source': 'Fuente: {source}',
    'copyright.sourceFallback': 'post social',
    'copyright.preview': 'Usamos la preview para encontrar alternativas similares. Los derechos siguen siendo de sus propietarios.',
    'copyright.takedown': 'Quieres retirarla?',
    'betaInvite.title': 'Invita a un amigo',
    'betaInvite.copy': '¿Conoces a alguien que guarda outfits, sigue creators o busca productos similares? Envíale Outfit Detective.',
    'betaInvite.whatsapp': 'WhatsApp',
    'betaInvite.email': 'Email',
    'betaInvite.copyLink': 'Copiar link',
    'betaInvite.native': 'Compartir',
    'betaInvite.shareTitle': 'Prueba Outfit Detective Beta',
    'betaInvite.shareText': 'Estoy probando Outfit Detective Beta: un wall personal de looks sociales, productos similares y try-on virtual en tus fotos. Aún está en beta, pero la idea es fuerte.',
    'share.copy': 'Copiar link',
    'share.copyDone': 'Copiado',
    'share.copyToast': 'Link copiado',
    'share.channel.whatsapp': 'WhatsApp',
    'share.channel.email': 'Email',
    'share.channel.native': 'Compartir',
    'share.channel.pinterest': 'Pinterest',
    'shareLook.kicker': 'Invitación beta',
    'shareLook.cardTitle': 'Comparte este look',
    'shareLook.cardCopy': 'Envía el look a un amigo o invítalo a probar Outfit Detective Beta.',
    'shareLook.button': 'Compartir look',
    'shareLook.title': 'Look de {celebrity} en Outfit Detective',
    'shareLook.text': 'Encontré este look de {celebrity} en Outfit Detective Beta: looks sociales, productos similares y try-on virtual.',
    'authPrompt.kicker': 'Mini login',
    'authPrompt.title': 'Haz tuyo este fit',
    'authPrompt.copy': 'Pon tu email y te mandamos un codigo de un solo uso. Sin password, sin registro pesado.',
    'authPrompt.signIn': 'Entrar',
    'authPrompt.sendCode': 'Enviar codigo',
    'authPrompt.verify': 'Continuar',
    'authPrompt.tryOnTitle': 'Mira cómo te queda',
    'authPrompt.tryOnCopy': 'Entra para mantener tus fotos privadas.',
    'authPrompt.matchTitle': 'Vamos a buscar el look',
    'authPrompt.matchCopy': 'Entra un momento y lo buscamos para ti.',
    'authPrompt.photosTitle': 'Tus fotos',
    'authPrompt.photosCopy': 'Guarda aquí las fotos donde quieres probar looks.',
    'authPrompt.savedTitle': 'Guarda los looks buenos',
    'authPrompt.savedCopy': 'Entra para no perderlos.',
    'authPrompt.followTitle': 'Hazlo más tuyo',
    'authPrompt.followCopy': 'Sigue a la gente que inspira tu estilo.',
    'publicIntro.tagline': 'Looks sociales publicos · Alternativas similares · Fit-on-me privado',
    'publicIntro.imageAlt': 'Idea outfit',
    'publicIntro.demoLabel': 'Demo de look real a fit-on-me',
    'publicIntro.sourceAlt': 'Outfit publico original de inspiracion',
    'publicIntro.resultAlt': 'Preview fit-on-me',
    'publicIntro.mediaSource': 'fit inspo',
    'publicIntro.mediaResult': 'en mi',
    'publicIntro.mediaCelebrities': 'Looks publicos',
    'publicIntro.mediaSimilar': 'Ropa similar',
    'publicIntro.mediaTryOn': 'Ideas try-on',
    'publicIntro.kicker': 'Vista publica',
    'publicIntro.title': 'Pon el look en tu foto',
    'publicIntro.copy': 'Elige un look publico y Outfit Detective te muestra como podria verse en ti con ropa similar.',
    'publicIntro.howCopy': 'Sigues perfiles publicos de Instagram o TikTok, abres un look y lo pruebas en tu foto.',
    'publicIntro.featuresLabel': 'Funciones Outfit Detective',
    'publicIntro.pillCelebrities': 'Posts publicos',
    'publicIntro.pillProducts': 'Productos similares',
    'publicIntro.pillTryOn': 'Ideas virtual try-on',
    'publicIntro.primaryCta': 'Probar este fit',
    'publicIntro.secondaryCta': 'Ver looks publicados por otros',
    'how.metaTitle': 'Como funciona Outfit Detective',
    'how.metaDescription': 'Pruébate looks publicos de Instagram y TikTok, encuentra alternativas similares para recrear el fit y mantén privadas las previews fit-on-me.',
    'how.kicker': 'Como funciona',
    'how.title': 'De un look social publico a un fit que puedes probarte.',
    'how.copy': 'Outfit Detective lee pistas visuales en looks publicos de Instagram y TikTok, separa las prendas, encuentra alternativas similares y te ayuda a probar el vibe en tus propias fotos.',
    'how.primaryCta': 'Empezar con email',
    'how.secondaryCta': 'Ver Fit Wall',
    'how.demoLabel': 'Demo generada del flujo Outfit Detective',
    'how.demoImageAlt': 'Demo generada de Outfit Detective con prendas detectadas',
    'how.demoDetected': 'Prendas detectadas',
    'how.demoPieces': 'Top, chaqueta, pantalones, zapatos y accesorios',
    'how.demoMatch': 'Estilo similar',
    'how.demoMatchTitle': 'Alternativas visuales mas cercanas',
    'how.demoMatchMeta': 'Color, forma y vibe alineados',
    'how.promiseLabel': 'Que hace Outfit Detective',
    'how.promise.1.value': '01',
    'how.promise.1.label': 'Lee el fit desde un post publico de Instagram o TikTok',
    'how.promise.2.value': '02',
    'how.promise.2.label': 'Encuentra alternativas similares sin saber la marca',
    'how.promise.3.value': '03',
    'how.promise.3.label': 'Prueba el look en privado en tus fotos',
    'how.stepsKicker': 'El flujo',
    'how.stepsTitle': 'Tres pasos para recrear un look publico con buenos limites.',
    'how.steps.1.title': 'Trae el look',
    'how.steps.1.copy': 'Outfit Detective parte de looks publicados por creators en Instagram y TikTok. La app se centra en el outfit y las señales de styling, no en datos innecesarios.',
    'how.steps.2.title': 'Deja que AI divida el fit',
    'how.steps.2.copy': 'Detectamos prendas visibles, colores, silueta y señales de styling, y lo convertimos en un breakdown claro.',
    'how.steps.3.title': 'Haz algo con el',
    'how.steps.3.copy': 'Explora productos similares, guarda el look, compartelo o crea una preview fit-on-me.',
    'how.showcaseBadge': 'Preview fit-on-me',
    'how.showcaseTitle': 'Sube tu foto y mira una preview privada del look de creator en ti.',
    'how.featureKicker': 'Que hace',
    'how.featureTitle': 'Un buscador de estilo con algo de gusto.',
    'how.feature.find.title': 'Encontrar ropa similar',
    'how.feature.find.copy': 'Busca por como se ve el outfit, aunque no conozcas la marca original.',
    'how.feature.explain.title': 'Entender el look',
    'how.feature.explain.copy': 'Ve prendas y señales de styling en vez de una lista plana de productos aleatorios.',
    'how.feature.try.title': 'Fit on me',
    'how.feature.try.copy': 'Usa tus fotos privadas para crear una preview inspirada en el outfit elegido.',
    'how.feature.save.title': 'Guardar y comparar',
    'how.feature.save.copy': 'Manten juntos looks, ideas de producto y previews try-on para decidir despues.',
    'how.trustKicker': 'Limites claros',
    'how.trustTitle': 'Util, pero sin falsa certeza.',
    'how.trustCopy': 'Outfit Detective sugiere estilos visualmente similares sin fingir que cada producto es exacto o aprobado por un creator.',
    'how.trust.1': 'Las alternativas similares son sugerencias, no productos exactos garantizados.',
    'how.trust.2': 'Las fotos fit-on-me siguen siendo personales y sirven para tu preview.',
    'how.trust.3': 'Cuando es posible, la app explica señales como color, silueta y categoria.',
    'how.finalKicker': 'Listo cuando aparece la curiosidad',
    'how.finalTitle': 'La proxima vez que un look frene tu scroll, hazlo util.',
    'how.finalCopy': 'Convierte un look publico de creator en breakdown, alternativas similares e idea fit-on-me privada mientras la inspiracion sigue fresca.',
    'how.finalCta': 'Empezar beta',
    'how.finalSecondary': 'Explorar looks publicos',
    'app.photos': 'Mis fotos',
    'app.profile': 'Perfil',
    'app.menu': 'Menú',
    'common.follow': 'Seguir',
    'common.unfollow': 'Dejar de seguir',
    'common.remove': 'Eliminar',
    'profile.followingStatus': 'Siguiendo',
    'profile.notFollowingStatus': 'No seguido',
    'common.open': 'Abrir',
    'common.preview': 'Vista previa',
    'common.cancel': 'Cancelar',
    'followConfirm.title': '¿Dejar de seguir?',
    'followConfirm.copy': '{name} saldrá de tu lista y de tu feed personal.',
    'followConfirm.confirming': 'Dejando de seguir...',
    'login.heroTitle': 'Pruébate los looks que ves en Instagram y TikTok.',
    'login.heroSubtitle': 'Sigue looks publicos de creators, mantén privadas las previews fit-on-me y encuentra alternativas similares para recrear el fit.',
    'login.pillCelebs': 'Fresh drops',
    'login.pillDetect': 'Fit radar',
    'login.pillShop': 'Alternativas similares',
    'login.proofFast': 'Nuevos drops',
    'login.proofFastLabel': 'looks cuando salen',
    'login.proofSources': 'Instagram + TikTok',
    'login.proofSourcesLabel': 'juntos en un feed',
    'login.proofPersonal': 'Tuyo',
    'login.proofPersonalLabel': 'solo tus follows',
    'login.kicker': 'Tu style feed',
    'login.formTitle': 'Entra en Outfit Detective',
    'login.formSubtitle': 'Introduce tu email y te enviamos un codigo unico para entrar.',
    'login.email': 'Tu email',
    'login.emailPlaceholder': 'nombre@email.com',
    'login.title': 'Entra en Outfit Detective',
    'login.subtitle': 'Sigue los perfiles que te gustan y guarda los mejores looks en tu feed personal.',
    'login.sendCode': 'Enviar codigo unico',
    'login.sending': 'Enviando...',
    'login.otp': 'Pon el código',
    'login.changeEmail': 'Cambiar email',
    'login.verify': 'Continuar',
    'login.verifying': 'Verificando...',
    'login.codeSent': 'Mira tu correo. El código sigue fresh durante {minutes} minutos. Si no lo ves, revisa también spam o promociones.',
    'login.pendingOtpRestored': 'El código que te enviamos todavía sirve.',
    'login.pendingOtpExpired': 'Ese código expiró. Pide uno nuevo para continuar.',
    'login.useReceivedCode': 'Ya tengo un código',
    'login.resendCode': 'Enviarme otro código',
    'feed.yourList': 'Tu lista',
    'feed.findProfiles': 'Buscar perfiles',
    'feed.searchList': 'Buscar en tu lista',
    'feed.noListSearchResults': 'No hay perfiles con esa búsqueda.',
    'feed.noProfiles': 'Todavía no hay perfiles.',
    'feed.heroTitle': 'Looks nuevos de los perfiles que sigues.',
    'feed.heroEmpty': 'Sigue perfiles que te interesan para crear tu feed de outfits.',
    'feed.heroCount': 'Sigues {count} {label}.',
    'feed.emptyTitle': 'El feed está vacío',
    'feed.emptyCopy': 'Añade perfiles desde Discover para crear tu feed personal.',
    'feed.popularFallbackTitle': 'Looks populares mientras preparamos tu feed',
    'feed.popularFallbackCopy': 'Sigue perfiles para hacer este feed tuyo. Mientras tanto, mostramos looks populares.',
    'feed.popularFallbackCta': 'Encuentra tus style crushes',
    'feed.personalPendingCopy': 'Estamos preparando el contenido de los perfiles que sigues. Los nuevos looks apareceran aqui automaticamente.',
    'feed.suggested': 'Sugeridos',
    'feed.news': 'Novedades',
    'feed.aiAction': 'Recrear este look',
    'aiDialog.kicker': 'Outfit Detective AI',
    'aiDialog.title': 'Compra el look con matches mas claros.',
    'aiDialog.copy': 'Leemos el post, detectamos las piezas y ordenamos matches por vibe y presupuesto.',
    'aiDialog.tagsLabel': 'Detalles del match',
    'aiDialog.tagThreeLanes': '3 rangos precio',
    'aiDialog.tagSmartMatch': 'vibe primero',
    'aiDialog.laneExactTitle': 'Casi la pieza real',
    'aiDialog.laneExactCopy': 'Solo aparece cuando marca, modelo, categoria y pistas visuales coinciden con alta confianza.',
    'aiDialog.laneSimilarTitle': 'Misma energia',
    'aiDialog.laneSimilarCopy': 'Piezas cercanas al estilo del post, mood de marca y silueta.',
    'aiDialog.laneBudgetTitle': 'Smart save',
    'aiDialog.laneBudgetCopy': 'El look, sin susto para la cartera.',
    'aiDialog.laneLuxuryTitle': 'Luxury lane',
    'aiDialog.laneLuxuryCopy': 'Finds premium cuando el fit merece presupuesto main character.',
    'aiDialog.loadingTitle': 'Detectando el fit',
    'aiDialog.loadingCopy': 'Puedes cerrar este pop-up. Seguimos en background; cuando vuelvas aqui o al post, los finds estaran cuando esten listos.',
    'aiDialog.loadingPageCopy': 'Seguimos en background. Quedate aqui o vuelve luego: los finds apareceran en esta pagina cuando esten listos.',
    'aiDialog.liveTitle': 'Busqueda aun activa',
    'aiDialog.liveCopy': 'Ya hay algunos finds. Puedes cerrar; seguimos actualizando en background.',
    'aiDialog.livePageCopy': 'Ya hay algunos finds. Seguimos actualizando esta pagina en background.',
    'aiDialog.readyTitle': 'Matches listos',
    'aiDialog.readyCopy': 'Abre lo que te guste. Afinamos el ranking despues de los primeros tests.',
    'aiDialog.errorTitle': 'Match fallido',
    'aiDialog.errorCopy': 'Este look no ha hecho match ahora. Prueba otra vez en un momento.',
    'aiDialog.retry': 'Probar otra vez',
    'aiDialog.emptyTitle': 'Aun sin match limpio',
    'aiDialog.emptyCopy': 'La imagen no era bastante clara para resultados shopping utiles.',
    'aiDialog.noLaneResults': 'Aun no hay finds fuertes en este rango.',
    'aiDialog.lookMatch': 'look match',
    'aiDialog.vibeMatch': 'vibe match',
    'aiDialog.openLook': 'Fit source',
    'aiDialog.close': 'Cerrar dialog AI',
    'discover.title': 'Busca a quien quieres seguir.',
    'discover.subtitle': 'Encuentra perfiles de Instagram o TikTok y añádelos a tu lista personal.',
    'discover.added': '{name} se ha añadido a tu lista.',
    'discover.pasteTitle': 'Pegar un perfil',
    'discover.openBulk': 'Importar más perfiles',
    'discover.yourList': 'Tu lista',
    'discover.noFollowingTitle': 'Todavía no hay perfiles',
    'discover.inFeed': 'En tu feed',
    'discover.followersUnavailable': 'Followers no disponibles',
    'discover.followError': 'No puedo seguir este perfil ahora. Prueba de nuevo pronto.',
    'discover.blockedKicker': 'Aviso de derechos',
    'discover.blockedTitle': 'Este perfil no se puede seguir',
    'discover.blockedCopy': 'Por motivos de derechos, {name} no está disponible para seguirlo ni para usarlo en tu feed personal en Outfit Detective.',
    'discover.blockedReference': 'Referencia',
    'discover.blockedClose': 'Entendido',
    'detail.back': 'Volver al feed',
    'detail.aiTitle': 'Matches shopping para este look',
    'detail.aiEmptyTitle': 'Aun no hay matches guardados',
    'detail.aiEmptyCopy': 'Inicia el match AI para detectar piezas y ver rangos shopping.',
    'feed.openDetail': 'Abrir look',
    'photos.kicker': 'Fotos try-on',
    'photos.title': 'Mis fotos',
    'photos.copy': 'Ten a mano unas fotos claras para ver los looks directamente en ti.',
    'photos.uploadTitle': 'Añadir una foto',
    'photos.uploadCopy': 'Los mejores resultados salen con fotos luminosas de cuerpo completo.',
    'photos.usageNoteBefore': 'Usamos estas fotos solo para crear tus',
    'photos.usageNoteAfter': 'personales. Elige fotos claras de cuerpo completo para transferir mejor la ropa; puedes eliminarlas cuando quieras.',
    'photos.pickFile': 'Elegir una foto',
    'photos.optimizedBeforeUpload': 'Lista para tu try-on',
    'photos.titlePlaceholder': 'Nombre de la foto',
    'photos.upload': 'Subir foto',
    'photos.uploading': 'Subiendo...',
    'photos.uploaded': 'Foto guardada.',
    'photos.processingConsent': 'Acepto que esta foto se procese con nuestros sistemas AI y OpenAI para generar try-ons. La foto sigue siendo privada.',
    'photos.emptyTitle': 'Todavía no hay fotos',
    'photos.emptyCopy': 'Sube una foto de cuerpo completo para obtener mejores previews try-on.',
    'photos.default': 'Predeterminada',
    'photos.makeDefault': 'Usar como predeterminada',
    'photos.framing.full_body': 'Cuerpo completo',
    'photos.framing.upper_body': 'Parte superior',
    'photos.framing.portrait': 'Retrato',
    'photos.framing.unknown': 'No seguro',
    'photos.error.image_too_large': 'La imagen optimizada sigue siendo demasiado grande. Prueba otra foto.',
    'photos.error.photo_processing_consent_required': 'Confirma el consentimiento de procesamiento de la foto antes de subirla.',
    'photos.error.photo_safety_rejected': 'Esta foto no se puede usar para el try-on. Sube una foto segura con una persona adulta vestida.',
    'photos.error.photo_safety_check_failed': 'No pudimos completar la revisión de seguridad de la foto ahora. Inténtalo de nuevo en un momento.',
    'account.kicker': 'Perfil',
    'account.title': 'Tu perfil Outfit Detective',
    'account.copy': 'Gestiona fotos y herramientas personales.',
    'account.overview': 'Resumen',
    'account.tryonTitle': 'Listo para try-on',
    'account.tryonCopy': 'Tus fotos alimentan previews personales desde los looks que descubres.',
    'account.privacyTitle': 'Tú controlas',
    'account.privacyCopy': 'No necesitamos tus datos personales para que la app sea útil. Estos ajustes son opcionales, editables en cualquier momento y se usan solo para ajustar tu experiencia.',
    'account.legalLink': 'Ajustes de privacidad y legales',
    'account.shopAreaTitle': 'Tu zona fit radar',
    'account.shopAreaCopy': 'Outfit Detective busca en plataformas online y tiendas fisicas. Elige una zona base y daremos prioridad a matches bastante cerca para probar, recoger o recibir rapido.',
    'account.locationLabel': 'Zona base',
    'account.locationPlaceholder': 'Ciudad o zona, ej. Madrid, Chueca',
    'account.locationResults': 'Sugerencias de ubicacion',
    'account.locationSearching': 'Buscando lugares...',
    'account.locationNoResults': 'Aun sin match claro. Prueba ciudad mas pais.',
    'account.locationSelected': 'Zona verificada seleccionada',
    'account.radiusLabel': 'Radio de busqueda: {km} km',
    'account.radiusAria': 'Radio de busqueda',
    'account.locationPrivacy': 'No hace falta direccion exacta. Ciudad o barrio basta, y puedes borrarlo cuando quieras.',
    'account.styleGenderTitle': 'Estilo del wall',
    'account.styleGenderCopy': 'Elige que looks quieres ver primero en tu wall y perfiles sugeridos.',
    'account.styleGender.all': 'Todos',
    'account.styleGender.menswear': 'Hombres',
    'account.styleGender.womenswear': 'Mujeres',
    'account.styleGenderConfirm': 'Confirmo que quiero ver todos los looks en mi wall.',
    'account.styleGenderSummaryTitle': 'Preferencia del wall',
    'account.styleGenderSummary.all': 'Tu wall y sugeridos pueden incluir cualquier estilo.',
    'account.styleGenderSummary.menswear': 'Tu wall y sugeridos priorizan hombres y looks menswear.',
    'account.styleGenderSummary.womenswear': 'Tu wall y sugeridos priorizan mujeres y looks womenswear.',
    'account.identityTitle': 'Como apareces',
    'account.identityCopy': 'Para cosas publicas como Fit Wall y try-ons compartidos, elige nombre real o quedate low-key con nickname. Tu email queda backstage.',
    'account.useNickname': 'Nickname',
    'account.useRealName': 'Nombre real',
    'account.realNameLabel': 'Nombre real',
    'account.realNamePlaceholder': 'Visible solo si eliges nombre real',
    'account.nicknameLabel': 'Nickname',
    'account.nicknamePlaceholder': 'Tu nombre publico de estilo',
    'account.useSuggestion': 'Usar {nickname}',
    'account.save': 'Guardar perfil',
    'account.saving': 'Guardando...',
    'account.saved': 'Perfil guardado.',
    'account.areaSummaryTitle': 'Prioridad shopping',
    'account.areaSummarySet': 'Prioridad a matches online y tiendas en {km} km de {location}.',
    'account.areaSummaryEmpty': 'Sin zona todavia. Mantendremos resultados amplios y online-first.',
    'account.publicSummaryTitle': 'Nombre publico',
    'account.publicSummaryCopy': 'Los try-ons publicos apareceran como {name}.',
    'account.deleteTitle': 'Eliminar cuenta',
    'account.deleteCopy': 'Esto suspende tu cuenta ahora. Tienes 15 días para reactivarla iniciando sesión otra vez; después eliminamos permanentemente tu perfil, fotos, try-ons, follows, preferencias y sesiones.',
    'account.deleteConfirmLabel': 'Escribe DELETE para confirmar',
    'account.deleteConfirmMismatch': 'Escribe DELETE exactamente para confirmar la eliminación de la cuenta.',
    'account.deleteButton': 'Eliminar mi cuenta',
    'account.deleting': 'Programando eliminación...',
    'account.deleteError': 'No se pudo programar la eliminación de la cuenta ahora.',
    'account.error.nickname_required': 'Elige un nickname para continuar.',
    'account.error.display_name_required': 'Añade un nombre real o cambia a nickname.',
    'account.error.request_failed': 'No se pudo guardar ahora. Inténtalo de nuevo.',
    'account.error.api_unreachable': 'API no disponible ahora.',
    'account.error.profile_not_found': 'Perfil no encontrado.',
    'tryon.kicker': 'Try the vibe on me',
    'tryon.title': 'Ver el look en tu foto',
    'tryon.ctaTitle': 'Ver este fit en mí',
    'tryon.ctaCopy': 'Elige una foto y mira el outfit en ti. Fit check al momento.',
    'tryon.open': 'Probar',
    'tryon.hide': 'Ocultar',
    'tryon.existingKicker': 'Ya generado',
    'tryon.existingTitle': 'Ya tienes try-ons para este look',
    'tryon.existingCopy': 'Puedes reutilizar uno de estos resultados o crear una preview nueva con otra foto.',
    'tryon.originalPost': 'Post original',
    'tryonGallery.openEvent': 'Fit source',
    'tryonShare.button': 'Vistete como {celebrity}',
    'tryonShare.title': 'Vistete como {celebrity}',
    'tryonShare.text': 'Vistete como {celebrity} con Outfit Detective.',
    'tryonShare.chooseChannel': 'Compartir',
    'tryonShare.facebook': 'Compartir en Facebook',
    'tryonShare.pinterest': 'Compartir en Pinterest',
    'tryonShare.email': 'Compartir por email',
    'tryonShare.whatsapp': 'Compartir en WhatsApp',
    'tryonShare.x': 'Compartir en X',
    'tryonShare.copied': 'Texto copiado',
    'tryonShare.copyFailed': 'No se puede abrir compartir aqui. Prueba desde el menu del navegador.',
    'tryonShare.celebrityFallback': 'este creator',
    'tryon.newPreviewTitle': 'Crear nueva preview',
    'tryon.newPreviewCopy': 'Elige la foto que usaremos como referencia de tu cuerpo.',
    'tryon.error.photo_framing_not_enough': 'Este look necesita una foto de cuerpo completo.',
    'login.legalPrefix': 'Al iniciar sesión aceptas nuestra',
    'login.success': 'Has iniciado sesión.',
    'login.legalLink': 'Política de privacidad',
    'login.legalAnd': 'y los',
    'login.termsLink': 'Términos del servicio',
    'app.saved': 'Guardados',
    'welcome.preferencesLabel': 'Preferencias de estilo',
    'welcome.pref.budget': 'Budget',
    'welcome.pref.street': 'Street',
    'welcome.pref.minimal': 'Minimal',
    'welcome.pref.luxury': 'Luxury',
    'welcome.pref.everyday': 'Everyday',
    'feed.save': 'Guardar look',
    'feed.unsave': 'Quitar de guardados',
    'feed.share': 'Compartir look',
    'feed.tryOn': 'Probar en mí',
    'feed.shareTitle': 'Look de {name}',
    'feed.shareText': 'Encontré este look: {title}',
    'feed.shareFallback': 'Look Outfit Detective',
    'feed.shareFailed': 'No se pudo copiar el enlace para compartir.',
    'feed.badgeFresh': 'nuevo de tus follows',
    'feed.badgePopular': 'popular ahora',
    'feed.badgeMatched': '{count} piezas encontradas',
    'feed.starterKicker': 'Empieza aquí',
    'feed.starterTitle': 'Haz tuyo el feed en tres taps.',
    'feed.starterFollow': 'Seguir perfiles',
    'feed.starterMatch': 'Comprar el look',
    'feed.starterSave': 'Guardar o compartir',
    'feed.starterCta': 'Buscar perfiles',
    'aiDialog.best': 'Mejor opción',
    'aiDialog.bestPick': 'Mejor opción · {price}',
    'aiDialog.quick.budget': 'Budget desde {price}',
    'aiDialog.quick.similar': 'Similar desde {price}',
    'aiDialog.quick.luxury': 'Luxury desde {price}',
    'lookShare.open': 'Crear story card',
    'lookShare.title': 'Story card',
    'lookShare.total': 'Look encontrado por unos {total}',
    'lookShare.noTotal': 'Look encontrado con Outfit Detective',
    'lookShare.download': 'Descargar story card',
    'lookShare.hint': 'Úsala como portada de Reel/Story o después de grabar la pantalla.',
    'similarRail.kicker': 'Shopping visual',
    'similarRail.title': 'Comprar productos similares',
    'similarRail.emptyCopy': 'Activa Fit AI para abrir una fila visual de matches limpios para este look.',
    'similarRail.loading': 'Buscando matches visuales limpios...',
    'similarRail.start': 'Encontrar productos similares',
    'similarRail.showAll': 'Ver todos los matches',
    'similarRail.showMore': 'Mostrar más',
    'similarRail.lane.exact': 'Exacto',
    'similarRail.lane.similar': 'Similar',
    'similarRail.lane.budget': 'Budget',
    'similarRail.lane.luxury': 'Luxury',
    'productInfo.kicker': 'Match de producto',
    'productInfo.price': 'Precio',
    'productInfo.match': 'Prenda matcheada',
    'productInfo.matchFallback': 'Este look',
    'productInfo.copy': 'Quédate aquí: revisa la pieza y luego prueba todo el fit en tu foto.',
    'productInfo.tryOn': 'Probar el fit',
    'productInfo.openShop': 'Abrir shop',
    'saved.kicker': 'Tu radar outfit',
    'saved.title': 'Looks guardados',
    'saved.emptyTitle': 'Aún no hay looks guardados',
    'saved.emptyCopy': 'Guarda los looks que quieras revisar, compartir o probar más tarde.',
    'saved.emptyCta': 'Volver al feed',
    'saved.open': 'Abrir look',
    'saved.remove': 'Quitar',
    'publicTryOn.ctaCreate': 'Crear tu versión',
    'publicTryOn.ctaFind': 'Encontrar looks similares',
    'app.feed': 'Feed',
    'app.discover': 'Descubrir',
    'app.fitWall': 'Fit Wall',
    'nav.feed': 'Feed',
    'nav.fitWall': 'Wall',
    'nav.photos': 'Fotos',
    'nav.discover': 'Explorar',
    'nav.help': 'Ayuda',
    'app.legal': 'Legal',
    'common.loading': 'Cargando...',
    'common.previous': 'Anterior',
    'common.next': 'Siguiente',
    'feed.following': 'Siguiendo',
    'feed.apiError': 'API no disponible: {error}',
    'feed.loading': 'Cargando feed...',
    'feed.noSuggestions': 'No hay sugerencias por ahora.',
    'discover.removeError': 'No se pudo actualizar ahora.',
    'discover.loadMore': 'Cargar más',
    'discover.loadingMore': 'Cargando...',
    'detail.loading': 'Cargando look...',
    'detail.priceUnavailable': 'Precio no disponible',
    'detail.refreshAi': 'Actualizar Fit AI',
    'aiDialog.resultCount': '{count} matches de producto',
    'aiDialog.error.ai_matching_disabled': 'El matching AI aún no está activo.',
    'aiDialog.error.event_image_missing': 'Este post no tiene imagen para analizar.',
    'aiDialog.error.no_items_detected': 'El fit no era suficientemente claro para detectar piezas útiles.',
    'aiDialog.error.auth_required': 'Este match aun no esta en cache. Entra para lanzar una busqueda nueva.',
    'shareCard.generating': 'Generando...',
    'shareCard.downloadError': 'No se pudo crear la tarjeta.',
    'tryon.copy': 'Elige una foto tuya y crea una preview personal inspirada en este fit.',
    'tryon.needMatchTitle': 'Primero activa Fit AI',
    'tryon.needMatchCopy': 'Necesitamos las piezas detectadas antes de generar la preview try-on.',
    'tryon.startFitAi': 'Activar Fit AI',
    'tryon.viewFitAiStatus': 'Ver estado Fit AI',
    'tryon.noPhotosTitle': 'Primero añade una foto',
    'tryon.noPhotosCopy': 'Sube una foto de cuerpo completo para que la preview tenga contexto suficiente.',
    'tryon.generateShop': 'Generar try-on',
    'tryon.generateReplica': 'Generar replica',
    'tryon.queueing': 'Iniciando...',
    'tryon.notifyEmail': 'Avísame por email cuando esté listo',
    'tryon.notifyEmailOnCopy': 'Actívalo y puedes salir de esta pantalla en vez de esperar aquí.',
    'tryon.notifyEmailOffCopy': 'Sin aviso por email. Deja Mis try-ons abierto o vuelve más tarde.',
    'tryon.modeLabel': 'Tipo de preview',
    'tryon.modeTryonTitle': 'El try-on real',
    'tryon.modeTryonName': 'Construye el fit carrito',
    'tryon.modeTryonCopy': 'Elige las piezas shop. Lo que saltes se queda del look original.',
    'tryon.cartUnavailableKicker': 'Opción fit carrito',
    'tryon.cartUnavailableTitle': 'Construye el fit carrito después del match de productos',
    'tryon.cartUnavailableCopy': 'Primero buscamos productos similares para este look. Luego puedes elegir piezas shop y mezclarlas con el fit original.',
    'tryon.cartUnavailableCta': 'Encontrar productos similares',
    'tryon.cartUnavailableLoading': 'Buscando productos...',
    'tryon.modeReplicaTitle': 'La replica limpia',
    'tryon.modeReplicaName': 'Copiar el fit original',
    'tryon.modeReplicaCopy': 'Usa el post como referencia completa. Sin carrito, solo vibe clonado.',
    'tryon.shopBuilderTitle': 'Elige las piezas que realmente quieres',
    'tryon.shopBuilderCopy': 'Selecciona un producto por pieza. Las piezas omitidas se quedan del look original.',
    'tryon.selectedCount': '{count} elegidos',
    'tryon.totalUnavailable': 'Total variable',
    'tryon.shopSelected': 'Usamos tu pieza shop',
    'tryon.shopOriginalFallback': 'No elegido: se queda la pieza original',
    'tryon.shopNoProducts': 'Aún no hay producto útil para esta pieza.',
    'tryon.shopResponsibleCopy': 'Precios y disponibilidad vienen de las tiendas y pueden cambiar. Esto es una preview AI, no una garantía de ajuste.',
    'tryon.selectProductsFirstTitle': 'Elige al menos una pieza',
    'tryon.selectProductsFirstCopy': 'Selecciona un producto shop arriba, luego podrás elegir tu foto y generar el try-on.',
    'tryon.selectedProductsTitle': 'Piezas elegidas: {total}',
    'tryon.newPreviewShopCopy': 'Elige tu foto: mezclamos los productos elegidos con el fit original.',
    'tryon.newPreviewReplicaCopy': 'Elige tu foto y replicamos el look original de la celebrity.',
    'tryon.pendingExistingCopy': 'Ya hay una preview generándose para este look. Cuando esté lista la verás en Mis try-ons.',
    'tryon.queuedTitle': 'Preview en generación',
    'tryon.queuedEmailTitle': 'Aviso por email activado',
    'tryon.pendingEmailOn': 'Te enviaremos un email cuando la imagen esté lista y la guardaremos en Mis try-ons.',
    'tryon.pendingEmailOff': 'No enviaremos email. Puedes cerrar: la preview aparecerá en Mis try-ons.',
    'tryon.readyNoticeTitle': 'Tu try-on esta listo',
    'tryon.readyNoticeCopy': 'La preview termino y esta guardada en Mis try-ons.',
    'tryon.readyNoticeCta': 'Ver mis try-ons',
    'tryon.readyNoticeStay': 'Quedarme aqui',
    'tryon.openTryons': 'Abrir mis try-ons',
    'tryon.resultAlt': 'Preview try-on',
    'tryon.error.ai_match_required': 'Activa Fit AI antes de probar el look.',
    'tryon.error.selected_products_required': 'Elige al menos una pieza shop o cambia a Replica.',
    'tryon.error.try_on_input_safety_rejected': 'No se puede generar este try-on porque una imagen de referencia no pasó la revisión de seguridad.',
    'tryon.error.try_on_output_safety_rejected': 'La imagen generada no pasó la revisión de seguridad, así que no la guardamos.',
    'tryon.error.try_on_safety_check_failed': 'No pudimos completar la revisión de seguridad ahora. Inténtalo de nuevo en un momento.',
    'tryon.error.generic': 'La preview no funcionó ahora. Inténtalo de nuevo en un momento.',
    'tryonGallery.originalPost': 'Post original',
    'tryonGallery.userPhoto': 'Tu foto',
    'tryonGallery.status.running': 'En curso',
    'tryonGallery.status.completed': 'Listo',
    'tryonGallery.status.failed': 'Fallido',
    'tryonShare.shopTitle': 'Look como {celebrity} por unos {total}',
    'tryonShare.shopText': 'Construí un fit inspirado en {celebrity} por unos {total} con Outfit Detective.',
    'tryonFeed.creatorFallback': 'Creator Fit Wall',
    'publicTryOn.kicker': 'Try-on compartido',
    'publicTryOn.title': 'Vístete como {celebrity}',
    'publicTryOn.copy': '{creator} creó este try-on AI con Outfit Detective.',
    'publicTryOn.shopCopy': '{creator} construyó este try-on listo para carrito con piezas seleccionadas por unos {total}.',
    'publicTryOn.ctaKicker': 'Cero líos, más señal de estilo',
    'publicTryOn.ctaTitle': 'Encuentra el look antes de que el algoritmo lo entierre.',
    'publicTryOn.ctaCopy': 'Sigue creators, guarda fits frescos, prueba el vibe en tus fotos y encuentra piezas similares.',
    'publicTryOn.cta': 'Crear mi fit radar',
    'publicTryOn.missingTitle': 'Este try-on ya no está disponible',
    'publicTryOn.missingCopy': 'El enlace puede ser incorrecto o la preview ya no está disponible.',
    'common.refresh': 'Actualizar',
    'common.on': 'Activo',
    'common.off': 'Inactivo',
    'common.profile': 'perfil',
    'common.profiles': 'perfiles',
    'welcome.kicker': 'Ya estás dentro',
    'welcome.title': 'Construye un feed que entiende el fit.',
    'welcome.copy': 'Sigue looks publicos de creators, prueba el fit en privado en tus fotos y compara alternativas similares en luxury, mid y budget.',
    'welcome.pointFeedTitle': 'Tus perfiles',
    'welcome.pointFeedCopy': 'Elige los perfiles. Nosotros mantenemos el feed limpio y personal.',
    'welcome.pointDetectTitle': 'Fit radar',
    'welcome.pointDetectCopy': 'Convertimos posts publicos en pistas outfit útiles.',
    'welcome.pointBudgetTitle': 'Rangos de precio',
    'welcome.pointBudgetCopy': 'Vibe similar, desde grails luxury hasta finds accesibles.',
    'welcome.cta': 'Buscar perfiles',
    'welcome.secondary': 'Ver el feed',
    'welcome.dismiss': 'Cerrar bienvenida',
    'welcome.legalPrefix': 'Al continuar aceptas nuestra',
    'welcome.legalAnd': 'y los',
    'followRefresh.kicker': 'Bienvenido de vuelta',
    'followRefresh.title': 'Vamos a refrescar tus seguidos.',
    'followRefresh.copy': 'Mientras no estabas, pausamos algunas comprobaciones en segundo plano, así que algunos perfiles seguidos pueden no estar del todo actualizados.',
    'followRefresh.cta': 'Actualizar mis seguidos',
    'followRefresh.refreshing': 'Actualizando...',
    'followRefresh.secondary': 'Ahora no',
    'followRefresh.dismiss': 'Cerrar aviso',
    'followRefresh.success': 'Estamos actualizando {count} perfiles.',
    'followRefresh.fresh': 'Tus perfiles seguidos ya parecen actualizados.',
    'followRefresh.error': 'No pude iniciar la actualización ahora.',
    'feed.heroKicker': 'Feed fashion personal',
    'feed.cachedPosts': 'posts cacheados',
    'feed.images': 'imágenes',
    'feed.updated': 'Actualizado {date}.',
    'feed.workerReady': 'Worker listo para recoger nuevos looks.',
    'feed.checkingServices': 'Comprobando servicios...',
    'tryonGallery.title': 'Mis try-ons',
    'tryonGallery.emptyTitle': 'Aún no hay try-ons',
    'tryonGallery.emptyCopy': 'Crea tu primera preview desde un look.',
    'tryonGallery.untitled': 'Try-on sin título',
    'tryonGallery.created': 'Creado',
    'tryonGallery.email': 'Email',
    'tryonGallery.emailOn': 'Email activo',
    'tryonGallery.emailOff': 'Email inactivo',
    'tryonGallery.openGeneratedImage': 'Abrir imagen generada',
    'tryonGallery.openOriginalImage': 'Abrir imagen original',
    'tryonGallery.delete': 'Eliminar',
    'tryonGallery.deleting': 'Eliminando...',
    'tryonGallery.deleteConfirm': '¿Eliminar este try-on?',
    'tryonGallery.error.deleteFailed': 'No se pudo eliminar el try-on.',
    'tryonGallery.error.try_on_preview_not_found': 'Try-on no encontrado.',
    'tryonGallery.viewerLabel': 'Visor de imagen',
    'tryonGallery.zoomIn': 'Acercar',
    'tryonGallery.zoomOut': 'Alejar',
    'shareCard.title': 'Share card',
    'shareCard.download': 'Descargar tarjeta',
    'shareCard.hint': 'Guárdala y publícala en Stories o Reels.',
    'shareCard.openButton': 'Crear share card',
    'tryonFeed.kicker': 'Feed público try-on',
    'tryonFeed.title': 'Fit Wall',
    'tryonFeed.copy': 'Try-ons reales que la gente ha hecho públicos. Scrollea glow-ups, abre el post original e inspírate.',
    'tryonFeed.emptyTitle': 'El Fit Wall espera el primer drop',
    'tryonFeed.emptyCopy': 'Haz público uno de tus try-ons para iniciar el feed.',
    'tryonFeed.emptyCta': 'Abrir mis try-ons',
    'tryonFeed.fitCheck': 'Fit check',
    'tryonFeed.shareThis': 'Compartir este fit',
    'tryonFeed.rightTitle': 'Cómo llega un fit aquí',
    'tryonFeed.rightCopy': 'Los try-ons del Fit Wall los publican usuarios. Creas un look, lo activas como público y puedes retirarlo cuando quieras. Low-key con apodo o directo con tu nombre de perfil.',
    'tryonFeed.rightCta': 'Mis try-ons',
    'tryonFeed.rightBadge': 'AI try-on · público por elección',
    'tryon.generate': 'Generar preview',
    'tryon.generating': 'Generando...',
    'tryon.cartPresetLabel': 'Opciones automáticas del carrito',
    'tryon.cartPresetClosest': 'Más parecido',
    'tryon.cartPresetLuxury': 'Luxury',
    'tryon.cartPresetBudget': 'Budget',
    'tryon.typeSelectedProducts': 'Try-on',
    'tryon.typeReplica': 'Replica',
    'tryon.queuedCopy': 'Puedes cerrar esta ventana. El resultado aparecerá en Mis try-ons.',
    'tryon.queuedEmailCopy': 'Puedes cerrar esta ventana. Te avisaremos por email y lo guardaremos en Mis try-ons.',
    'tryonPublic.offTitle': 'Mantener privado',
    'tryonPublic.offCopy': 'Tu try-on se queda en tu espacio. Actívalo para publicarlo en el Fit Wall.',
    'tryonPublic.onTitle': 'Live en el Fit Wall',
    'tryonPublic.onCopy': 'Fit check público activo. Puedes retirarlo cuando cambie el vibe.',
    'tryonPublic.useName': 'Usar mi nombre de perfil',
    'tryonPublic.useNameCopy': 'La gente verá tu nombre de perfil en este post del Fit Wall.',
    'tryonPublic.aliasCopy': 'Mantenme low-key como {alias}.',
    'tryonPublic.aliasPending': 'un nombre fit aleatorio',
    'tryonPublic.error.try_on_not_ready': 'Este try-on debe estar listo antes de aparecer en el Fit Wall.',
    'tryonPublic.error.generic': 'No pude actualizar el uso compartido del Fit Wall ahora.',
    'legal.kicker': 'Legal',
    'legal.title': 'Documentos legales',
    'legal.copy': 'Información sobre privacidad, términos del servicio y solicitudes de datos.',
    'legal.privacyTitle': 'Política de privacidad',
    'legal.privacyCopy': 'Aviso sobre el tratamiento de datos personales.',
    'legal.termsTitle': 'Términos del servicio',
    'legal.termsCopy': 'Condiciones de uso del servicio.',
    'legal.cookiesTitle': 'Cookies y almacenamiento local',
    'legal.cookiesCopy': 'Almacenamiento técnico del navegador usado para login e idioma.',
    'legal.contactTitle': 'Contacto y solicitudes de datos',
    'legal.contactCopy': 'Cómo contactarnos por privacidad, derechos RGPD y soporte legal.',
    'legal.available': 'Disponible',
    'legal.backToApp': 'Volver a Outfit Detective',
    'alerts.tab': 'Alertas',
    'alerts.title': 'Alertas',
    'alerts.copy': 'Actualizaciones importantes sobre perfiles que sigues y actividad de tu cuenta.',
    'alerts.notice': 'Aviso',
    'alerts.emptyTitle': 'Aun no hay alertas',
    'alerts.emptyCopy': 'Cuando cambie algo importante, lo encontraras aqui.',
    'alerts.markRead': 'Marcar como leida',
    'alerts.emailSent': 'Email enviado: {date}',
    'alerts.error': 'No podemos cargar las alertas ahora.',
    'alerts.profileIncomplete.title': 'Completa tu perfil',
    'alerts.profileIncomplete.bodyBoth': 'Añade una zona shopping y confirma tu preferencia del wall para mejorar feed y sugeridos.',
    'alerts.profileIncomplete.bodyLocation': 'Añade una zona shopping para mejorar el feed y las sugerencias de productos.',
    'alerts.profileIncomplete.bodyStyleGender': 'Confirma tu preferencia de estilo del wall para mejorar el feed y los perfiles sugeridos.',
    'alerts.profileIncomplete.cta': 'Completar perfil',
    'alerts.followedProfile.fallbackName': 'Un perfil',
    'alerts.followedProfile.banned.title': 'Perfil seguido no disponible',
    'alerts.followedProfile.banned.body': '{profile} se elimino de tus seguidos y no se puede seguir ahora por motivos de derechos o gestion de contenido.',
    'alerts.followedProfile.banned.bodyReason': '{profile} se elimino de tus seguidos y no se puede seguir ahora por motivos de derechos o gestion de contenido. Motivo: {reason}',
    'alerts.followedProfile.deletedBanned.title': 'Perfil seguido eliminado y bloqueado',
    'alerts.followedProfile.deletedBanned.body': '{profile} se elimino, salio de tus seguidos y no se puede seguir ahora por motivos de derechos o gestion de contenido.',
    'alerts.followedProfile.deletedBanned.bodyReason': '{profile} se elimino, salio de tus seguidos y no se puede seguir ahora por motivos de derechos o gestion de contenido. Motivo: {reason}',
    'alerts.followedProfile.removed.title': 'Perfil seguido eliminado',
    'alerts.followedProfile.removed.body': '{profile} se elimino y salio de tus seguidos por motivos de derechos o gestion de contenido.',
    'alerts.followedProfile.removed.bodyReason': '{profile} se elimino y salio de tus seguidos por motivos de derechos o gestion de contenido. Motivo: {reason}',
  },
};

for (const code of Object.keys(languageOverrides)) {
  dictionaries[code] = { ...dictionaries.en, ...languageOverrides[code] };
}

function normalizeLanguage(value) {
  const code = String(value || '').toLowerCase().split('-')[0];
  return dictionaries[code] ? code : 'en';
}

function detectInitialLanguage() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) return normalizeLanguage(saved);

  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of browserLanguages) {
    const normalized = normalizeLanguage(language);
    if (normalized !== 'en' || String(language || '').toLowerCase().startsWith('en')) return normalized;
  }
  return 'en';
}

function interpolate(template, params = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) => params[key] ?? '');
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(detectInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  function setLanguage(code) {
    const nextLanguage = normalizeLanguage(code);
    localStorage.setItem(STORAGE_KEY, nextLanguage);
    document.documentElement.lang = nextLanguage;
    setLanguageState(nextLanguage);
  }

  const value = useMemo(() => {
    const dictionary = dictionaries[language] || dictionaries.en;
    const locale = language === 'en' ? 'en-US' : language;

    return {
      language,
      locale,
      languages,
      setLanguage,
      t: (key, params) => interpolate(dictionary[key] || dictionaries.en[key] || key, params),
      formatNumber: (value) => Number(value || 0).toLocaleString(locale),
      formatDate: (value, options) => {
        if (!value) return '';
        return new Intl.DateTimeFormat(locale, options).format(new Date(value));
      },
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}
